import dayjs from "dayjs";
import { delay, http, HttpResponse, RequestHandler } from "msw";
import { z } from "zod";
import { DASH_PLACEHOLDER } from "../constants/dashPlaceholder";
import { DaylightDateFormat } from "../constants/DaylightDateFormat";
import { filterArrayBySearchTerm } from "../constants/filters/utils";
import { HandlerConfig } from "../devtools/types";
import { ValidatePickupPayload } from "../hooks/react-query/backOffice/clientServices/useValidatePickup";
import type { FuelScaleDetail } from "../hooks/react-query/backOffice/useFuelScales";
import { RouteStatus } from "../hooks/react-query/dispatch/useRoutes";
import { AssignStopResponse } from "../hooks/react-query/dispatch/useStops";
import type { TripNote } from "../hooks/react-query/linehaul/useTripNotes";
import { Note } from "../hooks/react-query/outbound/linehaulTrailers/useLoadTrailerNotes";
import { User } from "../hooks/react-query/settings/security/useUsers";
import { AddToPlanResponse } from "../hooks/react-query/useAddToPlan";
import type { DeliveryRouteSequenceStatus } from "../hooks/react-query/useDeliveryRoutesSequenceStatus";
import {
  ucccOfdShipmentsSchema,
  type UcccOfdShipmentRow,
} from "../pages/linehaul/ucccOfd/ucccOfdShipmentRow.type";
import {
  EquipmentType,
  EquipmentTypeId,
  equipmentTypeMap,
} from "../types/equipment.type";
import type { OsadAssignment } from "../types/operations/osad.type";
import { LaneType } from "../types/outbound/lane.type";
import { Trap, trapSchema } from "../types/planning/trap.type";
import { apiUrls } from "../utils/apiUrls";
import { apiUrlsV2 } from "../utils/apiUrlsV2";
import { mockAddresses } from "./dispatch/addresses.mock";
import { mockEquipmentData } from "./equipment/equipments.mocks";
import {
  mockAppointmentBucketTableData,
  mockAppointmentRequiredShipmentsDetails,
  mockCallAttempts,
  mockPlannedTraps,
  mockPreviousDeliveries,
  mockRoutesSummaryData,
  mockSectionsData,
  mockShipmentData,
  mockTrapSummaryData,
} from "./inbound/prePlanning/prePlanning.mocks";
import {
  MOCK_CA_FSC_DATA,
  MOCK_CA_FSC_DISTINCT_VALUES,
} from "./linehaul/caFscMockData";
import { mockEntryDetails } from "./linehaul/entry.mocks";
import { mockLinehaulCarriers } from "./linehaul/linehaulCarriers.mocks";
import { mockMasterScheduleList } from "./linehaul/masterSchedule.mocks";
import { mockMasterScheduleTemplates } from "./linehaul/masterScheduleTemplates.mocks";
import { publishResponseMocks } from "./linehaul/publishResponses.mock";
import { mockServiceTypes } from "./linehaul/serviceTypes.mocks";
import { mockUcccOfdEditOwnerDialogOptions } from "./linehaul/ucccOfdOwnerFilter.mocks";
import { mockServiceFailureRecapReport } from "./linehaul/ucccOfdServiceFailureRecapReport.mocks";
import { mockUcccOfdShipmentStatusHistory } from "./linehaul/ucccOfdShipmentHistory.mocks";
import {
  mockServiceFailureReasonCodes,
  mockUcccOfdDashboardSummary,
  mockUcccOfdShipments,
  serializeUcccOfdShipmentsListForApi,
} from "./linehaul/ucCcOfdShipments.mocks";
import {
  formatUrl,
  getNextAutoIncValue,
  getTrapNameFromId,
  getUrlParam,
  stripQuery,
} from "./mockUtils";
import { linehaulTrailersResponse } from "./outbound/LinehaulTrailers.mocks";
import { linehaulTripsResponse } from "./outbound/LinehaulTrips.mocks";
import { loadTrailerNotesResponse } from "./outbound/LoadTrailerNotes.mocks";
import { mockPresetLanesList } from "./outbound/PresetLanes.mocks";
import { shipmentSearchResponse } from "./outbound/ShipmentsSearch.mocks";
import {
  companyOwnedServiceCenters,
  mockDrivers,
  mockDriversUsage,
  mockServiceCenterDoors,
  serviceCenterDoorNumbers,
} from "./serviceCenter/serviceCenters.mocks";

import {
  mockAccessorialTypeOptions,
  mockCalculatePercentageOfOptions,
  mockCurrencyOptions,
  mockMovementTypeOptions,
  mockRatingRateTypeOptions,
  mockRatingUnitOptions,
  mockShippingInstructionsCodeOptions,
} from "./backOffice/accessorialChargeForm.mocks";
import {
  mockAccessorialChargeDetail,
  mockAccessorialChargesListData,
  mockAccessorialCriteria,
  mockInactivateWarningSchedules,
} from "./backOffice/accessorialCharges.mocks";
import {
  mockAccessorialSchedules,
  mockAccessorialSchedulesListItems,
} from "./backOffice/accessorialSchedules.mocks";
import {
  mockAccessorialScheduleChargeOptions,
  mockAddedAccessorialScheduleChargeOptions,
} from "./backOffice/addNewAccessorialSchedule.mock";
import {
  BILL_STATES,
  mockBillers,
  mockBills,
  REGION_TIME_ZONES,
  TAGS,
} from "./backOffice/billing/billing.mocks";
import {
  bothAccessorialOptions,
  buildMockBlindShipmentFormDefaultBody,
  carrierOptions,
  classValues,
  deliveryAccessorialOptions,
  handlingUnitOptions,
  isZipServiceable,
  mockBlindShipmentEmailDetailsCustomerDisplayName,
  mockBlindShipmentFormDefaultRecipients,
  mockBlindShipmentFormDefaultSubject,
  mockBlindShipmentPickupId,
  mockDuplicatePickups,
  mockPickupTypes,
  mockServiceLevels,
  mockTraceTypes,
  nmfcOptions,
  NON_SERVICEABLE_LANES,
  pickupAccessorialOptions,
  pieceTypeOptions,
  shippingInstructionOptions,
  specialShipmentTagOptions,
  subOptions,
} from "./backOffice/clientServices/pickUpEntry.mocks";
import {
  mockCriteriaGroupLogicOperators,
  mockCriteriaLogicOperators,
  mockFieldNamesByTable,
  mockLookupTypesByTable,
  mockTableNameOptions,
} from "./backOffice/criteriaOptions.mocks";
import {
  mockFuelPriceApiResponse,
  mockFuelPriceHistoryResponses,
} from "./backOffice/fuelPrice.mocks";
import {
  mockFuelPriceListOptions,
  mockFuelScaleCriteria,
  mockFuelScales,
  mockFuelSurchargeCodeDetails,
  mockServiceLevelOptions,
} from "./backOffice/fuelScales.mocks";
import { mockFuelSurcharges } from "./backOffice/globalRates.mocks";
import { mockHighCostZipCodeList } from "./backOffice/highCostZipCodeList.mocks";
import {
  mockAppliesTo,
  mockHighCostZipCodeScheduleApiDetail,
  mockRateStructures,
  mockScopes,
  mockZipCodes,
  mockZipCodeTypes,
} from "./backOffice/highCostZipCodeScheduleForm.mocks";
import {
  mockHighCostZipCodeScheduleAccounts,
  mockHighCostZipCodeSchedules,
} from "./backOffice/highCostZipCodeSchedules.mocks";
import {
  mockLaneMinimumList,
  mockLocationTypes,
  mockLocationValuesByType,
} from "./backOffice/laneMinimumList.mocks";
import {
  mockLaneMinimumScheduleDetail,
  mockLaneMinimumSchedules,
} from "./backOffice/laneMinimumSchedules.mocks";
import { getRateBaseCriteriaMock } from "./backOffice/pricingAndRating/rateBaseCriteria.mocks";
import {
  MOCK_HANDLING_UNIT_TYPES,
  MOCK_OVERAGE_RULES,
  MOCK_RATE_BASE_TYPES,
  MOCK_RATE_TARIFFS,
  mockRateBases,
} from "./backOffice/pricingAndRating/rateBases.mocks";
import { getRatewareXLBaseDetail } from "./backOffice/pricingAndRating/ratewareXLBaseDetail.mocks";
import {
  mockRateClasses,
  mockRules,
  mockRuleTypes,
} from "./backOffice/pricingAndRating/rules.mocks";
import {
  mockAdditionalServices,
  mockBackOfficeAccounts,
  mockBackOfficeLocations,
  mockCommodityClassOptions,
} from "./backOffice/quickQuote.mocks";
import { mockQuickQuoteSummaryData } from "./backOffice/quickQuoteSummary.mocks";
import {
  mockShipments,
  mockShipmentsStatusCounts,
} from "./backOffice/shipments.mocks";
import {
  mockEolServiceCenterDetails,
  mockEolServiceCenters,
  mockEolZipCodesList,
  mockVendorScacOptions,
  mockZipCodesByServiceCenter,
} from "./backOffice/transitTimes/transitTimes.mocks";
import { mockEolCriteriaDetailsApiResponse } from "./backOffice/transitTimesAndServiceLevels/crossdock.mocks";
import { mockEolDestinationJunctionsLinehaulApiRows } from "./backOffice/transitTimesAndServiceLevels/destinationJunctions.mocks";
import { mockEolLinehaulJunctions } from "./backOffice/transitTimesAndServiceLevels/junctionCodes.mocks";
import {
  mockEolOriginJunctionsLinehaulApiRows,
  mockOriginJunctionCriteria,
} from "./backOffice/transitTimesAndServiceLevels/originJunctions.mocks";
import { mockDriverLatestLocations } from "./dispatch/drivers.mock";
import { mockInsightsDrivers } from "./dispatch/insightsDrivers.mock";
import { mockNotifications } from "./dispatch/notifications.mock";
import {
  mockDispatchAvailableRouteStatuses,
  mockDispatchRouteDetail,
  mockDispatchRoutes,
  mockDispatchSuggestedRoutes,
  mockDispatchSuggestedStops,
} from "./dispatch/routes.mock";
import {
  defaultShipmentNotes,
  mockShipmentDetails,
  mockShipmentDocuments,
  mockShipmentHistory,
  mockShipmentInsights,
} from "./dispatch/shipments.mock";
import {
  mockDispatchStops,
  mockDispatchStopShipmentNotes,
  mockDispatchStopSuggestedSics,
} from "./dispatch/stops.mock";
import { mockSummary } from "./dispatch/summary.mock";
import { centralizedEquipmentBySicMock } from "./equipment/centralizedEquipmentBySic.mocks";
import { mockEquipmentsBySicIdAndTypeData } from "./equipment/equipmentsBySicIdAndType.mocks";
import { mockEquipmentsUsageBySicIdData } from "./equipment/equipmentsUsageBySicId.mocks";
import { mockSearchByRefData } from "./equipment/liveInventory.mocks";
import {
  mockOldPlannedRouteTrapStatuses,
  mockPlannedRouteTrapStatuses,
} from "./inbound/planning/plannedRouteTrapStatus.mock";
import { mockPlannedUpdateRouteTrapStatuses } from "./inbound/planning/plannedUpdateRouteTrapStatus.mock";
import {
  mockPlannedRoutesTraps,
  mockRouteAssignment,
  mockRouteStatuses,
  mockTrapStatuses,
} from "./inbound/planning/planning.mock";
import { mockPrintResult } from "./inbound/planning/printResult.mock";
import {
  mockPlannedShipmentsDetails,
  mockShipmentIds,
  mockUnassignedShipmentIds,
} from "./inbound/planning/shipments.mock";
import {
  mockPresetRoutesAndTraps,
  oldMockPresetRoutesAndTraps,
} from "./inbound/presetRoutesAndTraps.mock";
import { mockIncompleteTrailers } from "./inbound/unplannedFreight/incomplete-trailer-mocks";
import { mockStops } from "./inbound/unplannedFreight/linehaul-stops.mocks";
import {
  mockFodShipments,
  mockIncompleteShipmentIds,
  mockIncompleteShipmentsDetails,
  mockLinehaulTrips,
  mockShuttleTrips,
} from "./inbound/unplannedFreight/unplannedFreight.mocks";
import { mockInboundUrgentAndCriticalCare } from "./inbound/urgentAndCriticalCare/UrgentAndCriticalCare-mocks";
import { mockTripDashboardNotifications } from "./linehaul/alerts.mocks";
import { mockBulkManifestShipments } from "./linehaul/bulkManifest.mock";
import { cancellationReportMock } from "./linehaul/cancellations.mock";
import { mockChangeRequestLogs } from "./linehaul/changeRequestLog.mocks";
import { mockedClosedTripDetails } from "./linehaul/closedTripDetails.mocks";
import { mockDaySummary } from "./linehaul/daySummary.mocks";
import {
  mockDaySummaryV2,
  mockRolloverFeeDetails,
  mockTripDetails,
} from "./linehaul/daySummaryV2.mocks";
import { mockDelayReasons } from "./linehaul/delayReasons.mocks";
import { mockDeliveredTrips } from "./linehaul/deliveredTrips.mocks";
import { mockEmployeeDrivers } from "./linehaul/employeeDrivers.mock";
import {
  cancelledTrips,
  endOfShiftComments,
  laneProjections,
  latestModifications,
  reportHeader,
  unpublishedBookings,
  yardCheck,
  yardCheckFileUpload,
} from "./linehaul/endOfShiftReport.mock";
import { mockEquipment } from "./linehaul/equipment.mock";
import { mockLoadBoardSchedulePair } from "./linehaul/loadBoardSchedulePair.mocks";
import { loadTrips, trailers } from "./linehaul/loadTrips.mocks";
import { mockTransitDashboardSummary } from "./linehaul/mockTransitDashboardSummary";
import { mockRegions } from "./linehaul/regions.mock";
import {
  mockLoadAndHoldReport,
  mockTripsReport,
  mockUrgentCareReport,
  mockVendorComplianceReport,
} from "./linehaul/reports.mocks";
import { mockScacCodes } from "./linehaul/scacCodes.mocks";
import { mockScheduleNotes } from "./linehaul/scheduleNotes.mock";
import { mockShipmentNotes } from "./linehaul/shipmentNotes.mocks";
import {
  mockBulkRequestTripsByCarrier,
  mockInTransitTrips,
  mockRecentUpdates,
  mockTransitDashboardShipments,
  mockTransitDashboardTripDetails,
  mockTransitDashboardTripDetailsWithLoadPair,
  mockTripNotes,
} from "./linehaul/transitDashboard.mocks";
import { mockTripHistory } from "./linehaul/tripHistory.mock";
import { mockTripOptimization } from "./linehaul/tripOptimization.mocks";
import { mockTripsForReassign } from "./linehaul/tripsForReassign.mocks";
import { notifications } from "./linehaul/ucccNotifications.mocks";
import { mockUcccOfdShipmentDetails } from "./linehaul/ucCcOfdShipments.mocks";
import { mockUcccPlanningShipments } from "./linehaul/ucCcPlanningShipments.mocks";
import { mockReasonCodes } from "./operations/reason-codes.mock";
import { mockTripByNumber } from "./operations/trip-by-number.mock";
import { mockBookingSearch, mockTripList } from "./operations/trip-list.mock";
import { mockTripShipments } from "./operations/trip-shipments.mock";
import { carriersList } from "./outbound/Carriers.mocks";
import { doorShipmentsResponse } from "./outbound/DoorShipments.mocks";
import { doorLayoutList } from "./outbound/DoorsLayout.mocks";
import { equipmentList } from "./outbound/Equipment.mocks";
import { doorAssets } from "./outbound/LaneDetails/DoorAssets/DoorAssets.mocks";
import { doorsUsageInfo } from "./outbound/LaneDetails/DoorsUsageInfo.mocks";
import { laneDetails } from "./outbound/LaneDetails/LaneDetails.mocks";
import { laneDetailsTonnage } from "./outbound/LaneDetails/LaneDetailsTonnage.mocks";
import {
  dalLaneShipments,
  dfwLaneShipments,
  laneShipments,
} from "./outbound/LaneDetails/ShipmentDetails/LaneShipments.mocks";
import { shipmentDetails } from "./outbound/LaneDetails/ShipmentDetails/ShipmentDetails.mocks";
import { shipmentDetailsHistory } from "./outbound/LaneDetails/ShipmentDetails/ShipmentDetailsHistory.mocks";
import { trapDetails } from "./outbound/LaneDetails/ShipmentDetails/TrapDetails.mocks";
import { mockLanesList } from "./outbound/Lanes.mocks";
import { linehaulTrailerShipmentsResponse } from "./outbound/LinehaulTrailerShipments.mocks";
import { loadTrailerShipmentsResponse } from "./outbound/LoadsTrailerShipments.mocks";
import { loadTrailersResponse } from "./outbound/LoadTrailers.mocks";
import { osadSummaryAndShipments } from "./outbound/OSAD";
import { pndTrailersResponse } from "./outbound/PndTrailers.mocks";
import { mockPresetLanesSicsSuggestionList } from "./outbound/PresetLanesSicsSuggestion.mocks";
import { shipmentTagsResponse } from "./outbound/ShipmentTags.mocks";
import { mockStaticLoadTemplatesList } from "./outbound/StaticLoadTemplates.mocks";
import { trapTrailersResponse } from "./outbound/TrapTrailers.mocks";
import { getMockReport } from "./report";
import { mockServiceCenterGeocode } from "./serviceCenter/geocode.mocks";
import { mockSicsSuggestion } from "./serviceCenter/sicsSuggestion.mocks";
import { mockBiometricUsers } from "./settings/biometrics.mocks";
import { mockLCodesData } from "./settings/LcodesTable.mocks";
import {
  mockAccessorialRates,
  mockCarrierLanesResponse,
  mockExpiredAccessorialHistory,
  mockExpiredRatesLCodesHistory,
  mockExpiredTollsLaneHistory,
  mockLCodeCarrierDestinations,
} from "./settings/linehaul-lcodes-carrier.mocks";
import { mockSicContact, mockSicDetails } from "./settings/linehaul-sics.mocks";
import { mockTollsData } from "./settings/tolls.mocks";
import { mockVendorsData } from "./settings/vendors.mocks";
import {
  mockAvailableZipCodeWithTags,
  mockCreateZipCodeWithTags,
  mockUpdateZipCodeWithTags,
} from "./settings/zipCodeWithTags.mocks";

// Track auto-sequence processing state for each route
const autoSequenceState = new Map<
  number,
  { startTime: number; pollCount: number }
>();
// the endpoints for these handlers are deployed and active. More: https://mswjs.io/docs/basics/intercepting-requests#request-pathname

const readNotificationIds = new Set<number>();

export function requestHandlers(config: HandlerConfig): RequestHandler[] {
  return [
    /** Master Schedule */
    http.get(
      formatUrl(apiUrls.masterSchedule.get("*")),
      async ({ request }) => {
        await delay(config.delay);

        const url = new URL(request.url);
        const date = dayjs(url.searchParams.get("weekOf") ?? "");
        const key = date.isSame(dayjs(), "week")
          ? "default"
          : date.isSame(dayjs().add({ week: 1 }), "week")
            ? "deletable"
            : "empty";
        return HttpResponse.json(mockMasterScheduleList[key]);
      }
    ),
    http.post(
      formatUrl(apiUrls.masterSchedule.createTemplateWithClone),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.get(
      formatUrl(apiUrls.masterSchedule.getSchedulePair(":id")),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const id = url.searchParams.get("id");
        const idSegment = url.pathname.split("/").pop();
        const num = Number(idSegment);
        if (!Number.isNaN(num) && String(num) === idSegment) {
          return HttpResponse.json(mockEntryDetails.withStopAndDeadhead);
        }
        if (!id) throw new Error("Missing id parameter");
        return HttpResponse.json(mockEntryDetails.default);
      }
    ),

    http.post(
      formatUrl(
        apiUrls.masterSchedule.publish(
          dayjs().endOf("week").format(DaylightDateFormat.ISO_DATE)
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(publishResponseMocks.default);
      }
    ),

    http.get(formatUrl(apiUrls.masterSchedule.getCarriers), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockLinehaulCarriers.default);
    }),

    http.get(formatUrl(apiUrls.masterSchedule.getServiceTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockServiceTypes.default);
    }),

    http.get(formatUrl(apiUrls.masterSchedule.getTemplates), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockMasterScheduleTemplates.default);
    }),

    /** Inbound Pre-Planning */

    // * Get routes
    http.get(
      formatUrl(
        apiUrls.getDeliveryRoutesSummary(
          ":sicId" as unknown as number,
          ":date" as unknown as number
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const searchTerm = url.searchParams.get("searchTerm");
        return HttpResponse.json(
          filterArrayBySearchTerm(
            mockRoutesSummaryData.default,
            ["title"],
            searchTerm || ""
          )
        );
      }
    ),

    // * Get traps
    http.get(formatUrl(apiUrls.getTraps), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const searchTerm = url.searchParams.get("searchTerm");
      return HttpResponse.json(
        filterArrayBySearchTerm(
          mockTrapSummaryData.default,
          ["title", "trailerNumber"],
          searchTerm || ""
        )
      );
    }),

    http.get(
      formatUrl(apiUrls.getInboundTraps(":sicId", ":date")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockPlannedTraps.default);
      }
    ),

    // * Get appointment buckets
    http.get(
      formatUrl(apiUrls.getAppointmentBucket(":sicId", ":planDate", "*")),
      async ({ request }) => {
        const url = new URL(request.url);
        const searchTerm = url.searchParams.get("searchTerm");
        await delay(config.delay);
        return HttpResponse.json(
          filterArrayBySearchTerm(
            mockAppointmentBucketTableData.default,
            ["title"],
            searchTerm || ""
          )
        );
      }
    ),

    // * Get sections
    http.get(formatUrl(apiUrls.getSectionsTableData), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const searchTerm = url.searchParams.get("searchTerm");
      const destinationSicId = url.searchParams.get("destinationSicId");
      if (!destinationSicId) {
        throw new Error("Missing destinationSicId parameter");
      }
      return HttpResponse.json(
        filterArrayBySearchTerm(
          mockSectionsData.default,
          ["title"],
          searchTerm || ""
        ),
        {
          status: destinationSicId ? 200 : 400,
        }
      );
    }),

    // * Merge Routes Traps
    http.post(formatUrl(apiUrls.mergeRoutesAndTraps), async () => {
      await delay(config.delay);
      return new HttpResponse(null, { status: 204 });
    }),

    // * Merge Routes
    http.patch(
      formatUrl(apiUrls.mergeRoutes(":routeId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return new HttpResponse(null, { status: 204 });
      }
    ),

    // * Delete Routes
    http.delete(
      formatUrl(apiUrls.deleteRouteAssignments(":idsToDelete")),
      async () => {
        await delay(config.delay);
        return new HttpResponse(null, { status: 200 });
      }
    ),

    /** Global */
    // * Get service centers
    http.get(formatUrl(apiUrls.getServiceCenters), async () => {
      await delay(config.delay);
      return HttpResponse.json(companyOwnedServiceCenters);
    }),

    http.get(
      formatUrl(
        apiUrls.getServiceCenterGeocodeByIds([":sicIds" as unknown as number])
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockServiceCenterGeocode.default);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.sicsSuggestion(
          ":sicCode",
          ":destinationSicId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockSicsSuggestion.default);
      }
    ),

    // * Create Trap
    http.post(
      formatUrl(apiUrls.createTrap(":sicId", ":unixDate")),
      async ({ request }) => {
        const responseSchema = z.object({
          name: z.string(),
        });

        const data = responseSchema.parse(await request.json());

        // Simulate a server-side auto-incrementing id by getting the current max id and adding 1
        const newAutoIncTrapId = getNextAutoIncValue(
          mockTrapSummaryData.default.map((item) => item.id)
        );
        const title = getTrapNameFromId(newAutoIncTrapId);

        const newTrap: Trap = trapSchema.parse({
          id: newAutoIncTrapId,
          name: data.name,
          title,
          doorId: null,
          notes: null,
          shipmentsTags: [],
          trailerNumber: null,
          shipmentsCount: 0,
          shipmentsPallets: 0,
          shipmentsPieces: 0,
          shipmentsWeight: 0,
          shipmentsWeightUnit: "lbs",
          shipmentIds: [],
          appointmentDate: new Date(2023, 2, 1),
          serviceDueDate: new Date(2023, 2, 1),
        });

        mockTrapSummaryData.default.push(newTrap);

        await delay(config.delay);
        return HttpResponse.json(newTrap, { status: 201 });
      }
    ),

    // * Get Load templates
    http.get(
      formatUrl(apiUrls.getStaticLoadTemplates(":sicCode")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockStaticLoadTemplatesList.default);
      }
    ),

    // * Get Doors
    http.get(formatUrl(apiUrls.getDoorLayouts(":sicCode")), async () => {
      await delay(config.delay);
      return HttpResponse.json(doorLayoutList.default);
    }),

    // * Get preset lane
    http.get(formatUrl(apiUrls.getPresetLanes(":sicCode")), () => {
      delay(config.delay);
      return HttpResponse.json(mockPresetLanesList.default);
    }),

    // * Get preset lane sics suggestion
    http.get(
      formatUrl(
        apiUrls.getPresetLaneSicSuggestion(
          ":sicCode",
          ":destinationSic" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(mockPresetLanesSicsSuggestionList.default);
      }
    ),

    // * Get Outbound linehaul Trailers
    http.get(
      formatUrl(
        apiUrls.getOutboundLinehaulTrailers({
          sicCode: ":sicCode",
          eta: ":eta",
        })
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(linehaulTrailersResponse.default);
      }
    ),

    // * Get Outbound linehaul Trips
    http.get(
      formatUrl(apiUrls.getLinehaulTrips(":sicCode", ":destinationSicCode")),
      () => {
        delay(config.delay);
        const scenario = getUrlParam("disabledTrips");
        if (scenario === "true") {
          return HttpResponse.json(linehaulTripsResponse.empty);
        }
        return HttpResponse.json(linehaulTripsResponse.default);
      }
    ),

    http.get(
      formatUrl(apiUrls.getOutboundLoadTrailers(":sicCode")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(loadTrailersResponse.default);
      }
    ),

    // * Change trailer visibility
    http.put(
      formatUrl(apiUrls.changeTrailerVisibility(":sicCode")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // * Swing Trailer
    http.post(
      formatUrl(
        apiUrls.swingTrailer(":sicCode", ":plannedPullId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // * Get Outbound pnd Trailers
    http.get(formatUrl(apiUrls.getPndTrailers(":sicCode")), () => {
      delay(config.delay);
      return HttpResponse.json(pndTrailersResponse.default);
    }),

    // * Get Outbound trap Trailers
    http.get(formatUrl(apiUrls.getTrapTrailers(":sicCode")), () => {
      delay(config.delay);
      return HttpResponse.json(trapTrailersResponse.default);
    }),

    // * Get Outbound carriers
    http.get(formatUrl(apiUrls.getOutboundCarriers), () => {
      delay(config.delay);
      return HttpResponse.json(carriersList.default);
    }),

    // * Get Outbound equipment
    http.get(formatUrl(apiUrls.getEquipments(":sicCode")), () => {
      delay(config.delay);
      return HttpResponse.json(equipmentList.default);
    }),

    // * Get Outbound Lanes
    http.get(formatUrl(apiUrls.getLanes(":sicCode")), () => {
      delay(config.delay);
      return HttpResponse.json(mockLanesList.default);
    }),

    // * Get Outbound laneDetails
    http.get(formatUrl(apiUrls.getLaneDetailsTonnage(":sicCode", [])), () => {
      delay(config.delay);
      if (config.scenario === "error") {
        return HttpResponse.json(
          {
            success: false,
            response: {
              errorMessage: "Plan not started today",
            },
          },
          {
            status: 400,
          }
        );
      } else {
        return HttpResponse.json(laneDetailsTonnage.default);
      }
    }),

    http.get(
      formatUrl(
        apiUrls.getLaneDetails(":sicCode", ":laneId" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(laneDetails.default);
      }
    ),

    // Get OSAD lane sumary and details
    http.get(
      formatUrl(apiUrls.common.getOsadDetails(":sicId" as unknown as number)),
      () => {
        delay(config.delay);
        return HttpResponse.json(osadSummaryAndShipments.default);
      }
    ),

    // Get OSAD lane summary and details (green endpoint)
    http.get(
      formatUrl(apiUrlsV2.common.getOsadDetails(":sicId" as unknown as number)),
      () => {
        delay(config.delay);
        return HttpResponse.json(osadSummaryAndShipments.default);
      }
    ),

    // * Outbound add door and assets
    http.put(
      formatUrl(
        apiUrls.editDoorAndAssetsFromLaneOld(
          ":sicCode",
          ":doorNumber" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.editDoorAndAssetsFromLane(
          ":sicCode",
          ":doorNumber" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // * Outbound assign assets
    http.put(
      formatUrl(
        apiUrls.assignAsset(":sicCode", ":doorNumber" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),
    http.put(
      formatUrl(
        apiUrls.editAssignAsset(
          ":sicCode",
          ":plannedPullId" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),
    // * Outbound assign traps
    http.put(
      formatUrl(
        apiUrls.assignTrap(":sicCode", ":doorNumber" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    // * Outbound edit assign traps
    http.put(
      formatUrl(
        apiUrls.editAssignTrap(
          ":sicCode",
          ":plannedPullId" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    // * Get Outbound linehaul Trailer shipments
    http.get(
      formatUrl(
        apiUrls.getOutboundLinehaulTrailerShipments(
          ":sicCode",
          ":tripNumber" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(linehaulTrailerShipmentsResponse.default);
      }
    ),

    // * Get Outbound loads Trailer shipments
    http.get(
      formatUrl(
        apiUrls.getOutboundLoadTrailerShipments(
          ":sicCode",
          ":plannedPullId" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(loadTrailerShipmentsResponse.default);
      }
    ),

    // * Change status
    http.put(
      formatUrl(
        apiUrls.changeStatusOutboundLoads(
          ":sicCode",
          ":plannedPullId" as unknown as number,
          ":status"
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // * Get Outbound Shipments search
    http.get(
      formatUrl(
        apiUrls.getOutboundShipmentsSearch(
          ":sicCode",
          ":plannedPullId" as unknown as number,
          ":proNumber"
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(shipmentSearchResponse.default);
      }
    ),

    // * Get Door Shipments
    http.get(
      formatUrl(
        apiUrls.getDoorShipments(":sicCode", ":doorNumber" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(doorShipmentsResponse.default);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getDoorShipmentsByLane(
          ":sicCode",
          ":laneType" as unknown as number,
          ":destinationSicId" as unknown as number,
          ":doorNumber" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(doorShipmentsResponse.default);
      }
    ),

    // * Get Lane shipments
    http.get(
      formatUrl(
        apiUrls.getDestinationShipmentsByLane(
          ":sicCode",
          ":destinationType" as unknown as number,
          ":loadToSicId" as unknown as number,
          ":sicId" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        if (params.sicId) {
          let responseData;
          switch (Number(params.sicId)) {
            case 7:
              responseData = dfwLaneShipments.default;
              break;
            case 114:
              responseData = dalLaneShipments.default;
              break;
            default:
              responseData = laneShipments.default;
              break;
          }
          return HttpResponse.json(responseData);
        } else {
          return HttpResponse.json(laneShipments.default);
        }
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getOutboundShipmentDetailsV2(
          ":sicCode",
          ":laneType" as unknown as LaneType,
          ":destinationSicId" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(shipmentDetails.default);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getOutboundShipmentDetails(
          ":sicCode" as unknown as string,
          ":shipment" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(shipmentDetailsHistory.default);
      }
    ),

    http.get(
      formatUrl(apiUrls.getTrapDetails(":sicCode" as unknown as string)),
      () => {
        delay(config.delay);
        return HttpResponse.json(trapDetails.default);
      }
    ),

    http.put(
      formatUrl(
        apiUrls.moveShipmentsToDoor(
          ":sicCode" as unknown as string,
          ":doorNumber" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.moveShipmentsToDoorByLane(
          ":sicCode" as unknown as string,
          ":laneType" as unknown as number,
          ":destinationSicId" as unknown as number,
          ":doorNumber" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.moveShipmentsToTrap(
          ":sicCode" as unknown as string,
          ":trapId" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.splitLane(":sicCode", ":destinationSicId" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    // * Get Door Assets
    http.get(
      formatUrl(
        apiUrls.getDoorAssets(
          ":sicCode",
          ":laneType" as unknown as LaneType,
          ":destinationSicId" as unknown as number
        )
      ),
      ({ params }) => {
        if (config.scenario === "unassigned") {
          return HttpResponse.json(doorAssets.unassigned);
        } else if (params.destinationSicId === "10") {
          return HttpResponse.json(doorAssets.bwi);
        } else {
          return HttpResponse.json(doorAssets.default);
        }
      }
    ),

    // * Get Doors Usage Info
    http.get(formatUrl(apiUrls.getDoorsUsageInfo(":sicCode")), () => {
      delay(config.delay);
      return HttpResponse.json(doorsUsageInfo);
    }),

    // * Get Outbound Load Trailer Notes
    http.get(
      formatUrl(
        apiUrls.getLoadTrailerNotes(
          ":sicCode",
          ":plannedPullId" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(loadTrailerNotesResponse.default);
      }
    ),

    // * Post Outbound Merge Doors
    http.post(
      formatUrl(
        apiUrls.mergeDoors(":sicCode", ":doorNumber" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.mergeDoorsAssets(":sicCode", ":doorNumber" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    // * Post Transform PnD to Trap
    http.post(formatUrl(apiUrls.transformPndToTrap(":sicCode")), () => {
      delay(config.delay);

      return HttpResponse.json(null, { status: 201 });
    }),

    // * Get Outbound Shipment Tags
    http.get(formatUrl(apiUrls.shipmentTags), () => {
      delay(config.delay);
      return HttpResponse.json(shipmentTagsResponse.default);
    }),

    http.post(
      formatUrl(
        apiUrls.createLoadTrailerNote(
          ":sicCode",
          ":plannedPullId" as unknown as number
        )
      ),
      async ({ request }) => {
        const requestSchema = z.object({
          content: z.string(),
        });

        const data = requestSchema.parse(await request.json());
        const newAutoIncNoteId = getNextAutoIncValue(
          loadTrailerNotesResponse.default.map((item) => item.id)
        );

        const newNote: Note = {
          id: newAutoIncNoteId,
          content: data.content,
          userName: "Current User",
          timeStamp: new Date(),
          isEdited: false,
          canEdit: true,
        };

        loadTrailerNotesResponse.default.push(newNote);

        await delay(config.delay);

        return HttpResponse.json(newNote, { status: 201 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.updateLoadTrailerNote(
          ":sicCode",
          ":plannedPullId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.deleteLoadTrailerNote(
          ":sicCode",
          ":plannedPullId" as unknown as number,
          ":noteId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    // * Get loadboard schedule pairs
    http.get(
      formatUrl(apiUrls.loadBoard.getSchedulePair(":pairId")),
      async ({ params }) => {
        await delay(config.delay);
        const { pairId } = params;
        if (!pairId) throw new Error("Missing pairId parameter");
        return HttpResponse.json(mockLoadBoardSchedulePair.multipleLoad);
      }
    ),

    // * Get loadboard change requests
    http.get(
      formatUrl(apiUrls.loadBoard.getChangeRequestLogs(":scheduleId")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockChangeRequestLogs.default);
      }
    ),

    /** Inbound Planning */
    // * Put Delivery Route Status
    http.put(
      formatUrl(apiUrls.putDeliveryRouteStatusOld(":id" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 204 });
      }
    ),
    // PUT Massage Route
    http.put(
      formatUrl(apiUrls.putMassageRoute(":id" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 204 });
      }
    ),

    // * Reassign Shipment
    http.post(formatUrl(apiUrls.reassignShipments), async () => {
      await delay(config.delay);
      return HttpResponse.text(null, { status: 204 });
    }),

    // * Reorder Shipment
    http.patch(
      formatUrl(apiUrls.reorderShipments(":routeId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 204 });
      }
    ),

    // LiveInventory

    http.get(
      formatUrl(apiUrls.getLiveInventory(":sicId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(centralizedEquipmentBySicMock.default);
      }
    ),

    http.get(
      formatUrl(apiUrls.searchEquipmentByRefNumber(":refNumber")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockSearchByRefData.default);
      }
    ),

    http.put(
      formatUrl(apiUrls.repositionTrailer(":equipmentId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true });
      }
    ),

    // * Get Unassigned Shipments
    http.get(
      formatUrl(
        apiUrls.getUnassignedShipments(
          ":sicId" as unknown as number,
          ":date" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockUnassignedShipmentIds);
      }
    ),

    // * Get Route Assigments
    http.get(
      formatUrl(
        apiUrls.getRouteAssignments(
          ":date" as unknown as number,
          ":sicId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockPlannedRoutesTraps.default);
      }
    ),

    http.get(
      formatUrl(apiUrls.getDeliveryRoutesTrapsStatusOld([], [])), // passing empty arrays since the search params are ignored by msw's matching algorithm, and thus, stripped by formatUrl anyway.
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockOldPlannedRouteTrapStatuses.default);
      }
    ),
    http.get(
      formatUrl(apiUrls.getDeliveryRoutesTrapsStatusV3([], [])),
      async () => {
        await delay(config.delay);
        const scenario = getUrlParam("deliveryRoutesTrapsStatusScenario");
        if (scenario === "allTransitionsAllowed") {
          return HttpResponse.json(
            mockPlannedRouteTrapStatuses.allTransitionsAllowed
          );
        }
        return HttpResponse.json(mockPlannedRouteTrapStatuses.default);
      }
    ),

    http.put(formatUrl(apiUrls.putDeliveryRoutesTrapsStatus), async () => {
      await delay(config.delay);
      return HttpResponse.text(null, { status: 204 });
    }),
    http.put<any, { statusId: number; routeIds: number[]; trapIds: number[] }>(
      formatUrl(apiUrls.putDeliveryRoutesTrapsStatusV2),
      async ({ request }) => {
        await delay(config.delay);

        const routesAndTrapsUpdateRequest = await request.json();

        if (routesAndTrapsUpdateRequest.routeIds.includes(312314)) {
          return HttpResponse.json(mockPlannedUpdateRouteTrapStatuses.partial, {
            status: 200,
          });
        }
        return HttpResponse.json(mockPlannedUpdateRouteTrapStatuses.default, {
          status: 200,
        });
      }
    ),

    // * Get Preset Routes
    http.get(formatUrl(apiUrls.getRoutesAndTraps), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const sicId = url.searchParams.get("sicId");
      if (!sicId) throw new Error("Missing sicId parameter");
      return HttpResponse.json(oldMockPresetRoutesAndTraps.default);
    }),

    http.get(formatUrl(apiUrls.getRoutesAndTrapsV2), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const sicId = url.searchParams.get("sicId");
      if (!sicId) throw new Error("Missing sicId parameter");
      return HttpResponse.json(mockPresetRoutesAndTraps.default);
    }),
    // * Put Preset Routes
    http.put(
      formatUrl(apiUrls.putRoutes(":routeId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    // * Get Planning Shipment Details
    http.post<any, number[]>(
      formatUrl(apiUrls.getShipmentDetails),
      async ({ request }) => {
        const shipmentIds = await request.json();
        await delay(config.delay);

        if (
          JSON.stringify(mockShipmentIds.sort()) ===
          JSON.stringify(shipmentIds.sort())
        ) {
          return HttpResponse.json(mockPlannedShipmentsDetails.default);
        }

        if (
          JSON.stringify(mockIncompleteShipmentIds[1]) ===
          JSON.stringify(shipmentIds[1])
        ) {
          return HttpResponse.json(mockIncompleteShipmentsDetails.default);
        }

        const allShipments = [
          ...mockShipmentData.default,
          ...mockPlannedShipmentsDetails.default,
          ...mockIncompleteShipmentsDetails.default,
        ];

        return HttpResponse.json(
          allShipments.filter((s) => shipmentIds.includes(s.id))
        );
      }
    ),

    http.get(
      formatUrl(apiUrls.getShipmentsByRoute(":trailerId")),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const routeId = url.searchParams.get("trailerId");
        if (routeId) {
          const route = mockPlannedRoutesTraps.default.find(
            (r) => r.id === parseInt(routeId, 10)
          );
          const shipmentIds = route ? route.shipmentIds : [];
          const shipments = mockPlannedShipmentsDetails.default.filter(
            (shipment) => shipmentIds.includes(shipment.id)
          );
          return HttpResponse.json(shipments);
        } else {
          return HttpResponse.json(null, { status: 404 });
        }
      }
    ),

    http.get(
      formatUrl(apiUrls.getShipmentsByTrap(":trailerId")),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const trapId = url.searchParams.get("trailerId");
        if (trapId) {
          const route = mockPlannedRoutesTraps.default.find(
            (r) => r.id === parseInt(trapId, 10)
          );
          const shipmentIds = route ? route.shipmentIds : [];
          const shipments = mockPlannedShipmentsDetails.default.filter(
            (shipment) => shipmentIds.includes(shipment.id)
          );
          return HttpResponse.json(shipments);
        } else {
          return HttpResponse.json(null, { status: 404 });
        }
      }
    ),

    // * Get Planned Route statuses
    http.get(
      formatUrl(apiUrls.getDeliveryRouteStatusOld(":id" as unknown as number)),
      async () => {
        await delay(config.delay);
        const scenario = getUrlParam("deliveryRouteStatusScenario");
        if (scenario === "allTransitionsAllowed") {
          return HttpResponse.json(mockRouteStatuses.allTransitionsAllowed);
        }
        return HttpResponse.json(mockRouteStatuses.default);
      }
    ),

    //* Get Planned Trap statuses
    http.get(
      formatUrl(apiUrls.getDeliveryTrapStatusOld(":id" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockTrapStatuses.default);
      }
    ),

    /** Inbound Unplanned Freight */
    // * Get linehaul trips
    http.get(formatUrl(apiUrls.getTripLH), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const searchTerm = url.searchParams.get("searchTerm");
      const destinationSicId = url.searchParams.get("destinationSicId");
      const planDate = url.searchParams.get("planDate");
      if (!destinationSicId) {
        throw new Error("Missing destinationSicId parameter");
      }
      if (!planDate) throw new Error("Missing planDate parameter");
      return HttpResponse.json(
        filterArrayBySearchTerm(
          mockLinehaulTrips.default,
          ["tripNumber", "trailerNumber"],
          searchTerm || ""
        )
      );
    }),

    http.post(
      formatUrl(apiUrls.arriveTrip(":tripNumber" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // * Get linehaul stops
    http.get(formatUrl(apiUrls.getTripLHStops), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const destinationSicId = url.searchParams.get("destinationSicId");
      const planDate = url.searchParams.get("planDate");
      const searchTerm = url.searchParams.get("searchTerm");
      if (!destinationSicId) {
        throw new Error("Missing destinationSicId parameter");
      }
      if (!planDate) throw new Error("Missing planDate parameter");
      return HttpResponse.json(
        filterArrayBySearchTerm(
          mockStops.default,
          ["tripNumber", "trailerNumber"],
          searchTerm || ""
        )
      );
    }),

    // * Get freights
    http.get(formatUrl(apiUrls.getAdditionalFreight), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const searchTerm = url.searchParams.get("searchTerm");
      const destinationSicId = url.searchParams.get("destinationSicId");
      const planDate = url.searchParams.get("planDate");
      if (!destinationSicId) {
        throw new Error("Missing destinationSicId parameter");
      }
      if (!planDate) throw new Error("Missing planDate parameter");
      return HttpResponse.json(
        filterArrayBySearchTerm(
          mockFodShipments.default,
          ["proNumber"],
          searchTerm || ""
        )
      );
    }),

    // * Get shuttle trips
    http.get(formatUrl(apiUrls.getTripShuttle), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const searchTerm = url.searchParams.get("searchTerm");
      const destinationSicId = url.searchParams.get("destinationSicId");
      const planDate = url.searchParams.get("planDate");
      if (!destinationSicId) {
        throw new Error("Missing destinationSicId parameter");
      }
      if (!planDate) throw new Error("Missing planDate parameter");
      return HttpResponse.json(
        filterArrayBySearchTerm(
          mockShuttleTrips.default,
          ["tripNumber", "trailerNumber"],
          searchTerm || ""
        )
      );
    }),

    // * Get Incomplete Trailers
    http.get(
      formatUrl(
        apiUrls.getIncompleteTrailer(
          ":date" as unknown as number,
          ":sicId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockIncompleteTrailers);
      }
    ),

    http.post(
      formatUrl(
        apiUrls.reinstateToPlan(
          ":date" as unknown as number,
          ":sicId" as unknown as number
        )
      ),

      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, {
          status: 200,
        });
      }
    ),

    // * Add to plan
    http.post<any, { shipmentIds: number[] }>(
      formatUrl(
        apiUrls.addToPlan(
          ":sicId" as unknown as number,
          ":date" as unknown as number
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const { shipmentIds } = await request.json();
        const data: AddToPlanResponse = {
          successfullyProcessedShipmentCount: shipmentIds.length,
          failedProcessedShipmentCount: 0,
          failedShipmentIds: [],
          failedShipments: [],
          processedShipmentCount: shipmentIds.length,
          processedShipmentIds: shipmentIds,
          shipmentCountSubmitted: shipmentIds.length,
          shipmentIdsWithMissingConsigneeInfo: [],
          unprocessedShipmentCount: 0,
          unprocessedShipmentSearchResults: [],
        };
        return HttpResponse.json(data);
      }
    ),

    // * Put Preset Routes
    http.put(
      formatUrl(apiUrls.putRoutes(":routeId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getDoorNumbersBySicIdOld(":sicId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(serviceCenterDoorNumbers);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getEquipmentDetailsBySicId(":sicId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockEquipmentData.default);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getEquipmentById(":equipmentIdParam" as unknown as number)
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { equipmentIdParam } = params;
        const equipmentId = Number(equipmentIdParam);

        const mockEquipments = mockEquipmentData.getById;
        const mockEquipment = mockEquipments.find(
          (equipment) => equipment.id === equipmentId
        );
        return HttpResponse.json(mockEquipment);
      }
    ),

    http.get(formatUrl(apiUrls.getCurrentUser), async () => {
      await delay(config.delay);
      const mockUser: User = {
        id: "7fd75d50-622e-4258-9027-606eb0f11809",
        firstName: "Mock",
        lastName: "User",
        email: "mock@user.com",
        isActive: true,
        sicId: 111,
        groups: [],
        loginId: null,
      };
      return HttpResponse.json(mockUser);
    }),

    http.get(
      formatUrl(apiUrls.getGroup(":groupId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({
          data: {
            id: 999,
            name: "Mock Group",
            description: "Mock Group Description",
          },
        });
      }
    ),

    http.post(
      formatUrl(apiUrls.autoSequenceRoutes([])),
      async ({ request }) => {
        await delay(config.delay);
        const scenario = getUrlParam("autoSequenceRoutesScenario");
        if (scenario === "error") {
          return new HttpResponse(null, {
            status: 500,
            statusText: "Auto sequence routes error",
          });
        }
        const url = new URL(request.url);
        const routeIds = url.searchParams.getAll("routeIds");

        // Initialize auto-sequence state for each route
        routeIds.forEach((id) => {
          autoSequenceState.set(Number(id), {
            startTime: Date.now(),
            pollCount: 0,
          });
        });

        return HttpResponse.json(routeIds.map(Number));
      }
    ),

    http.get(
      formatUrl(apiUrls.getDeliveryRoutesSequenceStatus(":routeIds")),
      async ({ request }) => {
        // Minimal delay for tests
        await delay(100);
        const url = new URL(request.url);
        const routeIds = url.searchParams.getAll("routeIds");

        const result: DeliveryRouteSequenceStatus[] = routeIds.map(
          (routeId) => {
            const id = Number(routeId);
            const state = autoSequenceState.get(id);

            if (!state) {
              // If no state exists, assume already processed (old behavior for routes not actively being sequenced)
              return { id, sequenceStatus: "processed" };
            }

            // Check how long it's been since auto-sequence started
            const elapsedTime = Date.now() - state.startTime;

            // Complete after 2 seconds for fast test execution
            if (elapsedTime >= 2000) {
              // Done processing - clean up state and return "processed"
              autoSequenceState.delete(id);
              return { id, sequenceStatus: "processed" };
            } else {
              // Still processing (within first 2 seconds)
              return { id, sequenceStatus: "processing" };
            }
          }
        );

        return HttpResponse.json(result);
      }
    ),

    http.post(formatUrl(apiUrls.lockRoutes), async () => {
      await delay(config.delay);
      return new HttpResponse(null, { status: 204 });
    }),

    http.get(
      formatUrl(apiUrls.getDoorNumbersBySicId(":sicId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockServiceCenterDoors.default);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getEquipmentsBySicIdAndTypeId(
          ":sicIdParam" as unknown as number,
          ":typeParam" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { typeParam } = params;
        if (!typeParam) {
          return HttpResponse.json([]);
        }

        const allowedEquipmentTypes = Object.values(equipmentTypeMap);
        if (
          !allowedEquipmentTypes.includes(Number(typeParam) as EquipmentTypeId)
        ) {
          throw new Error("Invalid equipment type");
        }

        const equipmentType = Object.keys(equipmentTypeMap).find(
          (key) =>
            equipmentTypeMap[key as keyof typeof equipmentTypeMap] ===
            Number(typeParam)
        ) as EquipmentType | undefined;

        if (equipmentType && equipmentType in equipmentTypeMap) {
          return HttpResponse.json(
            mockEquipmentsBySicIdAndTypeData.default[equipmentType]
          );
        } else {
          return new HttpResponse(null, {
            status: 400,
            statusText: "Invalid equipment type",
          });
        }
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getEquipmentsUsageBySicId(":sicIdParam" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockEquipmentsUsageBySicIdData.default, {
          status: 200,
        });
      }
    ),

    http.get(
      formatUrl(apiUrls.getEquipmentBySic(":sicId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(centralizedEquipmentBySicMock.default);
      }
    ),

    http.put(
      formatUrl(apiUrls.putRouteAssignment(":routeId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockRouteAssignment.default, { status: 200 });
      }
    ),

    // * Drivers

    http.get(
      formatUrl(apiUrls.getDrivers(":sicId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDrivers.default);
      }
    ),

    http.get(
      formatUrl(apiUrls.getDriverById(":driverId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDrivers.default[0]);
      }
    ),

    http.put(
      formatUrl(apiUrls.putDriver(":driverId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDrivers.default[0]);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getDriversUsageBySicIdAndPlanDate(
          ":sicIdParam" as unknown as number,
          ":planDateParam" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDriversUsage.default, {
          status: 200,
        });
      }
    ),

    // * Reports
    http.get(
      formatUrl(apiUrls.getOutboundLinehaulTrailerShipmentsPdf(":sicCode", [])),
      getMockReport
    ),
    http.get(
      formatUrl(apiUrls.getOutboundPndTrailerShipmentsPdf(":sicCode", [])),
      getMockReport
    ),
    http.get(
      formatUrl(apiUrls.getOutboundTrapTrailerShipmentsPdf(":sicCode", [])),
      getMockReport
    ),
    http.get(
      formatUrl(apiUrls.getOutboundLoadTrailerShipmentsPdf(":sicCode", [])),
      getMockReport
    ),
    http.put(
      formatUrl(apiUrls.getOutboundLinehaulTrailerManifestReport),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockPrintResult.default, {
          status: 200,
        });
      }
    ),
    http.put(
      formatUrl(apiUrls.getOutboundCarrierBillOfLadingManifestReport),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockPrintResult.default, {
          status: 200,
        });
      }
    ),

    http.put(formatUrl(apiUrls.getOutboundTrapManifestReport), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockPrintResult.default, {
        status: 200,
      });
    }),
    http.put(formatUrl(apiUrls.getOutboundStripManifestReport), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockPrintResult.default, {
        status: 200,
      });
    }),
    http.post(formatUrl(apiUrls.getBulkReport), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockPrintResult.default, {
        status: 200,
      });
    }),

    http.get(
      formatUrl(
        apiUrls.getEndOfShiftReports(
          ":originSicId" as unknown as number,
          ":date",
          "report-header"
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(reportHeader, {
          status: 200,
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getEndOfShiftReports(
          ":originSicId" as unknown as number,
          ":date",
          "yard-check"
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(yardCheck.default);
      }
    ),

    http.post(
      formatUrl(
        apiUrls.getEndOfShiftReports(
          ":sicId" as unknown as number,
          ":date",
          "yard-check"
        )
      ),
      async ({ request }) => {
        await delay(config.delay);

        try {
          const formData = await request.formData();
          const file = formData.get("file") as File;

          // Check file size (5MB limit)
          if (file.size > 5 * 1024 * 1024) {
            return HttpResponse.json(yardCheckFileUpload.errors.fileTooLarge, {
              status: 413,
            });
          }

          // Check file type
          const allowedExtensions = [".pdf", ".doc", ".docx", ".xlsx", ".csv"];
          const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();

          if (!allowedExtensions.includes(fileExtension)) {
            return HttpResponse.json(
              yardCheckFileUpload.errors.invalidFileType,
              {
                status: 400,
              }
            );
          }

          return HttpResponse.json(yardCheckFileUpload.success, {
            status: 200,
          });
        } catch (error) {
          return HttpResponse.json(yardCheckFileUpload.errors.serverError, {
            status: 500,
          });
        }
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.deleteYardCheckFile(":sicId" as unknown as number, ":fileId")
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true }, { status: 200 });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.getEndOfShiftReports(
          ":originSicId" as unknown as number,
          ":date",
          "lane-projections"
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(laneProjections, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.getEndOfShiftReportDownload(
          ":sicId" as unknown as number,
          ":fileUri"
        )
      ),
      async () => {
        await delay(config.delay);

        // Return a mock pre-signed URL response
        return HttpResponse.json(
          {
            url: "https://mock-s3-url.com/documents/mock-file.pdf?X-Amz-Expires=900&X-Amz-Security-Token=mock-token",
          },
          {
            status: 200,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      }
    ),
    /** Inbound Urgent and Critical Care */
    http.get(
      formatUrl(
        apiUrls.getInboundUrgentAndCriticalCare(
          ":sicCode" as unknown as number,
          ":planDate" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockInboundUrgentAndCriticalCare);
      }
    ),

    /** UC/CC OFD Shipment Details */
    http.get(
      formatUrl(
        apiUrls.getUcccOfdShipmentDetails(":shipmentId" as unknown as string)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockUcccOfdShipmentDetails.default.shipment);
      }
    ),

    /** UC/CC OFD Shipment status history (timeline) */
    http.get(
      formatUrl(
        apiUrls.ucccOfd.getUcccOfdShipmentStatusHistory(
          ":shipmentId" as unknown as string
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockUcccOfdShipmentStatusHistory, {
          status: 200,
        });
      }
    ),

    // * Dispatch Routes
    http.get(
      formatUrl(
        apiUrls.dispatch.getLatestDriverLocations(
          ":sicCode" as unknown as number,
          ":planDate" as unknown as string
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(
          mockDriverLatestLocations.default.map((l) => ({
            ...l,
            timestamp: l.timestamp.valueOf() * 10000 + 621355968000000000,
          })),
          {
            status: 200,
          }
        );
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getRoutes(":sicCode" as unknown as number, ":planDate")
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDispatchRoutes.default, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(apiUrls.dispatch.getRoute(":id" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const { id } = params;
        const routeId = Number(id);
        if (!routeId) {
          return new HttpResponse(null, { status: 404 });
        }
        const route = mockDispatchRouteDetail.default.find(
          (route) => route.id === routeId
        );
        if (!route) {
          return new HttpResponse(null, { status: 404 });
        }
        return HttpResponse.json(route, { status: 200 });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getSummary(
          ":sicId" as unknown as number,
          ":planDate" as unknown as string
        )
      ),
      async () => {
        await delay(config.delay);
        const scenario = getUrlParam("planDate");
        type scenarios = keyof typeof mockSummary;
        if (scenario) {
          if (!Object.keys(mockSummary).includes(scenario)) {
            throw new Error("Invalid summary scenario");
          }
          return HttpResponse.json(mockSummary[scenario as scenarios], {
            status: 200,
          });
        }
        return HttpResponse.json(mockSummary.default, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getStops(
          ":sicId" as unknown as number,
          ":planDate" as unknown as string
        )
      ),
      async () => {
        await delay(config.delay);
        const scenario = getUrlParam("stopsScenario");
        type scenarios = keyof typeof mockDispatchStops;
        // Stop 18 with only one matching route (exclude stop 20) for merge/single-route tests
        if (scenario === "singleMatchingRoute") {
          const stops = mockDispatchStops.default
            .filter((stop) => stop.id !== 20)
            .map((stop) => {
              const {
                pickups: _pickups,
                deliveries: _deliveries,
                ...stopWithoutArrays
              } = stop;
              return stopWithoutArrays;
            });
          return HttpResponse.json(stops, { status: 200 });
        }
        if (scenario) {
          if (!Object.keys(mockDispatchStops).includes(scenario)) {
            throw new Error("Invalid stops scenario");
          }
          // Transform stops to have empty arrays (API no longer returns shipment arrays)
          // Mock data includes arrays for StopDetails, but /stops endpoint removes them
          const stops = mockDispatchStops[scenario as scenarios].map((stop) => {
            const {
              pickups: _pickups,
              deliveries: _deliveries,
              ...stopWithoutArrays
            } = stop;
            return stopWithoutArrays;
          });
          return HttpResponse.json(stops, {
            status: 200,
          });
        }

        // Transform stops to have empty arrays (API no longer returns shipment arrays)
        // Mock data includes arrays for StopDetails, but /stops endpoint removes them
        const stops = mockDispatchStops.default.map((stop) => {
          const {
            pickups: _pickups,
            deliveries: _deliveries,
            ...stopWithoutArrays
          } = stop;
          return stopWithoutArrays;
        });
        return HttpResponse.json(stops, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getStop(
          ":sicId" as unknown as number,
          ":id" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const stop = mockDispatchStops.default.find(
          (stop) => stop.id === Number(params.id)
        );

        if (!stop) {
          return new HttpResponse(null, { status: 404 });
        }

        // Return stop with full shipment arrays for StopDetails endpoint
        return HttpResponse.json(stop, {
          status: 200,
        });
      }
    ),
    http.post(
      formatUrl(
        apiUrls.dispatch.postUnassignStopFromRoute(
          ":sicId" as unknown as number,
          ":id" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const scenario = getUrlParam("unassignStopScenario");
        if (scenario === "error") {
          return new HttpResponse(null, {
            status: 500,
          });
        }
        const stop = mockDispatchStops.default.find(
          (stop) => stop.id === Number(params.id)
        );
        if (!stop) {
          return new HttpResponse(null, { status: 404 });
        }

        return new HttpResponse(null, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getSuggestedRoutes(
          ":sicCode" as unknown as number,
          ":stopId" as unknown as number,
          ":planDate" as unknown as string
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDispatchSuggestedRoutes.default, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getSuggestedStops(
          ":sicId" as unknown as number,
          ":routeId" as unknown as number,
          ":planDate" as unknown as string
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDispatchSuggestedStops.default, {
          status: 200,
        });
      }
    ),
    http.post(
      formatUrl(
        apiUrls.dispatch.postAssignStopToRoute(
          ":sicCode" as unknown as number,
          ":stopId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        const assignResponse: AssignStopResponse = {
          routeName: "TEST ROUTE",
          driverName: "TEST DRIVER",
        };
        return HttpResponse.json(assignResponse, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.dispatch.postMergeStop(
          ":sicCode" as unknown as number,
          ":stopId" as unknown as number
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as { unassignedStopId: number };
        if (!body.unassignedStopId) {
          return HttpResponse.json(
            { title: "Unassigned stop is required." },
            { status: 400 }
          );
        }
        const mergeResponse: AssignStopResponse = {
          routeName: "TEST ROUTE",
          driverName: "TEST DRIVER",
        };
        return HttpResponse.json(mergeResponse, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.dispatch.postMultiAssignStopsToRoute(
          ":sicCode" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return new HttpResponse(null, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.dispatch.postReassignStopToRoute(
          ":sicCode" as unknown as number,
          ":stopId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return new HttpResponse(null, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.dispatch.postSplitStop(
          ":sicCode" as unknown as number,
          ":stopId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return new HttpResponse(null, { status: 200 });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getStopShipmentNotes(
          ":sicId" as unknown as number,
          ":id" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { id } = params;
        const shipmentId = Number(id);
        const scenario = getUrlParam("stopShipmentScenario");

        const size = mockDispatchStopShipmentNotes.default.length + 1;
        const index = shipmentId % size;

        const mockDispatchStopShipmentNote =
          mockDispatchStopShipmentNotes.default[index];

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (scenario === "noShipmentNotes" || !mockDispatchStopShipmentNote) {
          return new HttpResponse(null, { status: 204 });
        }

        return HttpResponse.json(mockDispatchStopShipmentNote, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getStopSuggestedSics(
          ":sicId" as unknown as number,
          ":id" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        return HttpResponse.json(
          mockDispatchStopSuggestedSics.default.filter(
            (x) => x.sicId !== Number(params.sicId)
          ),
          {
            status: 200,
          }
        );
      }
    ),
    http.patch(
      formatUrl(
        apiUrls.dispatch.patchStopSic(
          ":sicCode" as unknown as number,
          ":id" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return new HttpResponse(null, { status: 200 });
      }
    ),
    http.post(
      formatUrl(
        apiUrls.dispatch.postUpdateRouteStatus(
          ":sicId" as unknown as number,
          ":id" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const scenario = getUrlParam("updateRouteStatusScenario");
        if (scenario === "error") {
          return new HttpResponse(null, {
            status: 500,
          });
        }
        const stop = mockDispatchStops.default.find(
          (stop) => stop.id === Number(params.id)
        );
        if (!stop) {
          return new HttpResponse(null, { status: 404 });
        }

        return new HttpResponse(null, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getAvailableRouteStatuses(
          ":sicId" as unknown as number,
          ":routeId" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);

        const route = mockDispatchRoutes.default.find(
          (route) => Number(params.routeId) === route.id
        );

        const routeStatuses = ((routeStatus: RouteStatus | undefined) => {
          switch (routeStatus) {
            case "DISP":
              return mockDispatchAvailableRouteStatuses.routeWithStatusDisp;
            case "CLDV":
              return mockDispatchAvailableRouteStatuses.routeWithStatusCldv;
            case "CLDK":
              return mockDispatchAvailableRouteStatuses.routeWithStatusCldk;
            default:
              return null;
          }
        })(route?.status);

        if (!routeStatuses) {
          return new HttpResponse(null, { status: 404 });
        }
        return HttpResponse.json(routeStatuses, {
          status: 200,
        });
      }
    ),
    http.post(
      formatUrl(
        apiUrls.dispatch.postRescheduleStop(
          ":sicCode" as unknown as number,
          ":stopId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        const scenario = getUrlParam("rescheduleStopScenario");
        if (scenario === "error") {
          return new HttpResponse(null, {
            status: 500,
          });
        }
        return new HttpResponse(null, { status: 200 });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getNotifications(
          ":sicCode" as unknown as number,
          ":planDate"
        )
      ),
      async () => {
        await delay(config.delay);
        const scenario = getUrlParam("notificationsScenario");
        if (scenario === "read") {
          return HttpResponse.json(
            mockNotifications.default.map((notification) => ({
              ...notification,
              isRead: true,
            })),
            {
              status: 200,
            }
          );
        }

        const notifications = mockNotifications.default.map((notification) => ({
          ...notification,
          isRead:
            readNotificationIds.has(notification.id) || notification.isRead,
        }));

        return HttpResponse.json(notifications, {
          status: 200,
        });
      }
    ),

    http.post(
      formatUrl(apiUrls.dispatch.postMarkNotificationsAsRead()),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as { notificationIds: number[] };

        body.notificationIds.forEach((id) => {
          readNotificationIds.add(id);
        });

        return HttpResponse.json(null, {
          status: 200,
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getShipments(
          ":sicId" as unknown as number,
          ":planDate" as unknown as string,
          ":searchTerm" as unknown as string | null | undefined
        )
      ),
      async () => {
        await delay(config.delay);

        return HttpResponse.json(mockShipmentInsights, {
          status: 200,
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getDrivers(
          ":sicId" as unknown as number,
          ":planDate" as unknown as string,
          ":searchTerm" as unknown as string | null | undefined
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const searchTerm = url.searchParams.get("searchTerm");

        let filteredDrivers = mockInsightsDrivers;

        // Apply search term filtering if provided
        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          filteredDrivers = mockInsightsDrivers.filter(
            (driver) =>
              driver.driver.toLowerCase().includes(term) ||
              driver.route.toLowerCase().includes(term)
          );
        }

        return HttpResponse.json(filteredDrivers, {
          status: 200,
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getShipmentDetails(":shipmentId" as unknown as number)
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { shipmentId } = params;
        const validIds = Object.keys(mockShipmentDetails);

        if (!shipmentId || validIds.indexOf(shipmentId as string) === -1) {
          return new HttpResponse(null, { status: 404 });
        }
        const shipment = mockShipmentDetails[shipmentId as string];
        return HttpResponse.json(shipment, { status: 200 });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getShipmentNotes(":shipmentId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(defaultShipmentNotes, { status: 200 });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getShipmentHistory(":shipmentId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockShipmentHistory, { status: 200 });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getShipmentDocuments(
          ":shipmentId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockShipmentDocuments, { status: 200 });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.isZipCodeServiceable(
          ":destinationZipCode" as unknown as string,
          ":destinationCountryCode" as unknown as string
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const destinationZipCode = url.searchParams.get("DestinationZipCode");
        return HttpResponse.json(destinationZipCode !== "99999", {
          status: 200,
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getEntityShipmentDocuments(":shipmentId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(
          {
            data: mockShipmentDocuments.map((doc) => ({
              id: doc.id,
              ["document_type"]: doc.name,
              ["original_filename"]: doc.name,
              url: doc.url,
              ["created_on"]: doc.createdAt,
              ["is_deleted"]: false,
            })),
            total: mockShipmentDocuments.length,
            limit: 100,
            offset: 0,
          },
          { status: 200 }
        );
      }
    ),

    // Loadboard V2 Routes

    http.get(formatUrl(apiUrls.loadBoard.daySummary), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockDaySummary.default, { status: 200 });
    }),

    http.get(formatUrl(apiUrls.loadBoard.daySummaryV2(":date")), () =>
      HttpResponse.json(mockDaySummaryV2.default, { status: 200 })
    ),

    http.get(
      formatUrl(
        apiUrls.loadBoard.serviceCenterShipmentsByDay(":originSicCode", ":date")
      ),
      () => {
        // Get unique shipments by shipmentId
        const shipmentMap = new Map<number, any>();
        for (const shipment of mockDaySummaryV2.default.flatMap(
          (i) => i.shipments
        )) {
          shipmentMap.set(shipment.shipmentId, shipment);
        }
        const shipments = Array.from(shipmentMap.values());
        return HttpResponse.json(shipments, { status: 200 });
      }
    ),

    http.get(formatUrl(apiUrls.getRegionsWithSics), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockRegions.default, { status: 200 });
    }),

    http.get(formatUrl(apiUrls.loadBoard.daySummaryV2(":date")), () =>
      HttpResponse.json(mockLoadAndHoldReport.default, { status: 200 })
    ),

    http.get(
      formatUrl(
        apiUrls.getUrgentCriticalCareReports(
          ":originSicCode" as unknown as number,
          ":date"
        )
      ),
      () => HttpResponse.json(mockUrgentCareReport.default, { status: 200 })
    ),

    http.get(
      formatUrl(
        apiUrls.getVendorComplianceReports(
          ":originSicCode" as unknown as number,
          ":date"
        )
      ),
      () =>
        HttpResponse.json(mockVendorComplianceReport.default, { status: 200 })
    ),

    http.get(
      formatUrl(
        apiUrls.getLoadPlanReportsPdf(
          ":originSicCode" as unknown as number,
          ":action",
          ":date",
          ":reportType",
          ":generatedTimestamp",
          ":timezone"
        )
      ),
      ({ params }) => {
        const { action, reportType } = params;

        const pdfBase64 = `
        JVBERi0xLjQKJeLjz9MKMSAwIG9iago8PC9UeXBlL0NhdGFsb2cvUGFnZXMgMiAwIFIgPj4KZW5kb2Jq
        CjIgMCBvYmoKPDwvVHlwZS9QYWdlcy9LaWRzWzMgMCBSXS9Db3VudCAxPj4KZW5kb2JqCjMgMCBvYmoK
        PDwvVHlwZS9QYWdlL1BhcmVudCAyIDAgUi9NZWRpYUJveFswIDAgNjEyIDc5Ml0vUmVzb3VyY2VzPDwv
        Rm9udDw8L0YwIDQgMCBSPj4+Pi9Db250ZW50cyA1IDAgUj4+CmVuZG9iago0IDAgb2JqCjw8L1R5cGUv
        Rm9udC9TdWJ0eXBlL1R5cGUxL05hbWUvRjAvQmFzZUZvbnQvSGVsdmV0aWNhPj4KZW5kb2JqCjUgMCBv
        YmoKPDwvTGVuZ3RoIDYzPj4Kc3RyZWFtCkJUCi9GMCBUIDI0IFRmCjAgMCBUZAooSGVsbG8gV29ybGQh
        KSBUagpFVAplbmRzdHJlYW0KZW5kb2JqCnhyZWYKMCA2CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAw
        MDAxNSAwMDAwMCBuIAowMDAwMDAwMDY3IDAwMDAwIG4gCjAwMDAwMDAxMjggMDAwMDAgbiAKMDAwMDAw
        MDI0MCAwMDAwMCBuIAowMDAwMDAwMzQ3IDAwMDAwIG4gCnRyYWlsZXIKPDwvUm9vdCAxIDAgUi9TaXpl
        IDY+PgpzdGFydHhyZWYKNDA2CiUlRU9GCg==`.replace(/\s+/g, "");

        // Using atob cause there's no Buffer available into Vite's engine
        // eslint-disable-next-line deprecation/deprecation
        const binaryString = atob(pdfBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: "application/pdf" });

        const currentTimestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const filename = `${reportType}_${currentTimestamp}.pdf`;

        const headers: Record<string, string> = {
          "Content-Type": "application/pdf",
        };

        if (action === "print") {
          headers["Content-Disposition"] = `inline; filename=${filename}`;
        } else {
          // Default to download
          headers["Content-Disposition"] = `attachment; filename=${filename}`;
        }

        return new HttpResponse(blob, {
          status: 200,
          headers,
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getTripsReports(":originSicCode" as unknown as number, ":date")
      ),
      () =>
        HttpResponse.json(mockTripsReport.default, {
          status: 200,
        })
    ),

    http.get(
      formatUrl(
        apiUrls.getLoadAndHoldReports(
          ":originSicCode" as unknown as number,
          ":date"
        )
      ),
      () =>
        HttpResponse.json(mockLoadAndHoldReport.default, {
          status: 200,
        })
    ),

    http.get(formatUrl(apiUrls.getEmployeeDrivers), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockEmployeeDrivers.default, { status: 200 });
    }),

    http.get(formatUrl(apiUrls.loadBoard.tripsForReassign), () =>
      HttpResponse.json(mockTripsForReassign.default, { status: 200 })
    ),

    http.post(formatUrl(apiUrls.loadBoard.reassignShipments), () =>
      HttpResponse.json([12345], { status: 200 })
    ),

    http.post(formatUrl(apiUrls.loadBoard.calculateRollover), () =>
      HttpResponse.json(
        {
          isFeeApplicable: true,
          fee: 250,
          feeType: "Rollover",
        },
        { status: 200 }
      )
    ),

    http.put(
      formatUrl(
        apiUrls.updateLoadPlanReportNotes(
          ":sicCode" as unknown as number,
          ":tripNumber" as unknown as number
        )
      ),
      () => HttpResponse.json(null, { status: 200 })
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.updateSchedule(":sicCode" as unknown as number)
      ),
      () => HttpResponse.json(null, { status: 200 })
    ),

    http.post(formatUrl(apiUrls.loadBoard.createSchedulePair), () =>
      HttpResponse.json(null, { status: 200 })
    ),

    http.post(formatUrl(apiUrls.loadBoard.batchPublishSchedules), () =>
      HttpResponse.json([], { status: 200 })
    ),

    http.get(formatUrl(apiUrls.loadBoard.ucccPlanning(":day")), () =>
      HttpResponse.json(mockUcccPlanningShipments, { status: 200 })
    ),

    http.post(
      formatUrl(
        apiUrls.loadBoard.closeTrip(":tripNumber" as unknown as number)
      ),
      () => HttpResponse.json(null, { status: 200 })
    ),

    http.post(formatUrl(apiUrls.loadBoard.reinstateSchedulePair), () =>
      HttpResponse.json(null, { status: 200 })
    ),

    http.get(
      formatUrl(
        apiUrls.loadBoard.closedTripDetails(":tripNumber" as unknown as number)
      ),
      () => HttpResponse.json(mockedClosedTripDetails.default, { status: 200 })
    ),

    http.get(
      formatUrl(
        apiUrls.loadBoard.tripOptimization(":tripNumber" as unknown as number)
      ),
      () => {
        const scenario = getUrlParam("scenario");

        if (scenario === "withStops") {
          return HttpResponse.json(mockTripOptimization.withStops, {
            status: 200,
          });
        }
        return HttpResponse.json(mockTripOptimization.default, { status: 200 });
      }
    ),

    // Move Shipments in Trip Optimization
    http.patch(
      formatUrl(
        apiUrls.loadBoard.moveShipments(":tripNumber" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // Trip List
    // Linehaul Trips
    http.get(formatUrl(apiUrls.loadBoard.tripList(":day")), () =>
      HttpResponse.json(loadTrips, { status: 200 })
    ),

    // Transit Dashboard - supports pagination for infinite scroll testing
    http.get(
      formatUrl(apiUrls.loadBoard.transitDashboardTripsInTransit),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const parsedLimit = Number(url.searchParams.get("limit"));
        const parsedOffset = Number(url.searchParams.get("offset"));
        const limit =
          Number.isNaN(parsedLimit) || parsedLimit < 1 ? 100 : parsedLimit;
        const offset =
          Number.isNaN(parsedOffset) || parsedOffset < 0 ? 0 : parsedOffset;

        // Generator function for infinite scroll testing
        const mockTrips = mockInTransitTrips.trips;
        const hasTrips = Array.isArray(mockTrips) && mockTrips.length > 0;
        const tripsLength = hasTrips ? mockTrips.length : 1;

        const generateTrip = (index: number) => ({
          tripNumber: 10016841 + index,
          loadBoardScheduleId: 3104048 + index,
          originSic: hasTrips
            ? mockTrips[index % tripsLength]?.originSic ?? "UNK"
            : "UNK",
          destinationSic: hasTrips
            ? mockTrips[index % tripsLength]?.destinationSic ?? "UNK"
            : "UNK",
          stops: index % 3 === 0 ? ["SEFL"] : [],
          deadheadOrigin: "",
          deadheadDestination: "",
          bookingNumber: `BK${100000 + index}`,
          carrier: hasTrips
            ? mockTrips[index % tripsLength]?.carrier ?? "UNKNOWN"
            : "UNKNOWN",
          trailer: `TRL-${index.toString().padStart(3, "0")}`,
          shipmentTags: ["CC", "UC"],
          pickupDate: new Date("2025-11-03T23:25:00"),
          eta: new Date("2025-11-07T18:00:00"),
          ata: null,
          tripStatus: "PLAN",
          isStarred: index % 5 === 0,
          isNote: index % 4 === 0,
          employeeDrivers: [{ name: `DRIVER ${index}` }],
          ibCuttime: new Date("2025-11-04T16:30:00"),
          category: ["ON TIME", "RISK", "LATE", "AT RISK"][index % 4],
          latestModifiedOn: new Date("2025-11-03T10:22:00"),
        });

        const totalCount = Number.isFinite(mockInTransitTrips.totalCount)
          ? Math.max(0, Math.floor(Number(mockInTransitTrips.totalCount)))
          : Array.isArray(mockInTransitTrips.trips)
            ? mockInTransitTrips.trips.length
            : 0;

        const remainingItems = totalCount - offset;
        const trips =
          offset >= totalCount || remainingItems <= 0
            ? []
            : Array.from({ length: Math.min(limit, remainingItems) }, (_, i) =>
                generateTrip(offset + i)
              );

        return HttpResponse.json({ totalCount, trips }, { status: 200 });
      }
    ),

    // Transit Dashboard - Delivered Trips
    http.get(
      formatUrl(apiUrls.loadBoard.transitDashboardDeliveredTrips),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(
          {
            totalCount: mockDeliveredTrips.totalCount,
            trips: mockDeliveredTrips.default,
          },
          { status: 200 }
        );
      }
    ),

    // Transit Dashboard - Search (supports Planning, InTransit, and Delivered tabs)
    http.get(
      formatUrl(apiUrls.loadBoard.transitDashboardSearch),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const tab = url.searchParams.get("Tab");
        const searchValue = url.searchParams.get("SearchValue");

        // Return appropriate data based on tab
        if (tab === "Delivered") {
          // Filter delivered trips based on search value (simplified mock logic)
          const filteredTrips = searchValue
            ? mockDeliveredTrips.default.filter((trip) =>
                trip.tripNumber.toString().includes(searchValue)
              )
            : mockDeliveredTrips.default;

          return HttpResponse.json(
            {
              totalCount: filteredTrips.length,
              tab: "Delivered",
              deliveredTrips: filteredTrips,
            },
            { status: 200 }
          );
        }

        // Default/fallback for other tabs
        return HttpResponse.json(
          {
            totalCount: 0,
            tab: tab || "Planning",
            trips: [],
          },
          { status: 200 }
        );
      }
    ),

    http.get(formatUrl(apiUrls.getLinehaulTrailers), () =>
      HttpResponse.json(mockEquipment, { status: 200 })
    ),

    http.get(formatUrl(apiUrls.getLinehaulTrailers), () =>
      HttpResponse.json(trailers, { status: 200 })
    ),

    http.get(formatUrl(apiUrls.loadBoard.ucccNotifications(":date")), () =>
      HttpResponse.json(notifications.default)
    ),

    http.get(
      formatUrl(apiUrls.loadBoard.tripDetails(":tripId" as unknown as number)),
      () => {
        const rolloverFeeApplicable = "rollover-fee-applicable";
        const scenario = getUrlParam("tripStatus");
        const rolloverFeeScenario =
          getUrlParam("rolloverFeeScenario") ?? rolloverFeeApplicable;
        const rolloverFeeDetails =
          rolloverFeeScenario === rolloverFeeApplicable
            ? mockRolloverFeeDetails.applicable
            : mockRolloverFeeDetails.notApplicable;
        switch (scenario) {
          case "ENRT":
            return HttpResponse.json({
              ...mockTripDetails.enroute,
              ...rolloverFeeDetails,
            });
          case "CLOS":
            return HttpResponse.json({
              ...mockTripDetails.closed,
              ...rolloverFeeDetails,
            });
          case "UNPUBLISHED":
            return HttpResponse.json({
              ...mockTripDetails.unpublished,
              ...rolloverFeeDetails,
            });
          case "PUBLISHED":
            return HttpResponse.json({
              ...mockTripDetails.published,
              ...rolloverFeeDetails,
            });
          case "NO_GATE_CUT_DATE":
            return HttpResponse.json({
              ...mockTripDetails.published,
              ...rolloverFeeDetails,
              gateCutDate: null,
            });
          case "CANCELLED":
            return HttpResponse.json({
              ...mockTripDetails.cancelled,
              ...rolloverFeeDetails,
            });
          case "FOREIGN_TRAILER":
            return HttpResponse.json({
              ...mockTripDetails.default,
              ...rolloverFeeDetails,
              isDyltTrailer: false,
              scacCode: "",
              trailerNumber: "",
            });
          default:
            return HttpResponse.json({
              ...mockTripDetails.default,
              ...rolloverFeeDetails,
            });
        }
      }
    ),

    http.post(
      formatUrl(apiUrls.loadBoard.enrouteTrip(":tripId" as unknown as number)),
      () => HttpResponse.json(mockTripDetails.default)
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.loadAndHold(":scheduleId" as unknown as number)
      ),
      () => HttpResponse.json(mockTripDetails.loadAndHold)
    ),

    http.get(formatUrl(apiUrls.getCarrierScacCodes), () =>
      HttpResponse.json(mockScacCodes.default)
    ),

    http.get(formatUrl(apiUrls.getCaFsc), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const searchSic = url.searchParams.get("searchSic") ?? "";
      const toFromFilter = url.searchParams.getAll("toFrom");
      const fromToFilter = url.searchParams.getAll("fromTo");
      const expirationFilter = url.searchParams.getAll("expirationDate");

      let filtered = MOCK_CA_FSC_DATA;

      if (searchSic) {
        filtered = filtered.filter(
          (row) =>
            row.toFrom.toLowerCase().includes(searchSic.toLowerCase()) ||
            row.fromTo.toLowerCase().includes(searchSic.toLowerCase())
        );
      }
      if (toFromFilter.length > 0) {
        filtered = filtered.filter((row) => toFromFilter.includes(row.toFrom));
      }
      if (fromToFilter.length > 0) {
        filtered = filtered.filter((row) => fromToFilter.includes(row.fromTo));
      }
      if (expirationFilter.length > 0) {
        const withExp = expirationFilter.includes("WITH_EXPIRATION");
        const withoutExp = expirationFilter.includes("WITHOUT_EXPIRATION");
        if (withExp && !withoutExp) {
          filtered = filtered.filter((row) => !!row.expirationDate);
        } else if (withoutExp && !withExp) {
          filtered = filtered.filter((row) => !row.expirationDate);
        }
      }

      return HttpResponse.json({
        totalCount: filtered.length,
        data: filtered,
      });
    }),

    http.patch(
      formatUrl(apiUrls.updateCaFsc(":id" as unknown as number)),
      async ({ request, params }) => {
        await delay(config.delay);
        const id = Number(params["id"]);
        const body = (await request.json()) as {
          rate: number;
          effectiveDate: string;
          expirationDate: string | null;
        };
        const index = MOCK_CA_FSC_DATA.findIndex((row) => row.id === id);
        if (index !== -1) {
          MOCK_CA_FSC_DATA[index] = {
            ...MOCK_CA_FSC_DATA[index],
            rate: body.rate,
            effectiveDate: body.effectiveDate,
            expirationDate: body.expirationDate ?? null,
          };
        }
        return new HttpResponse(null, { status: 204 });
      }
    ),

    http.get(
      formatUrl(apiUrls.ratesAndAccessorial.getLCodes()),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const search =
          url.searchParams.get("search")?.trim().toLowerCase() ?? "";
        const includeInactive =
          url.searchParams.get("includeInactive") === "true";
        const carrierFilter = url.searchParams.getAll("carrier");

        let filtered = mockLCodesData;

        if (!includeInactive) {
          filtered = filtered.filter((row) => row.isActive);
        }

        if (search) {
          filtered = filtered.filter(
            (row) =>
              row.carrierCode.toLowerCase().includes(search) ||
              row.carrierName.toLowerCase().includes(search)
          );
        }

        if (carrierFilter.length > 0) {
          filtered = filtered.filter((row) =>
            carrierFilter.includes(row.carrierCode)
          );
        }

        return HttpResponse.json(filtered);
      }
    ),

    http.get(
      formatUrl(apiUrls.ratesAndAccessorial.getLCodesCarrierFilterOptions),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const filterName = url.searchParams.get("filterName");
        const includeInactive =
          url.searchParams.get("includeInactive") === "true";

        if (filterName !== "carrier") {
          return HttpResponse.json([]);
        }

        const carriers = mockLCodesData.filter(
          (row) => includeInactive || row.isActive
        );

        return HttpResponse.json(
          carriers.map((row) => ({
            carrierCode: row.carrierCode,
            carrierName: row.carrierName,
          }))
        );
      }
    ),

    http.get(
      formatUrl(apiUrls.ratesAndAccessorial.getMCodeVendors()),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({
          totalActiveCount: mockVendorsData.filter((v) => v.active).length,
          records: mockVendorsData.map((v) => ({
            vendorId: v.id,
            vendorName: v.name,
            vendorCode: v.code,
            isActive: v.active,
          })),
        });
      }
    ),

    http.get(formatUrl(apiUrls.ratesAndAccessorial.getTolls), async () => {
      await delay(config.delay);
      return HttpResponse.json({
        totalLaneCount: mockTollsData.length,
        tolls: mockTollsData.map((toll, index) => ({
          ...toll,
          id: index + 1,
          expirationDate: toll.expirationDate ?? null,
        })),
      });
    }),

    http.get(
      formatUrl(apiUrls.ratesAndAccessorial.getTollsFilterDistinctValues),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const filterName = url.searchParams.get("filterName")?.toLowerCase();
        const values =
          filterName === "destination"
            ? [...new Set(mockTollsData.map((t) => t.destination))].sort()
            : [...new Set(mockTollsData.map((t) => t.origin))].sort();
        return HttpResponse.json(values);
      }
    ),

    http.get(
      formatUrl(apiUrls.ratesAndAccessorial.getMCodesExpiredTolls()),
      async ({ request }) => {
        await delay(config.delay);
        const searchParams = new URL(request.url).searchParams;
        const originSicId = searchParams.get("originSic")?.trim() ?? "";
        const destinationSicId =
          searchParams.get("destinationSic")?.trim() ?? "";

        if (!originSicId || !destinationSicId) {
          return HttpResponse.json(
            { message: "originSic and destinationSic are required" },
            { status: 400 }
          );
        }

        const records = mockExpiredTollsLaneHistory.map((item) => ({
          id: item.id,
          rate: Number(item.amount.replace(/[^0-9.]/g, "")) || 0,
          effectiveDate: item.effectiveDate,
          expirationDate: item.expirationDate,
          expiredBy: item.expiredBy,
        }));

        const sortOrder =
          searchParams.get("sortOrder")?.toLowerCase() ?? "descending";
        records.sort((a, b) => {
          const aTime = Date.parse(String(a.expirationDate));
          const bTime = Date.parse(String(b.expirationDate));
          return sortOrder === "ascending" ? aTime - bTime : bTime - aTime;
        });

        return HttpResponse.json({
          originSic: Number(originSicId),
          destinationSic: Number(destinationSicId),
          totalCount: records.length,
          records,
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getRatesAccessorialsCarrier(":carrierId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({
          name: "John Smith",
          email: "john.smith@example.com",
          phone: "555-123-4567",
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getRatesAccessorialsCarrierAccessorials(
          ":carrierId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockAccessorialRates.default);
      }
    ),

    http.put(
      formatUrl(apiUrls.updateLCodeAccessorials(":carrierCode")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockAccessorialRates.default);
      }
    ),

    http.get(
      formatUrl(apiUrls.getCarrierLanes(":carrierCode")),
      async ({ request }) => {
        await delay(config.delay);
        const isDestination =
          new URL(request.url).searchParams.get("isDestination") === "true";

        return HttpResponse.json(
          isDestination
            ? mockLCodeCarrierDestinations.default
            : mockCarrierLanesResponse.default
        );
      }
    ),

    http.put(
      formatUrl(
        apiUrls.ratesAndAccessorial.expireRate(":rateId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({}, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.addCarrierLaneRate(
          ":carrierCode",
          ":laneId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true }, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrlsV2.addCarrierLaneRate(
          ":carrierCode",
          ":laneId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true }, { status: 200 });
      }
    ),

    http.put(
      formatUrl(apiUrls.editCarrierLaneRate(":rateId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true }, { status: 200 });
      }
    ),

    http.put(
      formatUrl(apiUrlsV2.editCarrierLaneRate(":rateId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true }, { status: 200 });
      }
    ),

    http.delete(
      formatUrl(apiUrls.deleteCarrierLaneRate(":rateId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true }, { status: 200 });
      }
    ),

    http.delete(
      formatUrl(
        apiUrlsV2.deleteCarrierLaneRate(":rateId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true }, { status: 200 });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.ratesAndAccessorial.getLCodesExpiredAccessorials(":carrierCode")
      ),
      async ({ request }) => {
        await delay(config.delay);
        const searchParams = new URL(request.url).searchParams;
        const type = searchParams.get("type") ?? "Stopoff";
        const sortOrder = searchParams.get("sortOrder") ?? "descending";
        const data = mockExpiredAccessorialHistory.map((item, index) => {
          const base = {
            effectiveDate: item.effectiveDate,
            expirationDate: item.expirationDate,
            expiredBy: item.expiredBy,
          };

          if (type === "Detention") {
            return {
              ...base,
              detentionFifteenMinuteIncrement: 25 + index,
              detentionMaxTime: 200 + index * 10,
              detentionFreeTimeHours: 2 + (index % 3),
            };
          }

          if (type === "TONU") {
            const tonuAmount =
              Number(item.amount.replace(/[^0-9.]/g, "")) || 100;
            return {
              ...base,
              tonuAmount,
              tonuCancellationWindow: 24,
            };
          }

          const amountFieldByType: Record<string, string> = {
            Stopoff: "stopoffAmount",
            Rollover: "rolloverAmount",
          };
          const amountField = amountFieldByType[type];
          return {
            ...base,
            [amountField]: Number(item.amount.replace(/[^0-9.]/g, "")) || 0,
          };
        });

        if (
          type !== "Stopoff" &&
          type !== "Rollover" &&
          type !== "TONU" &&
          type !== "Detention"
        ) {
          return HttpResponse.json(
            { message: `Invalid type: ${type}` },
            { status: 400 }
          );
        }

        data.sort((a, b) => {
          const aTime = Date.parse(String(a.expirationDate));
          const bTime = Date.parse(String(b.expirationDate));
          return sortOrder === "ascending" ? aTime - bTime : bTime - aTime;
        });

        return HttpResponse.json({
          carrierName: mockAccessorialRates.default.carrierName,
          refCode: mockAccessorialRates.default.carrierCode,
          type,
          count: data.length,
          data,
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.ratesAndAccessorial.getLCodesExpiredRates(":carrierCode")
      ),
      async ({ request }) => {
        await delay(config.delay);
        const searchParams = new URL(request.url).searchParams;
        const sortOrder =
          searchParams.get("sortOrder")?.toLowerCase() ?? "descending";

        const data = mockExpiredRatesLCodesHistory.map((item) => ({
          rate: Number(item.amount.replace(/[^0-9.]/g, "")) || 0,
          effectiveDate: item.effectiveDate,
          expirationDate: item.expirationDate,
          expiredBy: item.expiredBy,
        }));

        data.sort((a, b) => {
          const aTime = Date.parse(String(a.expirationDate));
          const bTime = Date.parse(String(b.expirationDate));
          return sortOrder === "ascending" ? aTime - bTime : bTime - aTime;
        });

        const serviceTypeByCode: Record<string, string> = {
          "1": "Wild",
          "2": "Dedicated",
          "3": "Shuttle",
          "4": "OneWay",
          "5": "Solo",
        };
        const serviceTypeParam = searchParams.get("serviceType") ?? "4";

        return HttpResponse.json({
          carrierName: mockAccessorialRates.default.carrierName,
          refCode: mockAccessorialRates.default.carrierCode,
          serviceType: serviceTypeByCode[serviceTypeParam] ?? serviceTypeParam,
          totalCount: data.length,
          records: data,
        });
      }
    ),

    http.get(formatUrl(apiUrls.getCaFscDistinctValues), async ({ request }) => {
      await delay(config.delay);
      const filterName = new URL(request.url).searchParams.get("filterName") as
        | keyof typeof MOCK_CA_FSC_DISTINCT_VALUES
        | null;

      if (!filterName || !(filterName in MOCK_CA_FSC_DISTINCT_VALUES)) {
        return HttpResponse.json([]);
      }

      return HttpResponse.json(MOCK_CA_FSC_DISTINCT_VALUES[filterName]);
    }),

    http.get(formatUrl(apiUrls.loadBoard.delayReasons), () =>
      HttpResponse.json(mockDelayReasons.default)
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.editTripClosedDetails(":tripId" as unknown as number)
      ),
      () => HttpResponse.json(mockTripDetails.default)
    ),

    http.patch(
      formatUrl(
        apiUrls.loadBoard.changeTripStatus(":tripId" as unknown as number)
      ),
      () => HttpResponse.json(mockTripDetails.default)
    ),

    http.post(
      formatUrl(
        apiUrls.loadBoard.updateTransitStatus(
          ":tripNumber" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true }, { status: 200 });
      }
    ),

    http.get(formatUrl(apiUrls.getSicByCode(":sicCode")), () =>
      HttpResponse.json(mockSicDetails.default)
    ),

    http.post(formatUrl(apiUrls.addSicContact(":sicCode")), () =>
      HttpResponse.json(mockSicDetails.default)
    ),

    http.get(
      formatUrl(
        apiUrls.getContactBySicCodeAndId(
          ":sicCode",
          ":contactId" as unknown as number
        )
      ),
      () => HttpResponse.json(mockSicContact.default)
    ),

    http.delete(
      formatUrl(
        apiUrls.getContactBySicCodeAndId(
          ":sicCode",
          ":contactId" as unknown as number
        )
      ),
      () => HttpResponse.json(mockSicContact.default)
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.updateSchedule(":scheduleId" as unknown as number)
      ),
      () => HttpResponse.json(mockTripDetails.default)
    ),
    /** Settings */
    // Route / traps
    // ZipCode and Shipment tags
    http.get(formatUrl(apiUrls.getAvailableTags(":zipcode")), () =>
      HttpResponse.json(mockAvailableZipCodeWithTags.default, {
        status: 200,
      })
    ),

    http.post(formatUrl(apiUrls.createZipCode), () =>
      HttpResponse.json(mockCreateZipCodeWithTags.default)
    ),

    http.put(formatUrl(apiUrls.moveZipCodesV2), () =>
      HttpResponse.json({ status: 200 })
    ),

    http.put(
      formatUrl(
        apiUrls.updateAndDeleteZipcode(":zipcodeId" as unknown as number)
      ),
      async () => {
        await delay(1200);
        return HttpResponse.json(mockUpdateZipCodeWithTags.default, {
          status: 200,
        });
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.updateAndDeleteZipcode(":zipcodeId" as unknown as number)
      ),
      () => HttpResponse.json({ status: 200 })
    ),

    http.get(
      formatUrl(apiUrls.getServiceCentersForLoadPlanReport(":date")),
      () => {
        const scenario = getUrlParam("scenario");
        if (scenario === "no-data") {
          return HttpResponse.json([]);
        }
        return HttpResponse.json(companyOwnedServiceCenters);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getEndOfShiftReports(
          ":sicId" as unknown as number,
          ":date",
          "unpublished-bookings"
        )
      ),
      () => HttpResponse.json(unpublishedBookings.default)
    ),

    http.get(
      formatUrl(
        apiUrls.getEndOfShiftReports(
          ":sicId" as unknown as number,
          ":date",
          "cancelled-trips"
        )
      ),
      () => HttpResponse.json(cancelledTrips.default)
    ),
    // end of shift report

    http.get(formatUrl(apiUrls.getEndOfShiftComments), ({ request }) => {
      const url = new URL(request.url);
      const contextDate = url.searchParams.get("contextDate");
      const regionId = url.searchParams.get("regionId");

      // Return comments if they match the date and region
      if (contextDate === "2025-08-20" && regionId === "3") {
        return HttpResponse.json(endOfShiftComments.default);
      }
      return HttpResponse.json([]);
    }),
    http.post(formatUrl(apiUrls.postEndOfShiftComment), () =>
      HttpResponse.json(endOfShiftComments.default[0])
    ),
    http.patch(formatUrl(apiUrls.editEndOfShiftComment(":commentId")), () =>
      HttpResponse.json(endOfShiftComments.default[0])
    ),
    http.delete(formatUrl(apiUrls.deleteEndOfShiftComment(":commentId")), () =>
      HttpResponse.json(undefined)
    ),
    http.patch(formatUrl(apiUrls.resolveEndOfShiftComment(":commentId")), () =>
      HttpResponse.json({ data: endOfShiftComments.default[0] })
    ),
    http.patch(
      formatUrl(apiUrls.unresolveEndOfShiftComment(":commentId")),
      () => HttpResponse.json({ data: endOfShiftComments.default[0] })
    ),
    http.get(
      formatUrl(apiUrls.getEndOfShiftLatestModifications),
      ({ request }) => {
        const url = new URL(request.url);
        const contextDate = url.searchParams.get("contextDate");
        const regionId = url.searchParams.get("regionId");

        // Return mock data if we have both parameters
        if (contextDate && regionId) {
          return HttpResponse.json(latestModifications.default);
        }
        return HttpResponse.json(null);
      }
    ),

    http.get(formatUrl(apiUrls.getCancellationReport), ({ request }) => {
      const url = new URL(request.url);
      const fromLoadingDate = url.searchParams.get("fromLoadingDate");
      const toLoadingDate = url.searchParams.get("toLoadingDate");

      if (fromLoadingDate && toLoadingDate && fromLoadingDate > toLoadingDate) {
        return HttpResponse.text(
          "fromLoadingDate must not be greater than toLoadingDate",
          {
            status: 400,
          }
        );
      }
      return HttpResponse.json(cancellationReportMock.default);
    }),

    http.get(formatUrl(apiUrls.getCancellationReportPdf), () => {
      const reportType = "CancellationsReport";
      const pdfBase64 = `
        JVBERi0xLjQKJeLjz9MKMSAwIG9iago8PC9UeXBlL0NhdGFsb2cvUGFnZXMgMiAwIFIgPj4KZW5kb2Jq
        CjIgMCBvYmoKPDwvVHlwZS9QYWdlcy9LaWRzWzMgMCBSXS9Db3VudCAxPj4KZW5kb2JqCjMgMCBvYmoK
        PDwvVHlwZS9QYWdlL1BhcmVudCAyIDAgUi9NZWRpYUJveFswIDAgNjEyIDc5Ml0vUmVzb3VyY2VzPDwv
        Rm9udDw8L0YwIDQgMCBSPj4+Pi9Db250ZW50cyA1IDAgUj4+CmVuZG9iago0IDAgb2JqCjw8L1R5cGUv
        Rm9udC9TdWJ0eXBlL1R5cGUxL05hbWUvRjAvQmFzZUZvbnQvSGVsdmV0aWNhPj4KZW5kb2JqCjUgMCBv
        YmoKPDwvTGVuZ3RoIDYzPj4Kc3RyZWFtCkJUCi9GMCBUIDI0IFRmCjAgMCBUZAooSGVsbG8gV29ybGQh
        KSBUagpFVAplbmRzdHJlYW0KZW5kb2JqCnhyZWYKMCA2CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAw
        MDAxNSAwMDAwMCBuIAowMDAwMDAwMDY3IDAwMDAwIG4gCjAwMDAwMDAxMjggMDAwMDAgbiAKMDAwMDAw
        MDI0MCAwMDAwMCBuIAowMDAwMDAwMzQ3IDAwMDAwIG4gCnRyYWlsZXIKPDwvUm9vdCAxIDAgUi9TaXpl
        IDY+PgpzdGFydHhyZWYKNDA2CiUlRU9GCg==`.replace(/\s+/g, "");

      // Using atob cause there's no Buffer available into Vite's engine
      // eslint-disable-next-line deprecation/deprecation
      const binaryString = atob(pdfBase64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: "application/pdf" });

      const currentTimestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `${reportType}_${currentTimestamp}.pdf`;

      const headers: Record<string, string> = {
        "Content-Type": "application/pdf",
      };

      // Default to download
      headers["Content-Disposition"] = `attachment; filename=${filename}`;

      return new HttpResponse(blob, {
        status: 200,
        headers,
      });
    }),

    http.delete(
      formatUrl(
        apiUrls.loadBoard.unpublishSchedule(":scheduleId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.loadBoard.publishSchedule(":scheduleId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // BackOffice

    http.get(formatUrl(apiUrls.getBillers(":searchTerm")), () =>
      HttpResponse.json({
        success: true,
        message: null,
        errorCode: null,
        item: mockBillers,
      })
    ),

    http.patch(formatUrl(apiUrls.assignBiller), async () => {
      await delay(config.delay);
      return HttpResponse.json({
        success: true,
        message: "Bills assigned successfully",
        errorCode: null,
      });
    }),

    http.get(
      formatUrl(apiUrls.getBackOfficeAccounts(":searchTerm")),
      async ({ params }) => {
        await delay(config.delay);

        const searchTerm = ((params.searchTerm as string) || "").toLowerCase();

        // Filter carriers by search term (match code, name, or zipcode)
        const filteredCarriers = carrierOptions.filter((carrier) => {
          const matchesCode = carrier.code.toLowerCase().includes(searchTerm);
          const matchesName = carrier.name.toLowerCase().includes(searchTerm);
          const matchesZipcode = carrier.zipcode
            .toLowerCase()
            .includes(searchTerm);
          return matchesCode || matchesName || matchesZipcode;
        });

        return HttpResponse.json({
          success: true,
          message: "Accounts retrieved successfully",
          errorCode: null,
          item: filteredCarriers.map((carrier) => ({
            id: carrier.id,
            accountNumber: carrier.code,
            accountName: carrier.name,
            address: carrier.address,
            city: carrier.city,
            state: carrier.state,
            postalCode: carrier.zipcode,
            country: carrier.country,
            contactName: carrier.contact.name,
            phone: carrier.contact.phone,
            email: carrier.contact.email,
          })),
        });
      }
    ),

    http.get(
      formatUrl(apiUrls.getClientAccountDetails),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const accountId = url.searchParams.get("accountId");

        if (!accountId) {
          return HttpResponse.json(
            {
              success: false,
              message: "Account ID is required",
              errorCode: "MISSING_ACCOUNT_ID",
              item: null,
            },
            { status: 400 }
          );
        }

        const carrier = carrierOptions.find((c) => c.code === accountId);

        if (!carrier) {
          return HttpResponse.json(
            {
              success: false,
              message: `Account with ID '${accountId}' not found`,
              errorCode: "ACCOUNT_NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        return HttpResponse.json({
          success: true,
          message: `Retrieved account details for '${accountId}'`,
          errorCode: null,
          item: {
            accountNumber: carrier.code,
            accountName: carrier.name,
            rateClientId: 1000 + carrier.id,
            address: carrier.address,
            city: carrier.city,
            state: carrier.state,
            zip: carrier.zipcode,
            country: carrier.country,
            contactName: carrier.contact.name,
            contactPhone: carrier.contact.phone,
            contactEmail: carrier.contact.email,
            accountStatus: carrier.status === "ACTIVE",
            creditStatus: carrier.creditStatus,
            originServiceCenter: carrier.originServiceCenter,
          },
        });
      }
    ),

    http.get(
      formatUrl(apiUrls.getSearchAccountsPreferences(":searchQuery")),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const searchQuery =
          url.searchParams.get("search_query")?.toLowerCase() || "";

        const filteredAccounts = searchQuery
          ? mockBackOfficeAccounts.default.item.filter(
              (account) =>
                account.id.toString().includes(searchQuery) ||
                account.accountNumber.toLowerCase().includes(searchQuery) ||
                account.accountName.toLowerCase().includes(searchQuery)
            )
          : [];

        return HttpResponse.json({
          ...mockBackOfficeAccounts.default,
          item: filteredAccounts,
        });
      }
    ),

    http.get(formatUrl(apiUrls.getRecentAccountsPreferences), async () => {
      await delay(config.delay);
      const recentAccounts = mockBackOfficeAccounts.default.item.slice(0, 5);

      return HttpResponse.json({
        ...mockBackOfficeAccounts.default,
        item: recentAccounts,
      });
    }),

    http.post(
      formatUrl(apiUrls.saveAccountPreferences),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          id: number;
          accountNumber: string;
          accountName: string;
        };

        return HttpResponse.json({
          success: true,
          message: "Account preference saved successfully",
          errorCode: null,
          item: {
            id: body.id,
            accountNumber: body.accountNumber || "",
            accountName: body.accountName || "",
          },
        });
      }
    ),

    http.get(formatUrl(apiUrls.getZipCodes(":zipcode")), () =>
      HttpResponse.json(mockBackOfficeLocations.default)
    ),

    http.get(formatUrl(apiUrls.getCommodityClasses()), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockCommodityClassOptions.default);
    }),

    http.get(formatUrl(apiUrls.getAdditionalServices), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockAdditionalServices.default);
    }),

    http.get(formatUrl(apiUrls.getShippingInstructions), async () => {
      await delay(config.delay);
      return HttpResponse.json({
        success: true,
        message: null,
        errorCode: null,
        item: shippingInstructionOptions.map((title, index) => ({
          id: index + 1,
          title,
        })),
      });
    }),

    // Create Quick Quote
    http.post(formatUrl(apiUrls.createQuickQuote), async () => {
      await delay(config.delay);

      const mockResponse = {
        success: true,
        message: "Quote saved",
        item: {
          standardServiceId: 399,
          guaranteedServiceId: 400,
        },
        errorCode: null,
      };

      return HttpResponse.json(mockResponse, { status: 200 });
    }),

    // Update Quick Quote
    http.put(
      new RegExp(
        formatUrl(apiUrls.updateQuickQuote(0)).replace(
          "guaranteedServiceId=0",
          "guaranteedServiceId=\\d+"
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const guaranteedServiceId = url.searchParams.get("guaranteedServiceId");

        const mockResponse = {
          success: true,
          message: "Quick Quote saved",
          item: {
            standardServiceId: guaranteedServiceId
              ? Number(guaranteedServiceId) - 1
              : 723,
            guaranteedServiceId: guaranteedServiceId
              ? Number(guaranteedServiceId)
              : 724,
          },
          errorCode: null,
        };

        return HttpResponse.json(mockResponse, { status: 200 });
      }
    ),

    // Get Quick Quote Details
    http.get(
      formatUrl(
        apiUrls.getQuickQuoteDetails(
          ":guaranteedServiceId" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { guaranteedServiceId } = params;
        const serviceId = Number(guaranteedServiceId);
        if (!serviceId) {
          return new HttpResponse(null, { status: 404 });
        }

        const mockResponse = {
          success: true,
          message: "Quote details retrieved successfully",
          errorCode: null,
          item: mockQuickQuoteSummaryData,
        };

        return HttpResponse.json(mockResponse, { status: 200 });
      }
    ),

    // Send Quote Email
    http.post(formatUrl(apiUrls.sendQuoteEmail), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const scenario = url.searchParams.get("scenario");

      // Schema validation (reject clearly invalid requests)
      const requestSchema = z.object({
        toAddresses: z.array(z.string().email()).nonempty(),
        subject: z.string().min(1),
        message: z.string().min(1),
        quoteNumber: z.number(),
      });

      let body;
      try {
        body = await request.json();
        requestSchema.parse(body);
      } catch {
        return HttpResponse.json(
          {
            success: false,
            message: "Invalid request",
            errorCode: "VALIDATION_ERROR",
            item: null,
          },
          { status: 400 }
        );
      }

      if (scenario === "error") {
        return HttpResponse.json(
          {
            success: false,
            message: "Failed to send quote email",
            errorCode: "EMAIL_SEND_FAILED",
            item: null,
          },
          { status: 500 }
        );
      }

      return HttpResponse.json(
        {
          success: true,
          message: "Quote email sent successfully",
          errorCode: null,
          item: {
            messageId:
              "0111019a24e415ae-5edf6456-e216-483f-8acd-da986c1e3eb0-000000",
            status: "sent",
          },
        },
        { status: 200 }
      );
    }),

    http.get(
      formatUrl(apiUrls.loadBoard.getNotes(":scheduleId" as unknown as number)),
      () => HttpResponse.json(mockScheduleNotes.default)
    ),

    http.get(
      formatUrl(apiUrls.loadBoard.bulkManifestShipments(":searchTerm")),
      () => HttpResponse.json(mockBulkManifestShipments.default)
    ),

    http.post(formatUrl(apiUrls.getShipments), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockShipments, { status: 200 });
    }),

    http.post(formatUrl(apiUrls.getShipmentsStatus), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            statusCounts: mockShipmentsStatusCounts,
          },
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getServiceLevelOptions), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockServiceLevelOptions,
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getPickupTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockPickupTypes,
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getTraceTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockTraceTypes,
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getServiceLevels), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockServiceLevels,
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getSpecialShipmentTagTypes), async () => {
      await delay(config.delay);
      const labelSeparator = " - ";
      const item = specialShipmentTagOptions.map((label, index) => {
        const sepIndex = label.indexOf(labelSeparator);
        if (sepIndex === -1) {
          return {
            id: index + 1,
            name: label,
            description: "",
          };
        }
        return {
          id: index + 1,
          name: label.slice(0, sepIndex).trim(),
          description: label.slice(sepIndex + labelSeparator.length).trim(),
        };
      });
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item,
        },
        { status: 200 }
      );
    }),

    http.get(
      formatUrl(apiUrls.getClientAccessorialTypes("P")),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const rawType = url.searchParams.get("accessorial_type") ?? "P";
        const appliesTo =
          rawType === "D" || rawType === "B" || rawType === "P" ? rawType : "P";
        const sourceList =
          appliesTo === "D"
            ? deliveryAccessorialOptions
            : appliesTo === "B"
              ? bothAccessorialOptions
              : pickupAccessorialOptions;
        const item = sourceList.map((instruction, index) => ({
          id: index + 1,
          instruction,
          appliesTo,
          description: "",
          webEnabled: true,
          displayName: instruction,
        }));
        return HttpResponse.json(
          {
            success: true,
            message: `Retrieved ${item.length} records`,
            errorCode: null,
            item,
          },
          { status: 200 }
        );
      }
    ),

    http.get(formatUrl(apiUrls.getHandlingTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: `Retrieved ${handlingUnitOptions.length} handling types`,
          errorCode: null,
          item: handlingUnitOptions.map((option, index) => ({
            id: index + 1,
            unitType: "P",
            name: option.value,
          })),
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getPiecesTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: `Retrieved ${pieceTypeOptions.length} pieces types`,
          errorCode: null,
          item: pieceTypeOptions.map((option, index) => {
            const parts = option.value.split(" - ");
            return {
              id: index + 1,
              unitType: "I",
              unitSymbol: parts[0] || "",
              name: parts[1] || option.value,
            };
          }),
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getFreightClasses), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: `Retrieved ${classValues.length} freight classes`,
          errorCode: null,
          item: classValues.map((classValue, index) => ({
            id: 77 - index,
            nmfcClass: parseFloat(classValue),
            commodityCode: classValue,
            description: `Tariff Class ${classValue}`,
          })),
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getNmfcCodes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: `Retrieved ${nmfcOptions.length} NMFC codes`,
          errorCode: null,
          item: nmfcOptions.map((code, index) => ({
            id: index + 1,
            nmfcItem: code,
            nmfcDescription: `NMFC code ${code}`,
          })),
        },
        { status: 200 }
      );
    }),

    http.get(
      formatUrl(apiUrls.getNmfcSubs(":nmfcItem")),
      async ({ params }) => {
        await delay(config.delay);
        const nmfcItem = params.nmfcItem as string;

        return HttpResponse.json(
          {
            success: true,
            message: `Retrieved ${subOptions.length} NMFC sub codes for NMFC code ${nmfcItem}`,
            errorCode: null,
            item: subOptions.map((sub, index) => ({
              id: index + 1,
              nmfcItem,
              nmfcSub: `0${index + 1}`,
              nmfcDescription: sub,
            })),
          },
          { status: 200 }
        );
      }
    ),

    http.post(
      formatUrl(apiUrls.calculateFreightDensity),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          length: number;
          width: number;
          height: number;
          mass: number;
          unitQty: number;
        };

        // Calculate cubic feet from dimensions (inches to cubic feet)
        const cubicFeet = (body.length * body.width * body.height) / 1728;

        // Calculate density (weight per cubic foot)
        const density =
          cubicFeet > 0 ? (body.mass / cubicFeet).toFixed(2) : "0.00";

        return HttpResponse.json(
          {
            success: true,
            message: `Freight calculated: ${density} lb/ft³`,
            errorCode: null,
            item: {
              density,
              densityUnit: "lb/ft³",
              linearFeet: null,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.post(
      formatUrl(apiUrls.checkLaneServiceability),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          shipperZip: string;
          consigneeZip: string;
        };

        // Check against non-serviceable lanes using mock data
        const laneKey = `${body.shipperZip}-${body.consigneeZip}`;
        const isNonServiceable = NON_SERVICEABLE_LANES[laneKey] === true;
        const isServiceable = !isNonServiceable;

        return HttpResponse.json(
          {
            success: true,
            message: "Serviceability check completed successfully",
            errorCode: null,
            item: {
              isServiceable,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.get(formatUrl(apiUrls.getFuelPriceList), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: "Fuel price list retrieved successfully",
          errorCode: null,
          item: {
            fuelPrices: mockFuelPriceListOptions,
          },
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getCriteriaLogicOptions), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockCriteriaLogicOperators,
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getCriteriaTableNames), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockTableNameOptions,
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getCriteriaGroupLogicOptions), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockCriteriaGroupLogicOperators,
        },
        { status: 200 }
      );
    }),

    http.get(
      formatUrl(apiUrls.getCriteriaFieldNames(":tableId" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const { tableId } = params;
        const tableIdNum = Number(tableId);

        // Get field names for the specific table
        const fieldNames = mockFieldNamesByTable[tableIdNum] ?? [];

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: fieldNames,
          },
          { status: 200 }
        );
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getCriteriaLookupTypes(":tableId" as unknown as number)
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { tableId } = params;
        const tableIdNum = Number(tableId);

        // Get lookup types for the specific table
        const lookupTypes = mockLookupTypesByTable[tableIdNum] ?? [];

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: lookupTypes,
          },
          { status: 200 }
        );
      }
    ),

    http.post(formatUrl(apiUrls.getFuelScales), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        page: number;
        pageSize: number;
        search: string;
        sortBy: string;
        sortOrder: string;
        status: string;
      };
      const {
        page = 1,
        pageSize = 10,
        search = "",
        sortBy = "",
        sortOrder = "",
        status = "",
      } = body;

      const filteredScales = mockFuelScales.fuelScales.filter((scale) => {
        if (search) {
          const lowerSearch = search.toLowerCase();
          const matchesSearch =
            scale.code.toLowerCase().includes(lowerSearch) ||
            scale.longDescription.toLowerCase().includes(lowerSearch);
          if (!matchesSearch) return false;
        }
        if (status && scale.status !== status) return false;
        return true;
      });

      if (sortBy) {
        filteredScales.sort((a, b) => {
          const getValue = (scale: typeof a) => {
            switch (sortBy) {
              case "code":
                return scale.code;
              case "longDescription":
                return scale.longDescription;
              case "fuelPrice":
                return scale.fuelPrice;
              case "fuelPriceValue":
                return scale.fuelPriceValue
                  ? parseFloat(scale.fuelPriceValue)
                  : 0;
              case "status":
                return scale.status;
              default:
                return "";
            }
          };
          const aVal = getValue(a);
          const bVal = getValue(b);
          const comparison =
            typeof aVal === "number" && typeof bVal === "number"
              ? aVal - bVal
              : String(aVal).localeCompare(String(bVal));
          return sortOrder === "desc" ? -comparison : comparison;
        });
      }

      const totalRecords = filteredScales.length;
      const startIndex = (page - 1) * pageSize;
      const paginatedScales = filteredScales.slice(
        startIndex,
        startIndex + pageSize
      );

      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            fuelScales: paginatedScales,
            pagination: {
              totalRecords,
              totalPages: Math.ceil(totalRecords / pageSize),
              currentPage: page,
              pageSize,
            },
          },
        },
        { status: 200 }
      );
    }),

    http.post(
      formatUrl(apiUrls.getAccessorialChargesList),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          page: number;
          pageSize: number;
          search: string;
          sortBy: string;
          sortOrder: string;
          status: string;
          appliesTo: string;
        };
        const { page = 1, pageSize = 10 } = body;

        const allCharges = mockAccessorialChargesListData.accessorialCharges;
        const totalRecords = allCharges.length;
        const startIndex = (page - 1) * pageSize;
        const paginatedCharges = allCharges.slice(
          startIndex,
          startIndex + pageSize
        );

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: {
              accessorialCharges: paginatedCharges,
              pagination: {
                totalRecords,
                totalPages: Math.ceil(totalRecords / pageSize),
                currentPage: page,
                pageSize,
              },
            },
          },
          { status: 200 }
        );
      }
    ),

    http.get(
      formatUrl(apiUrls.getAccessorialChargeAccessorialTypes),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: mockAccessorialTypeOptions,
          },
          { status: 200 }
        );
      }
    ),

    http.get(formatUrl(apiUrls.getAccessorialChargeMovementTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockMovementTypeOptions,
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getAccessorialChargeCurrencyTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockCurrencyOptions,
        },
        { status: 200 }
      );
    }),

    http.get(
      formatUrl(apiUrls.getAccessorialChargeInstructionCodes),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: mockShippingInstructionsCodeOptions,
          },
          { status: 200 }
        );
      }
    ),

    http.get(formatUrl(apiUrls.getAccessorialChargeRateTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockRatingRateTypeOptions,
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getAccessorialChargeUnitTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockRatingUnitOptions,
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getAccessorialChargeCalcTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockCalculatePercentageOfOptions,
        },
        { status: 200 }
      );
    }),

    // Accessorial Charges - Selection (for Add New Accessorial Schedule)
    // MUST come BEFORE getAccessorialChargeById to avoid route conflict
    http.get(formatUrl(apiUrls.getAccessorialChargesForSelection), async () => {
      await delay(config.delay);

      return HttpResponse.json(
        {
          success: true,
          message: "Accessorial charges retrieved successfully",
          errorCode: null,
          item: {
            accessorialCharges: mockAccessorialScheduleChargeOptions,
          },
        },
        { status: 200 }
      );
    }),

    // POST - Get detailed accessorial charge information by IDs
    http.post(
      formatUrl(apiUrls.getSelectedAccessorialChargesDetails),
      async ({ request }) => {
        await delay(config.delay);

        const body = (await request.json()) as {
          accessorialIds?: number[];
          accessorialScheduleId?: number;
        };
        const { accessorialIds, accessorialScheduleId } = body;

        // If accessorialScheduleId is provided, return all charges for that schedule
        // If accessorialIds is provided, return specific charges by IDs
        let detailedCharges: typeof mockAddedAccessorialScheduleChargeOptions;

        if (accessorialScheduleId) {
          // For now, return all charges (in real scenario, filter by schedule)
          detailedCharges = mockAddedAccessorialScheduleChargeOptions;
        } else if (accessorialIds && accessorialIds.length > 0) {
          detailedCharges = mockAddedAccessorialScheduleChargeOptions.filter(
            (charge) => accessorialIds.includes(charge.id)
          );
        } else {
          return HttpResponse.json(
            {
              success: false,
              message:
                "Either accessorialScheduleId or accessorialIds must be provided",
              errorCode: "MISSING_REQUIRED_PARAMETER",
              item: [],
            },
            { status: 400 }
          );
        }

        return HttpResponse.json(
          {
            success: true,
            message: "Accessorial charge details retrieved successfully",
            errorCode: null,
            item: {
              accessorialCharges: detailedCharges,
            },
          },
          { status: 200 }
        );
      }
    ),

    // POST - Create Accessorial Schedule
    http.post(
      formatUrl(apiUrls.createAccessorialSchedule),
      async ({ request }) => {
        await delay(config.delay);

        const body = (await request.json()) as {
          name: string;
          description: string;
          accessorialList: number[];
          status: string;
        };

        const newSchedule = {
          id: Math.floor(Math.random() * 10000),
          name: body.name,
          description: body.description,
          status: body.status,
        };

        return HttpResponse.json(
          {
            success: true,
            message: "Accessorial schedule created successfully",
            errorCode: null,
            item: newSchedule,
          },
          { status: 201 }
        );
      }
    ),

    // GET by ID - MUST come AFTER the specific routes above
    http.get(
      formatUrl(apiUrls.getAccessorialChargeById(":id" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const { id } = params;

        const detailData = {
          ...mockAccessorialChargeDetail,
          id: Number(id),
        };

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: detailData,
          },
          { status: 200 }
        );
      }
    ),

    // GET accessorial charge criteria
    http.get(
      formatUrl(
        apiUrls.getAccessorialChargeCriteria(":criteriaId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: mockAccessorialCriteria,
          },
          { status: 200 }
        );
      }
    ),

    // PATCH accessorial charge status
    http.patch(
      formatUrl(
        apiUrls.updateAccessorialChargeStatus(":id" as unknown as number)
      ),
      async ({ params, request }) => {
        await delay(config.delay);
        const { id } = params;
        const body = (await request.json()) as { status: string };

        // Simulate returning blocking schedules when trying to inactivate
        const blockingSchedules =
          body.status === "Inactive" ? mockInactivateWarningSchedules : null;

        return HttpResponse.json(
          {
            success: !blockingSchedules,
            message: blockingSchedules
              ? "This accessorial charge is used in 3 accessorial schedules. Remove or replace it before inactivating."
              : "Status updated successfully",
            errorCode: null,
            item: {
              id: Number(id),
              status: blockingSchedules ? null : body.status,
              blockingAccessorialSchedules: blockingSchedules,
            },
          },
          { status: 200 }
        );
      }
    ),

    // Accessorial Schedules - List
    http.post(
      formatUrl(apiUrls.getAccessorialSchedulesList),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          page: number;
          pageSize: number;
          sortBy: string;
          sortOrder: string;
          search: string;
          status: string;
        };
        const { page = 1, pageSize = 10 } = body;

        const allSchedules = mockAccessorialSchedulesListItems;
        const totalRecords = allSchedules.length;
        const startIndex = (page - 1) * pageSize;
        const paginatedSchedules = allSchedules.slice(
          startIndex,
          startIndex + pageSize
        );

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: {
              accessorialSchedules: paginatedSchedules,
              pagination: {
                totalRecords,
                totalPages: Math.ceil(totalRecords / pageSize),
                currentPage: page,
                pageSize,
              },
            },
          },
          { status: 200 }
        );
      }
    ),

    // Accessorial Schedules - Get by ID
    http.get(
      formatUrl(apiUrls.getAccessorialScheduleById(":id" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const { id } = params;
        const scheduleId = Number(id);

        const schedule = mockAccessorialSchedules.find(
          (s) => s.id === scheduleId
        );

        if (!schedule) {
          return HttpResponse.json(
            {
              success: false,
              message: `Accessorial schedule with ID ${scheduleId} not found`,
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        return HttpResponse.json(
          {
            success: true,
            message: `Retrieved accessorial schedule details for ID: ${scheduleId}`,
            errorCode: null,
            item: schedule,
          },
          { status: 200 }
        );
      }
    ),

    // Accessorial Schedules - Update (PATCH)
    http.patch(
      formatUrl(apiUrls.updateAccessorialSchedule(":id" as unknown as number)),
      async ({ params, request }) => {
        await delay(config.delay);
        const scheduleId = Number(params.id);
        const body = (await request.json()) as {
          name: string;
          description: string;
          accessorialList: number[];
          status?: string;
        };

        const schedule = mockAccessorialSchedules.find(
          (s) => s.id === scheduleId
        );

        if (!schedule) {
          return HttpResponse.json(
            {
              success: false,
              message: `Accessorial schedule with ID ${scheduleId} not found`,
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        schedule.name = body.name;
        schedule.description = body.description;
        if (body.status === "Draft") {
          schedule.status = "Draft";
        }

        return HttpResponse.json(
          {
            success: true,
            message: "Accessorial schedule updated successfully",
            errorCode: null,
            item: { accessorialScheduleId: scheduleId },
          },
          { status: 200 }
        );
      }
    ),

    // Accessorial Schedules - Get Accounts
    http.post(
      formatUrl(apiUrls.getAccessorialScheduleAccounts),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          scheduleId: number;
          page: number;
          pageSize: number;
          search?: string;
          sortBy?: string;
          sortOrder?: string;
        };
        const { page, pageSize } = body;

        const mockAccounts = mockFuelScales.commonAccounts;

        // Apply pagination
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedAccounts = mockAccounts.slice(startIndex, endIndex);

        return HttpResponse.json(
          {
            success: true,
            message: `Retrieved ${paginatedAccounts.length} linked accounts.`,
            errorCode: null,
            item: {
              accounts: paginatedAccounts,
              pagination: {
                totalRecords: mockAccounts.length,
                totalPages: Math.ceil(mockAccounts.length / pageSize),
                currentPage: page,
                pageSize,
              },
            },
          },
          { status: 200 }
        );
      }
    ),

    http.post(formatUrl(apiUrls.getBillingQueues), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        page: number;
        pageSize: number;
        search: string;
        sortBy?: string;
        sortOrder?: string;
        billState?: number[];
        regionTimeZone?: string[];
        shipmentTag?: number[];
        biller?: number[];
      };
      const { page = 1, pageSize = 10, search = "" } = body;

      let filtered = [...mockBills];

      // Search filter
      if (search.trim()) {
        const q = search.toLowerCase();
        filtered = filtered.filter(
          (b) =>
            b.proNumber.toLowerCase().includes(q) ||
            b.shipper.toLowerCase().includes(q) ||
            b.consignee.toLowerCase().includes(q)
        );
      }

      const unassigned = filtered.filter(
        (b) => b.billState === "Unassigned"
      ).length;
      const completed = filtered.filter(
        (b) => b.billState === "Completed"
      ).length;
      const totalRecords = filtered.length;
      const startIndex = (page - 1) * pageSize;
      const items = filtered.slice(startIndex, startIndex + pageSize);

      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            billingQueue: items,
            pagination: {
              totalRecords,
              totalPages: Math.ceil(totalRecords / pageSize),
              currentPage: page,
              pageSize,
            },
            metrics: {
              totalBills: totalRecords,
              unassigned,
              completedBills: completed,
            },
          },
        },
        { status: 200 }
      );
    }),

    // Billing Queue Filters API
    http.get(formatUrl(apiUrls.getBillingQueuesFilters), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: "Retrieved billing queue filter options",
          errorCode: null,
          item: {
            billStates: BILL_STATES.filter((state) => state !== "All").map(
              (name, index) => ({
                id: 1033 + index,
                name,
              })
            ),
            shipmentTags: TAGS.filter((tag) => tag !== "All").map(
              (name, index) => ({
                id: index + 1,
                name,
              })
            ),
            regionTimeZones: REGION_TIME_ZONES.filter((zone) => zone !== "All"),
            billers: mockBillers,
          },
        },
        { status: 200 }
      );
    }),

    http.post(
      formatUrl(apiUrls.getLaneMinimumSchedules),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          search: string;
          status: string | null;
          sortByName: boolean;
        };
        const { search = "", status = null, sortByName = false } = body;

        let filteredSchedules = [...mockLaneMinimumSchedules];

        // Filter by search
        if (search) {
          const lowerSearch = search.toLowerCase();
          filteredSchedules = filteredSchedules.filter(
            (schedule) =>
              schedule.name.toLowerCase().includes(lowerSearch) ||
              (schedule.description ?? "").toLowerCase().includes(lowerSearch)
          );
        }

        // Filter by status
        if (status) {
          filteredSchedules = filteredSchedules.filter(
            (schedule) => schedule.status === status
          );
        }

        // Sort by name if requested
        if (sortByName) {
          filteredSchedules.sort((a, b) => a.name.localeCompare(b.name));
        }

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: {
              laneMinimumSchedules: filteredSchedules,
            },
          },
          { status: 200 }
        );
      }
    ),

    // High Cost Zip Code Schedules
    http.post(
      formatUrl(apiUrls.getHighCostZipCodeSchedules),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          search: string;
          status: string | null;
          sortByName: boolean;
        };
        const { search = "", status = null, sortByName = false } = body;

        let filteredSchedules = [...mockHighCostZipCodeSchedules];

        // Filter by search
        if (search) {
          const lowerSearch = search.toLowerCase();
          filteredSchedules = filteredSchedules.filter((schedule) => {
            const nameMatch = schedule.name.toLowerCase().includes(lowerSearch);
            const descMatch =
              schedule.description &&
              schedule.description.toLowerCase().includes(lowerSearch);
            return nameMatch || descMatch;
          });
        }

        // Filter by status
        if (status) {
          filteredSchedules = filteredSchedules.filter(
            (schedule) => schedule.status === status
          );
        }

        // Sort by name if requested
        if (sortByName) {
          filteredSchedules.sort((a, b) => a.name.localeCompare(b.name));
        }

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: {
              highCostZipCodeSchedules: filteredSchedules,
            },
          },
          { status: 200 }
        );
      }
    ),

    // Get High Cost Zip Code Schedule by ID
    http.get(
      formatUrl(apiUrls.getHighCostZipCodeScheduleById(":id")),
      async ({ params }) => {
        await delay(config.delay);
        const scheduleId = Number(params.id);

        const schedule = mockHighCostZipCodeSchedules.find(
          (s) => s.id === scheduleId
        );

        if (!schedule) {
          return HttpResponse.json(
            {
              success: false,
              message: "Schedule not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        return HttpResponse.json(
          {
            success: true,
            message: "Schedule retrieved successfully",
            errorCode: null,
            item: {
              ...mockHighCostZipCodeScheduleApiDetail,
              id: schedule.id,
              name: schedule.name,
              description: schedule.description,
              status: schedule.status,
            },
          },
          { status: 200 }
        );
      }
    ),

    // Create High Cost Zip Code Schedule
    http.post(
      formatUrl(apiUrls.createHighCostZipCodeSchedule),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          name: string;
          description: string;
          status: string;
          highCostZips: Array<{
            sequence: number;
            zipCodeType: number;
            lowZipCode: string;
            highZipCode: string | null;
            rateType: number;
            rate: number;
            minimumAmt: number | null;
            scope: string;
            appliesTo: string;
            state: string;
          }>;
        };

        // Generate a mock response
        return HttpResponse.json(
          {
            success: true,
            message: "High cost zip code schedule created successfully",
            errorCode: null,
            item: {
              id: Math.floor(Math.random() * 1000) + 100,
              name: body.name,
              description: body.description,
              status: body.status,
              zipCodeCount: body.highCostZips.length,
            },
          },
          { status: 200 }
        );
      }
    ),

    // Update High Cost Zip Code Schedule
    http.put(
      formatUrl(apiUrls.updateHighCostZipCodeSchedule(":scheduleId")),
      async ({ request, params }) => {
        await delay(config.delay);
        const { scheduleId } = params;
        const body = (await request.json()) as {
          name: string;
          description: string;
          status: string;
          highCostZips: Array<{
            sequence: number;
            zipCodeType: number;
            lowZipCode: string;
            highZipCode: string | null;
            rateType: number;
            rate: number;
            minimumAmt: number | null;
            scope: string;
            appliesTo: string;
            state: string;
          }>;
        };

        // Generate a mock response
        return HttpResponse.json(
          {
            success: true,
            message: "High cost zip code schedule updated successfully",
            errorCode: null,
            item: {
              id: Number(scheduleId),
              name: body.name,
              description: body.description,
              status: body.status,
              zipCodeCount: body.highCostZips.length,
            },
          },
          { status: 200 }
        );
      }
    ),

    // Get Zip Code Types
    http.get(formatUrl(apiUrls.getZipCodeTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            zipCodeTypes: mockZipCodeTypes,
          },
        },
        { status: 200 }
      );
    }),

    // Get Rate Structures
    http.get(formatUrl(apiUrls.getRateStructures), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            rateStructures: mockRateStructures,
          },
        },
        { status: 200 }
      );
    }),

    // Get Scopes
    http.get(formatUrl(apiUrls.getScopes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            scopes: mockScopes,
          },
        },
        { status: 200 }
      );
    }),

    // Get Applies To
    http.get(formatUrl(apiUrls.getAppliesTo), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            appliesTo: mockAppliesTo,
          },
        },
        { status: 200 }
      );
    }),

    // Get Zip Codes for High Cost
    http.get(
      formatUrl(apiUrls.getZipCodesForHighCost()),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const search = url.searchParams.get("search");

        let filteredZipCodes = [...mockZipCodes];

        // Filter by search (prefix match on zip code)
        if (search) {
          filteredZipCodes = filteredZipCodes.filter((zip) =>
            zip.zipCode.startsWith(search)
          );
        }

        return HttpResponse.json(
          {
            success: true,
            message: `Retrieved ${filteredZipCodes.length} zip code(s)`,
            errorCode: null,
            item: {
              zipCodes: filteredZipCodes,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.post(formatUrl(apiUrls.getLaneMinimums), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        laneMinimumScheduleId: number;
        page: number;
        pageSize: number;
        search: string;
        sortBy: string;
        sortOrder: string;
      };
      const { page = 1, pageSize = 25 } = body;

      const allLanes = [...mockLaneMinimumList];

      // Pagination
      const totalRecords = allLanes.length;
      const totalPages = Math.ceil(totalRecords / pageSize);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedLanes = allLanes.slice(startIndex, endIndex);

      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            laneMinimums: paginatedLanes.map((lane) => ({
              ...lane,
              minimumCharge: String(lane.minimumCharge),
            })),
            pagination: {
              totalRecords,
              totalPages,
              currentPage: page,
              pageSize,
            },
          },
        },
        { status: 200 }
      );
    }),

    http.post(formatUrl(apiUrls.getHighCostZipCodes), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        scheduleId: number;
        page: number;
        pageSize: number;
        search: string;
        sortBy: string;
        sortOrder: string;
      };
      const {
        page = 1,
        pageSize = 25,
        search = "",
        sortBy = "",
        sortOrder = "",
      } = body;

      let filteredZipCodes = [...mockHighCostZipCodeList];

      // Apply search filter
      if (search && search.trim() !== "") {
        const searchLower = search.toLowerCase().trim();
        filteredZipCodes = filteredZipCodes.filter((zip) => {
          const checks = [
            zip.lowZipCode,
            zip.highZipCode,
            zip.state,
            zip.stateName,
            zip.zipCodeTypeName,
            zip.rateTypeName,
            zip.scope,
            zip.appliesTo,
          ];
          return checks.some(
            (field) => field && field.toLowerCase().includes(searchLower)
          );
        });
      }

      // Apply sorting
      if (sortBy && sortBy.trim() !== "") {
        filteredZipCodes.sort((a, b) => {
          const aVal = a[sortBy as keyof typeof a];
          const bVal = b[sortBy as keyof typeof b];

          // Handle null values
          if (aVal === null) return sortOrder === "desc" ? 1 : -1;
          if (bVal === null) return sortOrder === "desc" ? -1 : 1;

          // Compare values
          if (typeof aVal === "string" && typeof bVal === "string") {
            const comparison = aVal.localeCompare(bVal);
            return sortOrder === "desc" ? -comparison : comparison;
          }

          // Numeric comparison
          if (aVal < bVal) return sortOrder === "desc" ? 1 : -1;
          if (aVal > bVal) return sortOrder === "desc" ? -1 : 1;
          return 0;
        });
      }

      // Pagination
      const totalRecords = filteredZipCodes.length;
      const totalPages = Math.ceil(totalRecords / pageSize);
      const startIndex = (page - 1) * pageSize;
      const paginatedZipCodes = filteredZipCodes.slice(
        startIndex,
        startIndex + pageSize
      );

      // Convert numbers back to strings to match API response format (Zod will transform them back)
      const apiFormattedZipCodes = paginatedZipCodes.map((zip) => ({
        ...zip,
        rate: zip.rate !== null ? String(zip.rate) : null,
        minimumAmt: zip.minimumAmt !== null ? String(zip.minimumAmt) : null,
      }));

      return HttpResponse.json(
        {
          success: true,
          message: "High cost zip codes retrieved successfully",
          errorCode: null,
          item: {
            highCostZips: apiFormattedZipCodes,
            pagination: {
              totalRecords,
              totalPages,
              currentPage: page,
              pageSize,
            },
          },
        },
        { status: 200 }
      );
    }),

    // Get High Cost Zip Code Schedule Accounts
    http.post(
      formatUrl(apiUrls.getHighCostZipCodeScheduleAccounts),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          scheduleId: number;
          page: number;
          pageSize: number;
          search?: string;
          sortBy?: string;
          sortOrder?: string;
        };
        const {
          scheduleId,
          page = 1,
          pageSize = 25,
          search = "",
          sortBy = "",
          sortOrder = "asc",
        } = body;

        const allAccounts =
          mockHighCostZipCodeScheduleAccounts[scheduleId] ?? [];

        let filteredAccounts = [...allAccounts];

        if (search.trim()) {
          const lowerSearch = search.toLowerCase();
          filteredAccounts = filteredAccounts.filter(
            (account) =>
              account.accountName.toLowerCase().includes(lowerSearch) ||
              account.accountNumber.toLowerCase().includes(lowerSearch)
          );
        }

        if (sortBy === "accountName" || sortBy === "accountNumber") {
          filteredAccounts.sort((a, b) => {
            const aVal = a[sortBy as keyof typeof a] as string;
            const bVal = b[sortBy as keyof typeof b] as string;
            const comparison = aVal.localeCompare(bVal);
            return sortOrder === "desc" ? -comparison : comparison;
          });
        }

        const totalRecords = filteredAccounts.length;
        const totalPages = Math.ceil(totalRecords / pageSize) || 1;
        const startIndex = (page - 1) * pageSize;
        const paginatedAccounts = filteredAccounts.slice(
          startIndex,
          startIndex + pageSize
        );

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: {
              accounts: paginatedAccounts,
              pagination: {
                totalRecords,
                totalPages,
                currentPage: page,
                pageSize,
              },
            },
          },
          { status: 200 }
        );
      }
    ),

    http.patch(
      formatUrl(
        apiUrls.updateLaneMinimumScheduleStatus(":id" as unknown as number)
      ),
      async ({ request, params }) => {
        await delay(config.delay);
        const scheduleId = Number(params.id);
        const body = (await request.json()) as { status: string };

        const schedule = mockLaneMinimumSchedules.find(
          (s) => s.id === scheduleId
        );

        if (!schedule) {
          return HttpResponse.json(
            {
              success: false,
              message: "Schedule not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        // Update the schedule status in mock data
        schedule.status = body.status;

        return HttpResponse.json(
          {
            success: true,
            message: "Lane minimum schedule status updated successfully",
            errorCode: null,
            item: {
              id: scheduleId,
              status: body.status,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.get(
      formatUrl(apiUrls.getLaneMinimumScheduleById(":id")),
      async ({ params }) => {
        await delay(config.delay);
        const scheduleId = Number(params.id);

        const schedule = mockLaneMinimumSchedules.find(
          (s) => s.id === scheduleId
        );

        if (!schedule) {
          return HttpResponse.json(
            {
              success: false,
              message: "Schedule not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        // Return mock detail data with updated metadata for the requested schedule
        return HttpResponse.json(
          {
            success: true,
            message: "Schedule retrieved successfully",
            errorCode: null,
            item: {
              ...mockLaneMinimumScheduleDetail,
              id: schedule.id,
              name: schedule.name,
              description: schedule.description,
              status: schedule.status,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.get(formatUrl(apiUrls.getLaneMinimumLocationTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: "",
          errorCode: null,
          item: {
            locationTypes: mockLocationTypes,
          },
        },
        { status: 200 }
      );
    }),

    http.get(
      formatUrl(apiUrls.getLaneMinimumLocationValues),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const locationType = url.searchParams.get("locationType") || "";

        const locationValues =
          locationType in mockLocationValuesByType
            ? mockLocationValuesByType[locationType]
            : [];

        return HttpResponse.json(
          {
            success: true,
            message: "",
            errorCode: null,
            item: {
              locationValues,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.post(
      formatUrl(apiUrls.createLaneMinimumSchedule),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          name: string;
          description: string | null;
          status: string;
          laneMinimums: Array<{
            sequence: number;
            originType: number;
            originLocation: string;
            destinationType: number;
            destinationLocation: string;
            minimumCharge: number;
            direction: string;
          }>;
        };

        // Check if name already exists
        const nameExists = mockLaneMinimumSchedules.some(
          (schedule) => schedule.name.toLowerCase() === body.name.toLowerCase()
        );

        if (nameExists) {
          return HttpResponse.json(
            {
              success: false,
              message: "Lane minimum schedule with this name already exists",
              errorCode: "DUPLICATE_NAME",
              item: null,
            },
            { status: 400 }
          );
        }

        // Generate new ID
        const newId =
          Math.max(...mockLaneMinimumSchedules.map((s) => s.id)) + 1;

        // Create new schedule in list
        const newSchedule = {
          id: newId,
          name: body.name,
          description: body.description,
          status: body.status,
          laneMinimumCount: body.laneMinimums.length,
          createdBy: "Test User",
          createdOn: new Date().toLocaleDateString("en-US"),
          modifiedBy: "Test User",
          modifiedOn: new Date().toLocaleDateString("en-US"),
          isGlobal: false,
        };

        mockLaneMinimumSchedules.push(newSchedule);

        const successMessage =
          body.status === "Draft"
            ? `Lane minimum schedule '${body.name}' saved as draft successfully`
            : `Lane minimum schedule '${body.name}' created successfully`;

        return HttpResponse.json(
          {
            success: true,
            message: successMessage,
            errorCode: null,
            item: {
              id: newId,
              name: body.name,
              description: body.description,
              status: body.status,
              laneMinimumCount: body.laneMinimums.length,
            },
          },
          { status: 201 }
        );
      }
    ),

    http.post(formatUrl(apiUrls.getFuelScaleAccounts), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        code: string;
        page: number;
        pageSize: number;
        search?: string;
      };
      const { page, pageSize, search } = body;

      const mockAccounts = mockFuelScales.commonAccounts;

      // Filter by search if provided
      let filteredAccounts = mockAccounts;
      if (search) {
        const lowerSearch = search.toLowerCase();
        filteredAccounts = mockAccounts.filter(
          (account) =>
            account.accountName.toLowerCase().includes(lowerSearch) ||
            account.accountNumber.toLowerCase().includes(lowerSearch)
        );
      }

      // Apply pagination
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedAccounts = filteredAccounts.slice(startIndex, endIndex);

      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            accounts: paginatedAccounts,
            pagination: {
              totalRecords: filteredAccounts.length,
              totalPages: Math.ceil(filteredAccounts.length / pageSize),
              currentPage: page,
              pageSize,
            },
          },
        },
        { status: 200 }
      );
    }),

    http.post(
      formatUrl(
        apiUrls.getFuelScaleHistory(":fuelScaleId" as unknown as number)
      ),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          page: number;
          pageSize: number;
          search: string;
          sortBy: string;
          sortOrder: string;
        };
        const {
          page = 1,
          pageSize = 10,
          search = "",
          sortBy = "",
          sortOrder = "",
        } = body;

        // Get mock history data (already in correct format)
        let historyData = [...mockFuelScales.commonHistory];

        // Filter by search if provided (search in price or effectiveDate)
        if (search) {
          const lowerSearch = search.toLowerCase();
          historyData = historyData.filter(
            (item) =>
              item.price.includes(lowerSearch) ||
              item.effectiveDate.toLowerCase().includes(lowerSearch)
          );
        }

        // Sort if sortBy is provided
        if (sortBy) {
          historyData.sort((a, b) => {
            const getValue = (item: (typeof historyData)[0]) => {
              switch (sortBy) {
                case "price":
                  return parseFloat(item.price);
                case "effectiveDate":
                  return new Date(item.effectiveDate).getTime();
                default:
                  return 0;
              }
            };
            const aVal = getValue(a);
            const bVal = getValue(b);
            const comparison = aVal - bVal;
            return sortOrder === "desc" ? -comparison : comparison;
          });
        }

        // Paginate
        const totalRecords = historyData.length;
        const startIndex = (page - 1) * pageSize;
        const paginatedHistory = historyData.slice(
          startIndex,
          startIndex + pageSize
        );

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: {
              history: paginatedHistory,
              pagination: {
                totalRecords,
                totalPages: Math.ceil(totalRecords / pageSize),
                currentPage: page,
                pageSize,
              },
            },
          },
          { status: 200 }
        );
      }
    ),

    http.get(
      formatUrl(apiUrls.getFuelScaleById(":fuelScaleId" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const { fuelScaleId } = params;

        // Find the fuel scale in mockFuelScales by id
        const fuelScale = mockFuelScales.fuelScales.find(
          (scale) => scale.id === Number(fuelScaleId)
        );

        if (!fuelScale) {
          return HttpResponse.json(
            {
              success: false,
              message: "Fuel scale not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        // Get detailed data from mockFuelSurchargeCodeDetails using ID
        if (!(fuelScale.id in mockFuelSurchargeCodeDetails)) {
          return HttpResponse.json(
            {
              success: false,
              message: "Fuel scale detail not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        const detailData = mockFuelSurchargeCodeDetails[fuelScale.id];

        return HttpResponse.json(
          {
            success: true,
            message: "Fuel scale details retrieved successfully",
            errorCode: null,
            item: detailData,
          },
          { status: 200 }
        );
      }
    ),

    http.patch(
      formatUrl(
        apiUrls.updateFuelScaleStatus(":fuelScaleId" as unknown as number)
      ),
      async ({ params, request }) => {
        await delay(config.delay);
        const { fuelScaleId } = params;
        const body = (await request.json()) as {
          status: "Active" | "Inactive";
        };

        // Find and update the fuel scale in both mock sources
        const fuelScale = mockFuelScales.fuelScales.find(
          (scale) => scale.id === Number(fuelScaleId)
        );

        if (!fuelScale) {
          return HttpResponse.json(
            {
              success: false,
              message: "Fuel scale not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        // Update status in list
        fuelScale.status = body.status;

        // Update status in detail if exists
        if (fuelScale.id in mockFuelSurchargeCodeDetails) {
          mockFuelSurchargeCodeDetails[fuelScale.id].status = body.status;
        }

        return HttpResponse.json(
          {
            success: true,
            message: `Fuel scale status updated to ${body.status} successfully`,
            errorCode: null,
            item: {
              id: fuelScale.id,
              status: body.status,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.post(formatUrl(apiUrls.createFuelScale), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        code: string;
        longDescription: string;
        status?: string;
        [key: string]: unknown;
      };

      // Check if code already exists
      const codeExists = mockFuelScales.fuelScales.some(
        (scale) => scale.code === body.code
      );

      if (codeExists) {
        return HttpResponse.json(
          {
            success: false,
            message: "Fuel scale code already exists",
            errorCode: "DUPLICATE_CODE",
            item: null,
          },
          { status: 400 }
        );
      }

      // Generate new ID
      const newId = Math.max(...mockFuelScales.fuelScales.map((s) => s.id)) + 1;

      // Add to list
      mockFuelScales.fuelScales.push({
        id: newId,
        code: body.code,
        longDescription: body.longDescription,
        fuelPrice: "NATIONAL",
        fuelPriceValue: "0.00",
        status: body.status || "Active",
      });

      // Add to details
      mockFuelSurchargeCodeDetails[newId] = {
        ...body,
        fuelPrice: "NATIONAL",
        fuelPriceValue: "0.00",
        numberOfGroups: 0,
        numberOfSequences: 0,
        createdDate: new Date().toLocaleDateString(),
      } as FuelScaleDetail;

      return HttpResponse.json(
        {
          success: true,
          message:
            body.status === "Draft"
              ? "Draft saved successfully"
              : "Fuel scale created successfully",
          errorCode: null,
          item: mockFuelSurchargeCodeDetails[newId],
        },
        { status: 201 }
      );
    }),

    http.put(
      formatUrl(apiUrls.updateFuelScale(":fuelScaleId" as unknown as number)),
      async ({ params, request }) => {
        await delay(config.delay);
        const { fuelScaleId } = params;
        const body = (await request.json()) as {
          longDescription: string;
          status?: string;
          [key: string]: unknown;
        };

        const fuelScale = mockFuelScales.fuelScales.find(
          (scale) => scale.id === Number(fuelScaleId)
        );

        if (!fuelScale) {
          return HttpResponse.json(
            {
              success: false,
              message: "Fuel scale not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        // Update list item
        fuelScale.longDescription = body.longDescription;
        fuelScale.status = body.status || fuelScale.status;

        // Update detail entry
        const detailEntry = mockFuelSurchargeCodeDetails[Number(fuelScaleId)];
        Object.assign(detailEntry, body);

        return HttpResponse.json(
          {
            success: true,
            message:
              body.status === "Draft"
                ? "Draft saved successfully"
                : "Fuel scale updated successfully",
            errorCode: null,
            item: detailEntry,
          },
          { status: 200 }
        );
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getFuelScaleViewCriteria(":criteriaId" as unknown as number)
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { criteriaId } = params;
        const criteriaIdNum = Number(criteriaId);

        // Get criteria from mock data
        const mockCriteria =
          criteriaIdNum in mockFuelScaleCriteria
            ? mockFuelScaleCriteria[criteriaIdNum]
            : undefined;

        if (!mockCriteria) {
          return HttpResponse.json(
            {
              success: false,
              message: "Criteria not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        return HttpResponse.json(
          {
            success: true,
            message: "Criteria retrieved successfully",
            errorCode: null,
            item: mockCriteria,
          },
          { status: 200 }
        );
      }
    ),
    // GET /secured/api/v1/fuel-price - Fetch fuel price data
    http.get(formatUrl(apiUrls.getFuelPrice), async () => {
      await delay(config.delay);

      return HttpResponse.json(mockFuelPriceApiResponse, { status: 200 });
    }),

    // POST /secured/api/v1/fuel-price/history - Fetch fuel price history with pagination and sorting
    http.post(formatUrl(apiUrls.getFuelPriceHistory), async ({ request }) => {
      await delay(config.delay);

      const body = (await request.json()) as {
        fuelPriceId: number;
        page: number;
        pageSize: number;
        sortBy: "price" | "effectiveDate" | "modifiedOn" | "modifiedBy";
        sortOrder: "asc" | "desc";
        search?: string;
      };

      const { fuelPriceId, page, pageSize, sortBy, sortOrder, search } = body;

      // Get the mock data for this fuel price ID
      const mockResponse = mockFuelPriceHistoryResponses[fuelPriceId];
      // Get the history data and filter by search query if provided
      let filteredHistory = [...mockResponse.item.history];
      if (search) {
        const searchLower = search.toLowerCase();
        filteredHistory = filteredHistory.filter(
          (item) =>
            item.price.toLowerCase().includes(searchLower) ||
            item.effectiveDate.toLowerCase().includes(searchLower) ||
            item.modifiedOn.toLowerCase().includes(searchLower) ||
            item.modifiedBy.toLowerCase().includes(searchLower)
        );
      }

      // Sort the history data
      const sortedHistory = filteredHistory.sort((a, b) => {
        let comparison = 0;

        if (sortBy === "price") {
          const priceA = parseFloat(a.price);
          const priceB = parseFloat(b.price);
          comparison = priceA - priceB;
        } else if (sortBy === "effectiveDate") {
          const dateA = new Date(a.effectiveDate);
          const dateB = new Date(b.effectiveDate);
          comparison = dateA.getTime() - dateB.getTime();
        } else if (sortBy === "modifiedOn") {
          const dateA = new Date(a.modifiedOn);
          const dateB = new Date(b.modifiedOn);
          comparison = dateA.getTime() - dateB.getTime();
        } else {
          comparison = a.modifiedBy.localeCompare(b.modifiedBy);
        }

        return sortOrder === "desc" ? -comparison : comparison;
      });

      // Paginate the results
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedHistory = sortedHistory.slice(startIndex, endIndex);

      const totalRecords = sortedHistory.length;
      const totalPages = Math.ceil(totalRecords / pageSize);

      return HttpResponse.json(
        {
          success: true,
          message: "Fuel price history retrieved successfully",
          errorCode: null,
          item: {
            history: paginatedHistory,
            pagination: {
              currentPage: page,
              totalPages,
              totalRecords,
              pageSize,
            },
          },
        },
        { status: 200 }
      );
    }),

    http.patch(
      formatUrl(apiUrls.updateFuelPrice(":fuelPriceId" as unknown as number)),
      async ({ request, params }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          price: number;
          effectiveDate: string;
        };
        const { fuelPriceId } = params;

        // Mock successful update response
        return HttpResponse.json(
          {
            success: true,
            message: "Fuel price updated successfully",
            errorCode: null,
            item: {
              id: Number(fuelPriceId),
              price: body.price,
              effectiveDate: body.effectiveDate,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.post(
      formatUrl(apiUrls.retryFuelPrice(":fuelPriceId" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const { fuelPriceId } = params;

        // Mock successful retry response - simulate fetching fresh fuel price data
        return HttpResponse.json(
          {
            success: true,
            message: "Fuel price fetched successfully",
            errorCode: null,
            item: {
              id: Number(fuelPriceId),
              scope: "PADD5",
              currentPrice: 4.25,
              priceChange: 0.05,
              percentageChange: 1.19,
              lastUpdated: new Date().toLocaleDateString(),
              hasError: false,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.post(
      formatUrl(
        apiUrls.createRoute(
          ":unixTime" as unknown as number,
          ":sicId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: {
              statusCounts: mockShipmentsStatusCounts,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.get(formatUrl(apiUrls.loadBoard.transitDashboardSummary), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockTransitDashboardSummary.default);
    }),

    http.get(
      formatUrl(apiUrls.loadBoard.transitDashboardShipments(":tripNumber")),
      async () => {
        await delay(config.delay);

        // Return mock data - in real scenario, you could filter by tripNumber
        return HttpResponse.json(
          mockTransitDashboardShipments.default.shipments
        );
      }
    ),

    http.get(
      formatUrl(
        apiUrls.loadBoard.transitDashboardTripDetails(
          ":tripNumber" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const body =
          params.tripNumber === "10016841"
            ? mockTransitDashboardTripDetailsWithLoadPair
            : mockTransitDashboardTripDetails.default;
        return HttpResponse.json(body);
      }
    ),

    // Trip Notes
    http.get(
      formatUrl(
        apiUrls.loadBoard.getTripNotes(":tripNumber" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockTripNotes.default);
      }
    ),

    // Trip History (status history)
    http.get(
      formatUrl(
        apiUrls.loadBoard.getTripHistory(":tripNumber" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockTripHistory);
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.loadBoard.deleteTripHistoryStatus(
          ":tripNumber" as unknown as number,
          ":statusId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.loadBoard.createTripNote(":tripNumber" as unknown as number)
      ),
      async ({ request, params }) => {
        await delay(config.delay);

        const requestSchema = z.object({
          content: z.string(),
        });

        const requestBody = requestSchema.parse(await request.json());
        const newAutoIncNoteId = getNextAutoIncValue(
          mockTripNotes.default.map((item) => item.id)
        );

        const tripNumber = params.tripNumber as unknown as string;
        const scheduleId = Number(tripNumber);
        const creatorId = crypto.randomUUID();

        const currentDate = dayjs();
        const newNote: TripNote = {
          id: newAutoIncNoteId,
          content: requestBody.content,
          createdBy: "Test User",
          createdOn: currentDate,
          modifiedBy: "Test User",
          modifiedOn: currentDate,
          isEdited: false,
          scheduleId,
          creatorId,
        };

        return HttpResponse.json(newNote, { status: 201 });
      }
    ),
    http.get(formatUrl(apiUrls.loadBoard.transitRecentUpdates), async () => {
      await delay(config.delay);
      return HttpResponse.json({ trips: mockRecentUpdates.default });
    }),

    http.get(
      formatUrl(apiUrls.loadBoard.transitDashboardNotifications(100)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockTripDashboardNotifications, {
          status: 200,
        });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.updateTripNote(":noteId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.loadBoard.deleteTripNote(":noteId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    // Loadboard v2 Shipment Notes
    http.get(
      formatUrl(
        apiUrls.loadBoard.getShipmentNotes(":shipmentId" as unknown as number)
      ),
      async ({ params }) => {
        await delay(config.delay);
        const shipmentId = String(params.shipmentId);
        const notes = mockShipmentNotes[shipmentId] ?? [];
        return HttpResponse.json(notes);
      }
    ),

    http.post(
      formatUrl(
        apiUrls.loadBoard.createShipmentNote(":shipmentId" as unknown as number)
      ),
      async ({ request, params }) => {
        await delay(config.delay);

        const requestSchema = z.object({
          content: z.string(),
        });

        const requestBody = requestSchema.parse(await request.json());
        const shipmentId = String(params.shipmentId);
        const shipmentIdNum = Number(params.shipmentId);

        let existingNotes = mockShipmentNotes[shipmentId];
        if (!existingNotes) {
          existingNotes = [];
          mockShipmentNotes[shipmentId] = existingNotes;
        }

        const newAutoIncNoteId = getNextAutoIncValue(
          existingNotes.map((item) => item.id)
        );

        const creatorId = crypto.randomUUID();
        const currentDate = new Date();

        const newNote = {
          id: newAutoIncNoteId,
          shipmentId: shipmentIdNum,
          content: requestBody.content,
          createdBy: "Test User",
          createdOn: currentDate,
          modifiedBy: "Test User",
          modifiedOn: currentDate,
          isEdited: false,
          creatorId,
        };

        existingNotes.push(newNote);

        return HttpResponse.json(newNote, { status: 201 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.updateShipmentNote(
          ":shipmentId" as unknown as number,
          ":noteId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.loadBoard.deleteShipmentNote(
          ":shipmentId" as unknown as number,
          ":noteId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.tripCBOLInfo(":tripNumber" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json();
      }
    ),

    http.get(
      formatUrl(apiUrls.loadBoard.getTripsByCarrierInBulkRequest("*")),
      async ({ request }) => {
        const url = new URL(request.url);
        const carrierCode = url.searchParams.get("CarrierCode");

        if (!carrierCode) {
          await delay(config.delay);
          return HttpResponse.text("CarrierCode parameter is required", {
            status: 400,
          });
        }

        await delay(config.delay);

        // Return trips for the specified carrier, or empty if carrier not found
        const tripsData =
          carrierCode in mockBulkRequestTripsByCarrier
            ? mockBulkRequestTripsByCarrier[
                carrierCode as keyof typeof mockBulkRequestTripsByCarrier
              ]
            : { totalCount: 0, trips: [] };

        return HttpResponse.json(tripsData);
      }
    ),

    http.post(
      formatUrl(apiUrls.loadBoard.sendBulkRequest),
      async ({ request }) => {
        await delay(config.delay);

        const body = (await request.json()) as {
          tripNumbers?: number[];
        };

        if (!body.tripNumbers || !Array.isArray(body.tripNumbers)) {
          return HttpResponse.json(
            { error: "tripNumbers array is required" },
            { status: 400 }
          );
        }

        if (body.tripNumbers.length === 0) {
          return HttpResponse.json(
            { error: "At least one trip number is required" },
            { status: 400 }
          );
        }

        // Simulate successful bulk request
        return new HttpResponse(null, { status: 204 });
      }
    ),

    // * Biometrics (DockAuto) — employees + independent contractors
    http.get(
      formatUrl(apiUrls.getBiometricUserById(":id")),
      async ({ params }) => {
        const { id } = params;
        const user = mockBiometricUsers.users.find((u) => u.id === id);
        if (!user) return new HttpResponse(null, { status: 404 });
        await delay(config.delay);
        return HttpResponse.json(user);
      }
    ),
    http.get(formatUrl(apiUrls.getBiometricUsers()), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const pageIndex = Number(url.searchParams.get("pageIndex") ?? 0);
      const pageSize = Number(url.searchParams.get("pageSize") ?? 1000);
      const start = pageIndex * pageSize;
      const paged = mockBiometricUsers.users.slice(start, start + pageSize);
      return HttpResponse.json({
        totalUserCount: mockBiometricUsers.users.length,
        users: paged,
      });
    }),
    http.put<
      object,
      {
        loginId: string;
        roleType?: string;
        imageBase64?: string;
        imageExtension?: string;
      }
    >(formatUrl(apiUrls.resetFaceId), async ({ request }) => {
      await delay(config.delay);
      const body = await request.json();
      const user = mockBiometricUsers.users.find((u) => u.id === body.loginId);
      if (!user) {
        return new HttpResponse(null, { status: 404 });
      }
      if (body.imageBase64 && body.imageExtension) {
        // Simulate uploading a new face image
        user.pictureUrl = `https://mock-bucket.s3.amazonaws.com/face-images/${body.loginId}.${body.imageExtension}`;
        user.pictureLastModified = new Date().toISOString();
      } else {
        // Simulate removing the face image
        user.pictureUrl = null;
        user.pictureLastModified = null;
      }
      return new HttpResponse(null, { status: 200 });
    }),

    http.post<object, OsadAssignment>(
      formatUrl(apiUrls.common.createOSAD(":sicId" as unknown as number)),
      async ({ request }) => {
        await delay(config.delay);
        const body = await request.json();
        if (!body.reasonId) {
          return HttpResponse.json(
            { error: "Reason is required" },
            { status: 400 }
          );
        }

        if (body.shipmentIds.length === 0) {
          return HttpResponse.json(
            { error: "At least one shipment is required" },
            { status: 400 }
          );
        }

        return new HttpResponse(null, { status: 204 });
      }
    ),

    http.put<object, OsadAssignment>(
      formatUrl(apiUrls.common.createOSAD(":sicId" as unknown as number)),
      async () => {
        await delay(config.delay);

        return new HttpResponse(null, { status: 204 });
      }
    ),

    http.get(formatUrl(apiUrls.common.getReasonCodes), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockReasonCodes.default);
    }),

    http.get(
      formatUrl("operations/common/trip/get-by-sic/:sicId"),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockTripList.default);
      }
    ),

    http.get(
      formatUrl(
        "operations/common/trip/:sicId/search-by-booking-number/:bookingNumber"
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockBookingSearch.withAvailable);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getTripShipments(
          ":sicId" as unknown as number,
          ":tripNumber" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockTripShipments.default);
      }
    ),

    http.get(
      formatUrl(
        "operations/common/trip/:sicId/search-by-booking-number/:bookingNumber"
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockBookingSearch.withAvailable);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getTripShipments(
          ":sicId" as unknown as number,
          ":tripNumber" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockTripShipments.default);
      }
    ),

    http.get(
      formatUrl(
        "operations/common/trip/:sicId/search-by-booking-number/:bookingNumber"
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockBookingSearch.withAvailable);
      }
    ),

    http.get(
      formatUrl("operations/common/trip/:sicId/get-by-trip-number/:tripNumber"),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockTripByNumber.default);
      }
    ),

    http.put(
      formatUrl("operations/common/trip/:sicId/check-in/:tripNumber"),
      async () => {
        await delay(config.delay);
        if (config.scenario === "check-in-conflict") {
          return HttpResponse.json(
            { message: "Trip already checked in" },
            { status: 409 }
          );
        }
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    http.put(
      formatUrl("operations/common/green/trip/:sicId/check-in/:tripNumber"),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    http.get(formatUrl(apiUrls.getRegionsWithSicsV2), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockRegions.default);
    }),

    http.get(formatUrl(apiUrls.getRegionsCount), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          loadboardRegionCount: mockRegions.default.length,
          ucOfdRegionCount: mockRegions.default.length,
        },
        { status: 200 }
      );
    }),

    http.get(
      formatUrl(apiUrls.getRegionById(":regionId" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const { regionId } = params;
        const region = mockRegions.default.find(
          (r) => r.id === Number(regionId)
        );

        if (!region) {
          return HttpResponse.json(
            { error: "Region not found" },
            { status: 404 }
          );
        }

        return HttpResponse.json(region);
      }
    ),

    http.post(formatUrl(apiUrls.createRegionV2), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as { name: string };
      return HttpResponse.json(
        { id: 1, name: body.name, type: "Loadboard", serviceCenters: [] },
        { status: 201 }
      );
    }),

    http.put(
      formatUrl(apiUrls.updateRegionV2(":regionId" as unknown as number)),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          id: number;
          name: string;
          serviceCenters: { id: number; code: string }[];
        };
        return HttpResponse.json({
          id: body.id,
          name: body.name,
          type: "Loadboard",
          serviceCenters: body.serviceCenters,
        });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.unassignSicV2(":regionId" as unknown as number, ":sicCode")
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    http.patch(
      formatUrl(apiUrls.updateRegionName(":regionId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    http.delete(
      formatUrl(apiUrls.deleteRegionV2(":regionId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // UC/CC OFD Dashboard Summary endpoint
    http.get(
      formatUrl(apiUrls.ucccOfd.ucccOfdDashboardSummary(":date")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockUcccOfdDashboardSummary.default, {
          status: 200,
        });
      }
    ),

    // UC/CC OFD filter distinct-values (`filterName=owner` returns rows; others return string[])
    http.get(
      formatUrl(
        stripQuery(
          apiUrls.ucccOfd.ucccOfdFilterDistinctValues(
            ":date" as unknown as string
          )
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const filterName = url.searchParams.get("filterName");

        if (filterName === "owner") {
          return HttpResponse.json(
            [
              {
                ownerId: "bbf600c9-07ac-4ca5-8c35-60724ca9cab8",
                ownerName: "John Doe",
              },
              {
                ownerId: "e553a476-dcb6-4cc3-857d-683ea32f7f74",
                ownerName: "Jane Smith",
              },
            ],
            { status: 200 }
          );
        }

        return HttpResponse.json([], { status: 200 });
      }
    ),

    // UC/CC OFD Shipments list endpoint - returns filtered grouped payload
    http.get(
      formatUrl(apiUrls.ucccOfd.ucccOfdShipments(":date")),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);

        // Read filter params from URL
        const originCodes = url.searchParams.getAll("originCodes");
        const destinationCodes = url.searchParams.getAll("destinationCodes");
        const transferPoints = url.searchParams.getAll("transferPoints");
        const shipmentTagNames = url.searchParams.getAll("shipmentTagNames");
        const statusNames = url.searchParams.getAll("statusNames");
        const followUpStatus = url.searchParams.getAll("followUpStatus");
        const etaStatus = url.searchParams.getAll("etaStatus");
        const searchTerm = url.searchParams.get("searchTerm");

        // Filter function for shipments
        const filterShipment = (shipment: UcccOfdShipmentRow): boolean => {
          // Search term filter
          if (searchTerm) {
            const term = searchTerm.toLowerCase();
            const matchesSearch =
              shipment.proNumber.toLowerCase().includes(term) ||
              (shipment.tripNumber?.toString().includes(term) ?? false);
            if (!matchesSearch) return false;
          }

          // Origin filter
          if (
            originCodes.length > 0 &&
            !originCodes.includes(shipment.originSic.code)
          ) {
            return false;
          }

          // Destination filter
          if (
            destinationCodes.length > 0 &&
            !destinationCodes.includes(shipment.destinationSic.code)
          ) {
            return false;
          }

          // Transfer point filter (using currentSic as transfer point)
          if (
            transferPoints.length > 0 &&
            (!shipment.currentSic ||
              !transferPoints.includes(shipment.currentSic.code))
          ) {
            return false;
          }

          // Shipment tags filter (OR logic within filter)
          if (
            shipmentTagNames.length > 0 &&
            !shipment.shipmentTags.some((tag) => shipmentTagNames.includes(tag))
          ) {
            return false;
          }

          // Status filter
          if (
            statusNames.length > 0 &&
            !statusNames.includes(shipment.status)
          ) {
            return false;
          }

          // FB Follow Up Status filter
          if (
            followUpStatus.length > 0 &&
            (!shipment.fbFollowUpStatus ||
              !followUpStatus.includes(shipment.fbFollowUpStatus))
          ) {
            return false;
          }

          // ETA Status (On-Time Status) filter
          if (
            etaStatus.length > 0 &&
            (!shipment.etaStatus ||
              !etaStatus.some(
                (status) =>
                  status.toLowerCase() === shipment.etaStatus?.toLowerCase()
              ))
          ) {
            return false;
          }

          return true;
        };

        // Apply filters to mock data
        const filteredUnassignedShipments =
          mockUcccOfdShipments.default.unassignedShipments.filter(
            filterShipment
          );
        const filteredOwnerGroups = mockUcccOfdShipments.default.ownerGroups
          .map((group) => {
            const filteredShipments = group.shipments.filter(filterShipment);
            return {
              ...group,
              shipments: filteredShipments,
              shipmentCount: filteredShipments.length,
            };
          })
          .filter((group) => group.shipmentCount > 0);

        const filteredData = {
          unassignedShipmentCount: filteredUnassignedShipments.length,
          unassignedShipments: filteredUnassignedShipments,
          ownerGroups: filteredOwnerGroups,
        };

        const apiPayload = serializeUcccOfdShipmentsListForApi(filteredData);
        ucccOfdShipmentsSchema.listResponse.parse(apiPayload);
        return HttpResponse.json(apiPayload, { status: 200 });
      }
    ),

    http.get(formatUrl(apiUrls.ucccOfd.getOwners()), async () => {
      await delay(config.delay);
      const { suggested, available } =
        mockUcccOfdEditOwnerDialogOptions.default;
      const mapRows = (rows: { id: string; name: string }[]) =>
        rows
          .filter((o) => o.name.trim().toLowerCase() !== "unassign")
          .map((o) => {
            const parts = o.name.trim().split(/\s+/);
            return {
              id: o.id,
              firstName: parts[0] ?? "",
              lastName: parts.length > 1 ? parts.slice(1).join(" ") : "",
              fullName: o.name,
            };
          });
      return HttpResponse.json(
        {
          suggestedOwners: mapRows(suggested),
          availableOwners: mapRows(available),
        },
        { status: 200 }
      );
    }),

    http.post(
      formatUrl(apiUrls.ucccOfd.assignOwnerToShipments),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          shipmentIds: number[];
          ownerId?: string;
          isAssign: boolean;
        };
        if (!Array.isArray(body.shipmentIds) || body.shipmentIds.length === 0) {
          return HttpResponse.json(
            { message: "shipmentIds required" },
            { status: 400 }
          );
        }
        if (body.isAssign && !body.ownerId) {
          return HttpResponse.json(
            { message: "ownerId required when isAssign is true" },
            { status: 400 }
          );
        }
        return new HttpResponse(null, { status: 204 });
      }
    ),

    // Path-only match so any ServiceStartDate / ServiceEndDate / IncludeMonthlySummary query is accepted.
    http.get(
      formatUrl(
        stripQuery(apiUrls.ucccOfd.serviceFailureRecapReport("", "", false))
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockServiceFailureRecapReport, {
          status: 200,
        });
      }
    ),

    // Path-only match — accepts any documentType / ServiceStartDate / ServiceEndDate query.
    http.get(
      formatUrl(
        stripQuery(
          apiUrls.ucccOfd.serviceFailureRecapDownloadReport(
            "",
            "",
            false,
            "pdf",
            ""
          )
        )
      ),
      async () => {
        await delay(config.delay);
        return new HttpResponse(new Blob(["mock report content"]), {
          status: 200,
          headers: { "Content-Type": "application/octet-stream" },
        });
      }
    ),

    http.get(
      formatUrl(apiUrls.ucccOfdServiceFailure.getServiceFailureReasonCodes),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(
          { failureReasons: mockServiceFailureReasonCodes },
          { status: 200 }
        );
      }
    ),

    http.post(
      formatUrl(
        apiUrls.ucccOfdServiceFailure.updateServiceFailure(
          ":shipmentId" as unknown as number
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          failureReasonCode: string;
          subReason: string;
          explanation: string;
        };

        if (!body.failureReasonCode || !body.subReason || !body.explanation) {
          return HttpResponse.json(
            { error: "Missing required fields" },
            { status: 400 }
          );
        }

        return new HttpResponse(null, { status: 204 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.ucccOfdServiceFailure.updateServiceFailure(
          ":shipmentId" as unknown as number
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          failureReasonCode: string;
          subReason: string;
          explanation: string;
        };

        if (!body.failureReasonCode || !body.subReason || !body.explanation) {
          return HttpResponse.json(
            { error: "Missing required fields" },
            { status: 400 }
          );
        }

        return new HttpResponse(null, { status: 204 });
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.ucccOfdServiceFailure.deleteServiceFailure(
          ":shipmentId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return new HttpResponse(null, { status: 204 });
      }
    ),

    http.post(
      formatUrl(apiUrls.ucccOfd.expedite(":shipmentId")),
      async ({ request }) => {
        await delay(config.delay);
        await request.json();
        return new HttpResponse(null, { status: 204 });
      }
    ),

    // Fuel Surcharges List
    http.post(formatUrl(apiUrls.getFuelSurchargesList), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        page: number;
        pageSize: number;
        date?: string;
        sortBy?: string;
        sortOrder?: "asc" | "desc";
      };

      const { page = 1, pageSize = 10 } = body;

      // Calculate pagination
      const totalRecords = mockFuelSurcharges.length;
      const totalPages = Math.ceil(totalRecords / pageSize);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = mockFuelSurcharges.slice(startIndex, endIndex);

      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            items: paginatedData,
            pagination: {
              currentPage: page,
              totalPages,
              totalRecords,
              pageSize,
            },
          },
        },
        { status: 200 }
      );
    }),

    // EOL Service Centers List (v1)
    http.post(
      formatUrl(apiUrls.getEolServiceCentersList),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          page: number;
          pageSize: number;
          searchKey?: string;
          filter?: string;
          sortBy?: string;
          sortOrder?: string;
        };

        const {
          page = 1,
          pageSize = 25,
          searchKey = "",
          filter = "All",
        } = body;

        let filteredData = [...mockEolServiceCenters];

        // Apply filter
        if (filter !== "All") {
          filteredData = filteredData.filter((center) => {
            if (filter === "Active") return center.status === "Active";
            if (filter === "Inactive") return center.status === "Inactive";
            if (filter === "DYLT-operated") {
              return center.operationType === "DYLT-operated";
            }
            if (filter === "Vendor-operated") {
              return center.operationType === "Vendor-operated";
            }
            return true;
          });
        }

        // Apply search
        if (searchKey.trim()) {
          const query = searchKey.toLowerCase().trim();
          filteredData = filteredData.filter(
            (center) =>
              center.scCode.toLowerCase().includes(query) ||
              center.name.toLowerCase().includes(query) ||
              center.operationType.toLowerCase().includes(query) ||
              center.vendorScac.toLowerCase().includes(query)
          );
        }

        // Calculate pagination
        const totalRecords = filteredData.length;
        const totalPages = Math.ceil(totalRecords / pageSize);
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        // Transform to backend format
        const serviceCenters = paginatedData.map((center) => ({
          id: parseInt(center.id, 10),
          scCode: center.scCode,
          serviceCenterName: center.name,
          operationType: center.operationType,
          vendorScac:
            center.vendorScac === DASH_PLACEHOLDER ? null : center.vendorScac,
          status: center.status,
          lhJunction: center.lhJunction,
        }));

        return HttpResponse.json(
          {
            success: true,
            message: `Retrieved ${totalRecords} service centers (page ${page} of ${totalPages})`,
            errorCode: null,
            item: {
              serviceCenters,
              pagination: {
                currentPage: page,
                pageSize,
                totalRecords,
                totalPages,
              },
            },
          },
          { status: 200 }
        );
      }
    ),

    // EOL Service Centers List (v2)
    http.post(
      formatUrl(apiUrlsV2.getEolServiceCentersList),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          page: number;
          pageSize: number;
          searchKey?: string;
          filter?: string;
          sortBy?: string;
          sortOrder?: string;
        };

        const {
          page = 1,
          pageSize = 25,
          searchKey = "",
          filter = "All",
        } = body;

        let filteredData = [...mockEolServiceCenters];

        // Apply filter
        if (filter !== "All") {
          filteredData = filteredData.filter((center) => {
            if (filter === "Active") return center.status === "Active";
            if (filter === "Inactive") return center.status === "Inactive";
            if (filter === "DYLT-operated") {
              return center.operationType === "DYLT-operated";
            }
            if (filter === "Vendor-operated") {
              return center.operationType === "Vendor-operated";
            }
            return true;
          });
        }

        // Apply search
        if (searchKey.trim()) {
          const query = searchKey.toLowerCase().trim();
          filteredData = filteredData.filter(
            (center) =>
              center.scCode.toLowerCase().includes(query) ||
              center.name.toLowerCase().includes(query) ||
              center.operationType.toLowerCase().includes(query) ||
              center.vendorScac.toLowerCase().includes(query)
          );
        }

        // Calculate pagination
        const totalRecords = filteredData.length;
        const totalPages = Math.ceil(totalRecords / pageSize);
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        // Transform to backend format
        const serviceCenters = paginatedData.map((center) => ({
          id: parseInt(center.id, 10),
          scCode: center.scCode,
          serviceCenterName: center.name,
          operationType: center.operationType,
          vendorScac:
            center.vendorScac === DASH_PLACEHOLDER ? null : center.vendorScac,
          status: center.status,
          lhJunction: center.lhJunction,
        }));

        return HttpResponse.json(
          {
            success: true,
            message: `Retrieved ${totalRecords} service centers (page ${page} of ${totalPages})`,
            errorCode: null,
            item: {
              serviceCenters,
              pagination: {
                currentPage: page,
                totalPages,
                totalRecords,
                pageSize,
              },
            },
          },
          { status: 200 }
        );
      }
    ),

    // Back Office - Validate ZIP Code Serviceability
    http.get(formatUrl(apiUrls.zipServiceability), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const zip = url.searchParams.get("zip") || "";

      const isServiceable = isZipServiceable(zip);

      return HttpResponse.json(
        {
          success: true,
          message: isServiceable
            ? "ZIP code is serviceable within Daylight Network"
            : "ZIP code is outside Daylight Network",
          errorCode: null,
          item: {
            isServiceable,
          },
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrlsV2.zipServiceability), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const zip = url.searchParams.get("zip") || "";

      const isServiceable = isZipServiceable(zip);

      return HttpResponse.json(
        {
          success: true,
          message: isServiceable
            ? "ZIP code is serviceable within Daylight Network"
            : "ZIP code is outside Daylight Network",
          errorCode: null,
          item: {
            isServiceable,
          },
        },
        { status: 200 }
      );
    }),

    // Back Office - Validate Pickup for Duplicates
    http.post(formatUrl(apiUrls.validatePickup), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as ValidatePickupPayload;

      const duplicatePickup = mockDuplicatePickups[body.shipperId];

      if (duplicatePickup !== undefined) {
        return HttpResponse.json(
          {
            success: true,
            message: `Duplicate pickup found: ${duplicatePickup.pickupNumber}`,
            errorCode: null,
            item: {
              isDuplicate: true,
              duplicatePickup,
            },
          },
          { status: 200 }
        );
      }

      return HttpResponse.json(
        {
          success: true,
          message: "No duplicate pickup found",
          errorCode: null,
          item: {
            isDuplicate: false,
            duplicatePickup: null,
          },
        },
        { status: 200 }
      );
    }),

    // Back Office - Get Blind Shipment Email Details
    http.get(
      formatUrl(
        apiUrls.getBlindShipmentEmailDetails(mockBlindShipmentPickupId)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(
          {
            success: true,
            message: "Blind shipment email details retrieved successfully",
            errorCode: null,
            item: {
              email: mockBlindShipmentFormDefaultRecipients[0],
              subject: mockBlindShipmentFormDefaultSubject,
              body: buildMockBlindShipmentFormDefaultBody(
                mockBlindShipmentEmailDetailsCustomerDisplayName
              ),
            },
          },
          { status: 200 }
        );
      }
    ),

    // EOL Service Centers - List
    http.post(
      formatUrl(apiUrlsV2.getEolServiceCentersList),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          page: number;
          pageSize: number;
          searchKey?: string;
          filter?: string;
          sortBy?: string;
          sortOrder?: string;
        };

        const {
          page = 1,
          pageSize = 25,
          searchKey = "",
          filter = "All",
        } = body;

        let filteredData = [...mockEolServiceCenters];

        // Apply filter
        if (filter !== "All") {
          filteredData = filteredData.filter((center) => {
            if (filter === "Active") return center.status === "Active";
            if (filter === "Inactive") return center.status === "Inactive";
            if (filter === "DYLT-operated") {
              return center.operationType === "DYLT-operated";
            }
            if (filter === "Vendor-operated") {
              return center.operationType === "Vendor-operated";
            }
            return true;
          });
        }

        // Apply search
        if (searchKey.trim()) {
          const query = searchKey.toLowerCase().trim();
          filteredData = filteredData.filter(
            (center) =>
              center.scCode.toLowerCase().includes(query) ||
              center.name.toLowerCase().includes(query) ||
              center.operationType.toLowerCase().includes(query) ||
              center.vendorScac.toLowerCase().includes(query)
          );
        }

        // Calculate pagination
        const totalRecords = filteredData.length;
        const totalPages = Math.ceil(totalRecords / pageSize);
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        // Transform to backend format (matches backend response structure)
        const serviceCenters = paginatedData.map((center) => ({
          id: parseInt(center.id, 10),
          scCode: center.scCode,
          serviceCenterName: center.name,
          operationType: center.operationType,
          vendorScac: center.vendorScac === "-" ? null : center.vendorScac,
          status: center.status === "Active" ? "Active" : "Inactive",
          lhJunction: center.lhJunction,
        }));

        return HttpResponse.json(
          {
            success: true,
            message: `Retrieved ${totalRecords} service centers (page ${page} of ${totalPages})`,
            errorCode: null,
            item: {
              serviceCenters,
              pagination: {
                currentPage: page,
                totalPages,
                totalRecords,
                pageSize,
              },
            },
          },
          { status: 200 }
        );
      }
    ),

    // EOL Service Centers - Create
    http.post(
      formatUrl(apiUrlsV2.createEolServiceCenter),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          scCode: string;
          serviceCenterName: string;
          operationType: string;
          vendorScac?: string | null;
          status: "Active" | "Inactive";
          lhJunction: boolean;
          country: string;
          address: string;
          suite?: string;
          city: string;
          state: string;
          zip: string;
          latitude: number;
          longitude: number;
          timezone: string;
          addressEffectiveDate: string;
          addressExpiryDate: string | null;
        };

        // Validate country (2-letter ISO codes)
        const validCountries = ["US", "CA", "MX"];
        if (!validCountries.includes(body.country)) {
          return HttpResponse.json(
            {
              success: false,
              message: "Country must be one of: US, CA, MX",
              errorCode: "INVALID_COUNTRY",
              item: [],
            },
            { status: 400 }
          );
        }

        // Check for duplicate SC Code
        const duplicate = mockEolServiceCenters.find(
          (sc) => sc.scCode.toUpperCase() === body.scCode.toUpperCase()
        );
        if (duplicate) {
          return HttpResponse.json(
            {
              success: false,
              message: "Service center code must be unique",
              errorCode: "DUPLICATE_SC_CODE",
              item: null,
            },
            { status: 400 }
          );
        }

        // Create new service center ID
        const newId =
          Math.max(...mockEolServiceCenters.map((sc) => parseInt(sc.id, 10))) +
          1;

        // Add to mock data
        const newServiceCenter = {
          id: String(newId),
          scCode: body.scCode.toUpperCase(),
          name: body.serviceCenterName,
          operationType: body.operationType,
          vendorScac: body.vendorScac || DASH_PLACEHOLDER,
          status: body.status,
          lhJunction: body.lhJunction,
          latitude: body.latitude,
          longitude: body.longitude,
          timezone: body.timezone,
        };

        mockEolServiceCenters.push(newServiceCenter);

        return HttpResponse.json(
          {
            success: true,
            message: `Service center ${body.scCode} created successfully`,
            errorCode: null,
            item: {
              id: newId,
              scCode: body.scCode.toUpperCase(),
              serviceCenterName: body.serviceCenterName,
              operationType: body.operationType,
              vendorScac: body.vendorScac || null,
              status: body.status,
              lhJunction: body.lhJunction,
            },
          },
          { status: 201 }
        );
      }
    ),

    // EOL Service Centers - Get Carriers (MUST be before :scCode handler)
    http.get(
      formatUrl(apiUrls.getEolServiceCenterCarriers),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const search = url.searchParams.get("search")?.toLowerCase() || "";

        const allCarriers = mockVendorScacOptions.map((vendor, index) => ({
          id: 95016 + index,
          name: vendor.carrierName,
          refCarrierCode: vendor.code,
          refScacId: vendor.vendorScac,
        }));

        const filteredCarriers = search
          ? allCarriers.filter(
              (carrier) =>
                carrier.refCarrierCode.toLowerCase().startsWith(search) ||
                carrier.refScacId.toLowerCase().startsWith(search) ||
                carrier.name.toLowerCase().startsWith(search)
            )
          : allCarriers;

        return HttpResponse.json(
          {
            success: true,
            message: `Retrieved ${filteredCarriers.length} active carrier${filteredCarriers.length !== 1 ? "s" : ""}`,
            errorCode: null,
            item: {
              carriers: filteredCarriers,
            },
          },
          { status: 200 }
        );
      }
    ),

    // EOL Service Centers - Get Carriers (v2) (MUST be before :scCode handler)
    http.get(
      formatUrl(apiUrlsV2.getEolServiceCenterCarriers),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const search = url.searchParams.get("search")?.toLowerCase() || "";

        const allCarriers = mockVendorScacOptions.map((vendor, index) => ({
          id: 95016 + index,
          name: vendor.carrierName,
          refCarrierCode: vendor.code,
          refScacId: vendor.vendorScac,
        }));

        const filteredCarriers = search
          ? allCarriers.filter(
              (carrier) =>
                carrier.refCarrierCode.toLowerCase().startsWith(search) ||
                carrier.refScacId.toLowerCase().startsWith(search) ||
                carrier.name.toLowerCase().startsWith(search)
            )
          : allCarriers;

        return HttpResponse.json(
          {
            success: true,
            message: `Retrieved ${filteredCarriers.length} active carrier${filteredCarriers.length !== 1 ? "s" : ""}`,
            errorCode: null,
            item: {
              carriers: filteredCarriers,
            },
          },
          { status: 200 }
        );
      }
    ),

    // EOL Service Centers - Get Details
    http.get(
      formatUrl(apiUrlsV2.getEolServiceCenterDetails(":scCode")),
      async ({ params }) => {
        await delay(config.delay);
        const { scCode } = params;

        // Find service center by scCode
        const serviceCenter = mockEolServiceCenterDetails.find(
          (sc) => sc.scCode === scCode
        );

        if (!serviceCenter) {
          return HttpResponse.json(
            {
              success: false,
              message: `Service center with code ${scCode} not found`,
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        // Parse address to extract components
        const addressParts = serviceCenter.physicalAddress.split(", ");
        const street = addressParts[0] || "";
        const city = addressParts[1] || "";
        const stateZip = addressParts[2] || "";
        const [state, zip] = stateZip.split(" ");

        // Transform to API format with nested address
        const apiFormat = {
          id: parseInt(serviceCenter.id, 10),
          scCode: serviceCenter.scCode,
          serviceCenterName: serviceCenter.serviceCenterName,
          operationType: serviceCenter.operationType,
          vendorScac:
            serviceCenter.vendorScac === DASH_PLACEHOLDER
              ? null
              : serviceCenter.vendorScac,
          status: serviceCenter.status,
          lhJunction: serviceCenter.lhJunction,
          address: {
            country: serviceCenter.country,
            address: street,
            suite: null,
            city,
            state: state || "",
            zip: zip || "",
            latitude: parseFloat(serviceCenter.latitude) || null,
            longitude: parseFloat(serviceCenter.longitude) || null,
            timezone: serviceCenter.timezone,
            addressEffectiveDate: serviceCenter.addressEffectiveDate,
            addressExpiryDate: serviceCenter.addressExpiryDate,
          },
        };

        return HttpResponse.json(
          {
            success: true,
            message: "Service center details retrieved successfully",
            errorCode: null,
            item: apiFormat,
          },
          { status: 200 }
        );
      }
    ),

    // EOL Service Centers - Zip Codes List
    http.post(
      formatUrl(apiUrlsV2.getEolServiceCenterZipCodesList),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          scCode: string;
          page: number;
          pageSize: number;
          searchKey: string | null;
          filter: "All" | "Active" | "Inactive";
          operation?: "All" | "Pickup" | "Delivery";
        };

        // Find service center by scCode to get its ID
        const serviceCenter = mockEolServiceCenters.find(
          (sc) => sc.scCode === body.scCode
        );

        // Get zip codes for this service center (or fallback to all)
        const allZipCodes = serviceCenter?.id
          ? mockZipCodesByServiceCenter[serviceCenter.id] ?? mockEolZipCodesList
          : mockEolZipCodesList;

        // Filter by status
        let filteredZipCodes = allZipCodes;
        if (body.filter !== "All") {
          filteredZipCodes = filteredZipCodes.filter(
            (zc) => zc.status === body.filter
          );
        }

        // Filter by operation
        if (body.operation && body.operation !== "All") {
          filteredZipCodes = filteredZipCodes.filter(
            (zc) => zc.operation === body.operation
          );
        }

        // Filter by search key
        if (body.searchKey) {
          const searchLower = body.searchKey.toLowerCase();
          filteredZipCodes = filteredZipCodes.filter(
            (zc) =>
              zc.zipCode.toLowerCase().includes(searchLower) ||
              (zc.city ?? "").toLowerCase().includes(searchLower) ||
              (zc.state ?? "").toLowerCase().includes(searchLower)
          );
        }

        // Pagination
        const totalRecords = filteredZipCodes.length;
        const totalPages = Math.ceil(totalRecords / body.pageSize);
        const startIndex = (body.page - 1) * body.pageSize;
        const endIndex = startIndex + body.pageSize;
        const paginatedData = filteredZipCodes.slice(startIndex, endIndex);

        // Convert dates to null to avoid axios date parsing issues
        const sanitizedData = paginatedData.map((zc) => ({
          ...zc,
          effectiveDate: null,
          expiryDate: null,
        }));

        return HttpResponse.json(
          {
            success: true,
            message: `Retrieved ${sanitizedData.length} ZIP codes (page ${body.page} of ${totalPages})`,
            errorCode: null,
            item: {
              zipCodes: sanitizedData,
              pagination: {
                totalRecords,
                totalPages,
                currentPage: body.page,
                pageSize: body.pageSize,
              },
            },
          },
          { status: 200 }
        );
      }
    ),

    // EOL Service Centers - Delete Zip Code
    http.delete(
      formatUrl(apiUrls.deleteEolServiceCenterZipCode(":scCode", ":zipCodeId")),
      async ({ params }) => {
        await delay(config.delay);
        const { scCode, zipCodeId } = params;

        // Find the zip code in mock data
        const serviceCenter = mockEolServiceCenters.find(
          (sc) => sc.scCode === scCode
        );
        const zipCodes = serviceCenter?.id
          ? mockZipCodesByServiceCenter[serviceCenter.id] ?? mockEolZipCodesList
          : mockEolZipCodesList;

        const zipCode = zipCodes.find((zc) => zc.id === Number(zipCodeId));

        if (!zipCode) {
          return HttpResponse.json(
            {
              success: false,
              message: "ZIP code not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        return HttpResponse.json(
          {
            success: true,
            message: `ZIP code ${zipCode.zipCode} deleted successfully`,
            errorCode: null,
            item: {
              id: zipCode.id,
              zipCode: zipCode.zipCode,
            },
          },
          { status: 200 }
        );
      }
    ),

    // EOL Service Centers - Bulk Expire Zip Codes
    http.post(
      formatUrl(apiUrls.bulkExpireEolServiceCenterZipCodes),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          scCode: string;
          zipCodeIds: number[];
          expiryDate: string;
          reason: string;
        };

        const { scCode, zipCodeIds } = body;

        // Find the service center
        const serviceCenter = mockEolServiceCenters.find(
          (sc) => sc.scCode === scCode
        );

        if (!serviceCenter) {
          return HttpResponse.json(
            {
              success: false,
              message: "Service center not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        const zipCodes = serviceCenter.id
          ? mockZipCodesByServiceCenter[serviceCenter.id] ?? mockEolZipCodesList
          : mockEolZipCodesList;

        // Count how many zip codes will be expired
        const validZipCodeIds = zipCodeIds.filter((id) =>
          zipCodes.some((zc) => zc.id === id)
        );

        if (validZipCodeIds.length === 0) {
          return HttpResponse.json(
            {
              success: false,
              message: "No valid ZIP codes found to expire",
              errorCode: "INVALID_IDS",
              item: null,
            },
            { status: 400 }
          );
        }

        return HttpResponse.json(
          {
            success: true,
            message: `${validZipCodeIds.length} ZIP code${validZipCodeIds.length === 1 ? "" : "s"} expired successfully`,
            errorCode: null,
            item: {
              expiredCount: validZipCodeIds.length,
            },
          },
          { status: 200 }
        );
      }
    ),

    // EOL Service Centers - Zip Codes List (v1 - apiUrls)
    http.post(
      formatUrl(apiUrls.getEolServiceCenterZipCodesList),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          scCode: string;
          page: number;
          pageSize: number;
          searchKey: string | null;
          filter: "All" | "Active" | "Inactive";
          operation?: "All" | "Pickup" | "Delivery";
        };

        // Find service center by scCode to get its ID
        const serviceCenter = mockEolServiceCenters.find(
          (sc) => sc.scCode === body.scCode
        );

        // Get zip codes for this service center (or fallback to all)
        const allZipCodes = serviceCenter?.id
          ? mockZipCodesByServiceCenter[serviceCenter.id] ?? mockEolZipCodesList
          : mockEolZipCodesList;

        // Filter by status
        let filteredZipCodes = allZipCodes;
        if (body.filter !== "All") {
          filteredZipCodes = filteredZipCodes.filter(
            (zc) => zc.status === body.filter
          );
        }

        // Filter by operation
        if (body.operation && body.operation !== "All") {
          filteredZipCodes = filteredZipCodes.filter(
            (zc) => zc.operation === body.operation
          );
        }

        // Filter by search key
        if (body.searchKey) {
          const searchLower = body.searchKey.toLowerCase();
          filteredZipCodes = filteredZipCodes.filter(
            (zc) =>
              zc.zipCode.toLowerCase().includes(searchLower) ||
              (zc.city ?? "").toLowerCase().includes(searchLower) ||
              (zc.state ?? "").toLowerCase().includes(searchLower)
          );
        }

        // Calculate pagination
        const totalRecords = filteredZipCodes.length;
        const totalPages = Math.ceil(totalRecords / body.pageSize);
        const startIndex = (body.page - 1) * body.pageSize;
        const endIndex = startIndex + body.pageSize;
        const paginatedData = filteredZipCodes.slice(startIndex, endIndex);

        // Convert dates to null to avoid axios date parsing issues
        const sanitizedData = paginatedData.map((zc) => ({
          ...zc,
          effectiveDate: null,
          expiryDate: null,
        }));

        return HttpResponse.json(
          {
            success: true,
            message: "Zip codes retrieved successfully",
            errorCode: null,
            item: {
              zipCodes: sanitizedData,
              pagination: {
                totalRecords,
                totalPages,
                currentPage: body.page,
                pageSize: body.pageSize,
              },
            },
          },
          { status: 200 }
        );
      }
    ),

    // EOL Service Centers - Create Zip Code
    http.post(
      formatUrl(apiUrls.createEolServiceCenterZipCode),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          scCode: string;
          zipCode: string;
          dowService: number[];
          operation: string;
          additionalDays: number;
          effectiveDate: string;
          expiryDate: string;
          altVendor?: string;
          otherServiceCenter?: string[];
        };

        return HttpResponse.json(
          {
            success: true,
            message: `ZIP code ${body.zipCode} created successfully`,
            errorCode: null,
            item: {
              id: Math.floor(Math.random() * 10000),
              zipCode: body.zipCode,
              city: "Mock City",
              state: "MC",
              dowService: body.dowService,
              operation: body.operation,
              additionalDays: body.additionalDays,
              otherServiceCenter: body.otherServiceCenter ?? [],
              alternativeVendor: body.altVendor || null,
              effectiveDate: body.effectiveDate,
              expiryDate: body.expiryDate,
              status: "Active",
            },
          },
          { status: 201 }
        );
      }
    ),

    // EOL Service Centers - Edit Zip Code
    http.put(
      formatUrl(apiUrls.editEolServiceCenterZipCode(":scCode", ":zipCodeId")),
      async ({ request, params }) => {
        await delay(config.delay);
        const { zipCodeId } = params;
        const body = (await request.json()) as {
          zipCode: string;
          dowService: number[];
          operation: string;
          additionalDays: number;
          effectiveDate: string;
          expiryDate: string;
          altVendor?: string;
        };

        return HttpResponse.json(
          {
            success: true,
            message: `ZIP code ${body.zipCode} updated successfully`,
            errorCode: null,
            item: {
              id: Number(zipCodeId),
              zipCode: body.zipCode,
              city: "",
              state: "",
              dowService: body.dowService,
              additionalDays: body.additionalDays,
              otherServiceCenter: "",
              alternativeVendor: body.altVendor || null,
              effectiveDate: body.effectiveDate,
              expiryDate: body.expiryDate,
              status: "Active",
            },
          },
          { status: 200 }
        );
      }
    ),

    // EOL Service Centers - Export Zip Codes
    http.post(
      formatUrl(apiUrls.exportEolServiceCenterZipCodes),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          scCode: string;
          format: "CSV" | "Excel";
          filter?: "All" | "Active" | "Inactive";
          operation?: "All" | "Pickup" | "Delivery";
          searchKey?: string | null;
        };

        const filename = `zip-codes-${body.scCode}-${Date.now()}.${body.format.toLowerCase()}`;
        const downloadUrl = `https://mock-download-url.com/${filename}`;

        return HttpResponse.json(
          {
            success: true,
            message: "ZIP codes exported successfully",
            errorCode: null,
            item: {
              filename,
              downloadUrl,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.get(formatUrl(apiUrls.getEolLinehaulJunctions), async () => {
      await delay(config.delay);
      const linehaulJunctions = [...mockEolLinehaulJunctions];
      return HttpResponse.json(
        {
          success: true,
          message: `Retrieved ${linehaulJunctions.length} active linehaul junction(s)`,
          errorCode: null,
          item: { linehaulJunctions },
        },
        { status: 200 }
      );
    }),

    http.get(
      formatUrl(
        apiUrls.getEolCriteriaDetails(":criteriaId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockEolCriteriaDetailsApiResponse, {
          status: 200,
        });
      }
    ),

    http.post(
      formatUrl(apiUrls.postEolServiceCenterJunction(":scCode")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true }, { status: 201 });
      }
    ),

    http.get(formatUrl(apiUrls.getEolCriteriaById(":criteriaId")), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: { criteria: mockOriginJunctionCriteria },
        },
        { status: 200 }
      );
    }),

    http.put(
      formatUrl(apiUrls.editEolServiceCenterJunction(":scCode", ":junctionId")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true }, { status: 200 });
      }
    ),

    http.post(
      formatUrl(apiUrls.postEolServiceCenterJunctionsList(":scCode")),
      async ({ params, request }) => {
        await delay(config.delay);
        const { scCode } = params;
        const body = (await request.json()) as {
          type: string;
          page?: number;
          pageSize?: number;
          filter?: string;
        };

        const page = Math.max(1, Number(body.page ?? 1));
        const pageSize = Math.max(1, Number(body.pageSize ?? 25));
        const filter = body.filter ?? "Active";
        const junctionType = body.type || "origin";

        let rows: unknown[];
        if (junctionType === "destination") {
          rows = [...mockEolDestinationJunctionsLinehaulApiRows];
        } else {
          rows = [...mockEolOriginJunctionsLinehaulApiRows];
        }

        if (filter !== "Active") {
          rows = rows.filter((r: any) => r.status === filter);
        }

        const totalRecords = rows.length;
        const totalPages = Math.ceil(totalRecords / pageSize);
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const linehaulJunctions = rows.slice(startIndex, endIndex);

        return HttpResponse.json(
          {
            success: true,
            message: `Retrieved ${junctionType} junctions for service center ${String(scCode)}: ${totalRecords} junction(s) on page ${page} of ${totalPages}`,
            errorCode: null,
            item: {
              linehaulJunctions,
              pagination: {
                totalRecords,
                totalPages,
                currentPage: page,
                pageSize,
              },
            },
          },
          { status: 200 }
        );
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.deleteEolServiceCenterJunction(":scCode", ":junctionId")
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true }, { status: 200 });
      }
    ),

    // APTR Shipment detail
    http.get(
      formatUrl(apiUrls.getAppointmentRequiredShipmentDetail(":shipmentId")),
      async ({ params }) => {
        await delay(config.delay);
        const { shipmentId } = params;
        const shipment = mockShipmentData.default.find(
          (shipment) => String(shipment.id) === shipmentId
        );
        if (shipment) {
          const details = Object.entries(
            mockAppointmentRequiredShipmentsDetails
          ).find((entry) => entry[0] === shipment.proNumber);
          return HttpResponse.json(details?.[1], { status: 200 });
        }
        return HttpResponse.json(null, { status: 400 });
      }
    ),

    http.get(
      formatUrl(apiUrls.getPreviousDeliveriesByConsignee(":consigneeId")),
      async ({ params }) => {
        await delay(config.delay);
        const { consigneeId } = params;
        const deliveries = mockPreviousDeliveries.default.filter(
          (delivery) => String(delivery.consigneeId) === consigneeId
        );
        return HttpResponse.json(deliveries, { status: 200 });
      }
    ),

    http.get(
      formatUrl(apiUrls.getCallAttempts(":shipmentId")),
      async ({ params }) => {
        await delay(config.delay);
        const { shipmentId } = params;
        const calls = mockCallAttempts.default.filter(
          (callAttempt) => String(callAttempt.id) === shipmentId
        );
        return HttpResponse.json(calls, { status: 200 });
      }
    ),

    http.post(formatUrl(apiUrls.createCallAttempt(":shipmentId")), async () => {
      await delay(config.delay);

      return new HttpResponse(null, { status: 204 });
    }),

    // Get Rate Base Types
    http.get(formatUrl(apiUrls.getRateBaseTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: MOCK_RATE_BASE_TYPES,
        },
        { status: 200 }
      );
    }),

    http.post(formatUrl(apiUrls.createRateBase), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: "Rateware XL rate base created successfully with ID: 203",
          errorCode: null,
          item: {
            rateBaseDetailId: 203,
          },
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getRuleTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: "Rule types retrieved successfully",
          errorCode: null,
          item: mockRuleTypes,
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getRateClasses), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: "Rate classes retrieved successfully",
          errorCode: null,
          item: mockRateClasses,
        },
        { status: 200 }
      );
    }),

    // Get Rate Tariffs
    http.get(formatUrl(apiUrls.getRateTariffs), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            rateTariffs: MOCK_RATE_TARIFFS.map((tariff) => tariff.value),
          },
        },
        { status: 200 }
      );
    }),

    // Get Handling Unit Types
    http.get(formatUrl(apiUrls.getRateBasesHandlingUnits), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: MOCK_HANDLING_UNIT_TYPES.map(({ id, label }) => ({
            id,
            description: label,
          })),
        },
        { status: 200 }
      );
    }),

    // Get Overage Rule Options
    http.get(formatUrl(apiUrls.getRateBasesOverageRuleOptions), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: MOCK_OVERAGE_RULES.map(({ id, label }) => ({
            id,
            description: label,
          })),
        },
        { status: 200 }
      );
    }),

    http.get(
      formatUrl(
        apiUrls.getRateBaseCriteria(":criteriaId" as unknown as number)
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { criteriaId } = params;
        const criteriaIdNum = Number(criteriaId);

        const mockCriteria = getRateBaseCriteriaMock(criteriaIdNum);

        if (!mockCriteria) {
          return HttpResponse.json(
            {
              success: false,
              message: "Criteria not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        return HttpResponse.json(
          {
            success: true,
            message: "Criteria retrieved successfully",
            errorCode: null,
            item: mockCriteria,
          },
          { status: 200 }
        );
      }
    ),

    http.post(formatUrl(apiUrls.getRulesList), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        page: number;
        pageSize: number;
        search: string;
        status: string;
        ruleType: string;
        effectiveDateFrom: string;
        effectiveDateTo: string;
        version: number | null;
        sortBy: string;
        sortOrder: string;
      };
      const {
        page = 1,
        pageSize = 25,
        search = "",
        status = "",
        ruleType = "",
        effectiveDateFrom = "",
        effectiveDateTo = "",
        version = null,
        sortBy = "",
        sortOrder = "",
      } = body;

      let filtered = [...mockRules];

      if (search.trim()) {
        const lower = search.toLowerCase();
        filtered = filtered.filter(
          (r) =>
            r.name.toLowerCase().includes(lower) ||
            r.ruleType?.toLowerCase().includes(lower) ||
            r.ruleTypeDisplay?.toLowerCase().includes(lower) ||
            r.description?.toLowerCase().includes(lower)
        );
      }

      if (status) {
        filtered = filtered.filter((r) => r.status === status);
      }

      if (ruleType) {
        filtered = filtered.filter((r) => r.ruleType === ruleType);
      }

      if (effectiveDateFrom) {
        filtered = filtered.filter(
          (r) => r.effectiveDate && r.effectiveDate >= effectiveDateFrom
        );
      }

      if (effectiveDateTo) {
        filtered = filtered.filter(
          (r) => r.effectiveDate && r.effectiveDate <= effectiveDateTo
        );
      }

      if (typeof version === "number") {
        filtered = filtered.filter((r) => r.version === version);
      }

      if (sortBy) {
        filtered.sort((a, b) => {
          const aVal = a[sortBy as keyof typeof a];
          const bVal = b[sortBy as keyof typeof b];
          if (aVal === null && bVal === null) return 0;
          if (aVal === null) return 1;
          if (bVal === null) return -1;
          if (typeof aVal === "string" && typeof bVal === "string") {
            const result = aVal.localeCompare(bVal);
            return sortOrder === "desc" ? -result : result;
          }
          if (typeof aVal === "number" && typeof bVal === "number") {
            return sortOrder === "desc" ? bVal - aVal : aVal - bVal;
          }
          return 0;
        });
      }

      const totalRecords = filtered.length;
      const totalPages = Math.ceil(totalRecords / pageSize);
      const startIndex = (page - 1) * pageSize;
      const paginatedRules = filtered.slice(startIndex, startIndex + pageSize);

      return HttpResponse.json(
        {
          success: true,
          message: `Retrieved ${totalRecords} rule(s)`,
          errorCode: null,
          item: {
            rules: paginatedRules,
            pagination: {
              totalRecords,
              totalPages,
              currentPage: page,
              pageSize,
            },
          },
        },
        { status: 200 }
      );
    }),

    // Get Rate Bases List
    http.post(formatUrl(apiUrls.getRateBasesList), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        page: number;
        pageSize: number;
        search: string;
        rateType: string;
        status: string;
        sortBy: string;
        sortOrder: string;
      };
      const {
        page = 1,
        pageSize = 25,
        search = "",
        rateType = "",
        status = "",
        sortBy = "",
        sortOrder = "",
      } = body;

      let filtered = [...mockRateBases];

      if (search.trim()) {
        const lower = search.toLowerCase();
        filtered = filtered.filter(
          (r) =>
            r.name.toLowerCase().includes(lower) ||
            r.rateType?.toLowerCase().includes(lower) ||
            r.rateTypeDisplay?.toLowerCase().includes(lower) ||
            r.description?.toLowerCase().includes(lower)
        );
      }

      if (status) {
        filtered = filtered.filter((r) => r.status === status);
      }

      if (rateType) {
        filtered = filtered.filter((r) => r.rateType === rateType);
      }

      if (sortBy) {
        filtered.sort((a, b) => {
          const aVal = a[sortBy as keyof typeof a];
          const bVal = b[sortBy as keyof typeof b];
          if (aVal === null && bVal === null) return 0;
          if (aVal === null) return 1;
          if (bVal === null) return -1;
          if (typeof aVal === "string" && typeof bVal === "string") {
            const result = aVal.localeCompare(bVal);
            return sortOrder === "desc" ? -result : result;
          }
          if (typeof aVal === "number" && typeof bVal === "number") {
            return sortOrder === "desc" ? bVal - aVal : aVal - bVal;
          }
          return 0;
        });
      }

      const totalRecords = filtered.length;
      const totalPages = Math.ceil(totalRecords / pageSize) || 1;
      const startIndex = (page - 1) * pageSize;
      const paginatedRateBases = filtered.slice(
        startIndex,
        startIndex + pageSize
      );

      // Return wire format: API uses `rateBaseName`, Zod schema transforms it to `name`.
      // createdDate is formatted as MM/DD/YYYY to match real API (httpCommon only converts
      // ISO/YYYY-MM-DD strings to Date, which would break z.string().nullable() parsing).
      const wireRateBases = paginatedRateBases.map(
        ({
          name,
          rateTypeDisplay: _rtd,
          createdBy: _cb,
          modifiedDate: _md,
          modifiedBy: _mb,
          createdDate,
          ...rest
        }) => {
          let formattedCreatedDate: string | null = null;
          if (createdDate) {
            const d = new Date(createdDate);
            const month = String(d.getUTCMonth() + 1).padStart(2, "0");
            const day = String(d.getUTCDate()).padStart(2, "0");
            const year = d.getUTCFullYear();
            formattedCreatedDate = `${month}/${day}/${year}`;
          }
          return {
            ...rest,
            rateBaseName: name,
            createdDate: formattedCreatedDate,
          };
        }
      );

      return HttpResponse.json(
        {
          success: true,
          message: `Retrieved ${paginatedRateBases.length} rate bases (page ${page} of ${totalPages})`,
          errorCode: null,
          item: {
            rateBases: wireRateBases,
            pagination: {
              totalRecords,
              totalPages,
              currentPage: page,
              pageSize,
            },
          },
        },
        { status: 200 }
      );
    }),

    // Get Rate Base Detail (Rateware XL)
    // NOTE: This handler intercepts GET /rate-bases/:id for all rate-base types.
    // When other rate types (Truckload, Mileage, etc.) are integrated to the real API,
    // extend this handler to return type-specific mock data based on the rateType field.
    http.get(
      formatUrl(apiUrls.getRateBaseDetail(":rateBaseId" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const id = Number(params.rateBaseId);
        const item = getRatewareXLBaseDetail(id);
        if (!item) {
          return HttpResponse.json(
            {
              success: false,
              message: "Rate base not found",
              errorCode: "NOT_FOUND",
            },
            { status: 404 }
          );
        }
        return HttpResponse.json(
          { success: true, message: null, errorCode: null, item },
          { status: 200 }
        );
      }
    ),

    http.get(
      "https://geocode.search.hereapi.com/v1/geocode",
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const q = url.searchParams.get("q") ?? "";
        const qq = url.searchParams.get("qq") ?? "";
        const hasQuery = q.trim().length >= 3 || qq.trim().length > 0;
        const source = hasQuery ? mockAddresses.default : mockAddresses.empty;
        return HttpResponse.json({
          items: source.items.map((item) => ({
            ...item,
            position: { lat: 32.7767, lng: -96.797 },
            timeZone: { name: "America/Chicago", utcOffset: "-06:00" },
          })),
        });
      }
    ),
  ];
}
